/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import { dashboardMarkdownHtml } from "@/datox/dashboard-markdown"
import { DashboardKpiContext, KpiDisplaySettings, KpiFormulaSettings, KpiWidget, foldKpiDisplay, kpiDisplayStyle } from "./KpiWidget"
import { DashboardAnalyticsContext } from "./AdvancedWidgets"
import { serializeDashboardPdf } from "@/datox/dashboard-pdf"
import { KpiSourcePanel } from "./KpiSourcePanel"
import {
  CHART_SEMANTIC,
  TITLE_WEIGHT,
  chartPalette,
  designFontScale,
  designFontStack,
  designRadius,
  designToCssVars,
  SHEET_STYLES,
  HEADER_TINT,
  headerTone,
  sheetStyle,
  tintHex,
  isBenchmarkSeries,
  imageLuminance,
  logoNeedsPlate,
  readableTextOn,
  splitWidgetTitle,
  titleWidgetSize,
  widgetTitleSize,
} from "./tokens/design"
import { CHART_SIZE, CHART_TEXT, chartSizeMode, chartTextSizes, kpiLabelSize, lineShowsMarkers } from "./tokens/kpi"
import { TableSettingsEditor } from "./TableSettingsEditor"
import { FreeStyleEditor } from "./FreeStyleTableEditor"
import { EditorNumberInput, EditorSection } from "./WidgetEditorFields"
import {
  chartItems,
  explicitColumnFormat,
  formatTableCell,
  formattedCell,
  humanizeColumnName,
  inferColumnFormat,
  isNumericFormat,
  numericCell,
  tableColumnModels,
  tableColumnWidths,
  tableSummary,
  abbreviateLabel,
  niceValueAxis,
  chartCapCaption,
  distinctFilterValues,
  filterIsApplied,
  PIE_LEGEND_CENTER,
  scrollLegend,
  filterColumnKind,
  filterOperatorFor,
  filterOperatorsFor,
  filterValueHints,
  rowMatchesFilter,
  visibleFilterValues,
  wholeRowsHeight,
  SIGNED_COLORS,
  signedPercentCell,
  dateCategoryAxis,
  dateAxisTickIndexes,
  sentenceCaseLabels,
  categoryAxisLayout,
  labelTextPx,
  compactAxisUnit,
  formatCompactAxisValue,
  syncHeaderWidgets,
  HEADER_DATE_MIN_HEIGHT,
  contributionRows,
  contributionColors,
  explainFilterChips,
  chartHasNegative,
  chartBindingIssue,
  buildSmartTitle,
  withSmartTitle,
  dashboardFiltersFor,
  rowMatchesDashboardFilter,
  resolveDatasetRef,
  widgetOutputTable,
  changedWidgetIds,
  multiSeriesPlan,
  percentStackItems,
  chartStackMode,
  filterTableRows,
} from "./dashboard-widget-data"
import { dataDate } from "@/datox/v2/readiness"
import { formatDay } from "@/datox/v2/dates"
import { TABLE_TOKENS, tableTokenStyle } from "./tokens/table"
import { withSheetChrome } from "./tokens/sheet-chart"
import {
  compareDashboardCells,
  dashboardKpiPageHeight,
  dashboardWidgetHeight,
  waitForDashboardKpis,
} from "@/datox/dashboard-kpi"
import { Rnd } from "react-rnd"
import { flushSync } from "react-dom"
import ReactECharts from "echarts-for-react"
import {
  alignmentGuides,
  boundedGroupMove,
  canvasAutoHeight,
  marqueeSelection,
  nearestFreePlacement,
  placementCollides,
  snapLayout,
  snapValue,
  defaultGridLayout,
  gridMinimumSize,
  nearestFreeGridPlacement,
  fitPageLayout,
  normalizeLayout,
  pageGrid,
  snapGridLayout,
  snapGridPosition,
  compactLayoutOnDrop,
  persistFlowLayout,
  pushLayoutForDrag,
  gridResizeLayout,
  freeGridSlot,
  dropSlotLayout,
  contentFitLayouts,
  planGridDrop,
  moveToPageTop,
  gridCells,
  gridLayout,
  reportTemplateLayout,
  tidyReportPage,
  printPageLayout,
  rowLineAfter,
  arrangeByTypeLayout,
  rescaleLayoutToCanvas,
  isPageTopHeader,
  clearOfHeaders,
  closeDeletedGap,
  rectanglesIntersect,
} from "./dashboardCanvas"
import { CONTENT_FIT_TYPES, widgetMinCells } from "./tokens/layout"
import { DashboardFitContext, widgetContentFit } from "./dashboard-fit-context"
import { WidgetEmptyContext, WidgetEmpty } from "./WidgetEmpty"
import {
  TbEdit,
  TbChartBar,
  TbChartDots,
  TbChartLine,
  TbChartPie,
  TbFileExport,
  TbPlus,
  TbFilter,
  TbFlame,
  TbGauge,
  TbLayoutGrid,
  TbLayoutList,
  TbLoader2,
  TbTable,
  TbTextCaption,
  TbTrash,
  TbTypography,
  // TbArrowNarrowDown,
  // TbArrowNarrowUp,
  TbChevronLeft,
  TbChevronRight,
} from "react-icons/tb"
import { KpiComparisonDisplay, KpiComparisonSettings, useDashboardKpiComparisons } from "./kpi-comparison"
import "./datox-compliance-dashboard.css"
import "./widget-editors.css"
import "@/datox/report-canvas.css"
import { paginateReportTab, renderReportPdf, reportNumber, shownTableRows } from "./report-rendering"
import { constrainReportGridLayout, constrainReportLayout, copyReportPage, reportWidgetOffPage } from "./report-canvas-model"
import { ReportCanvasToolbar, ReportPageStrip, ReportTemplatePanel } from "./ReportCanvasChrome"
import { trapReportDialog } from "@/datox/report-designer"

class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  render() {
    if (this.state.error)
      return (
        <div className="datox-react-dashboard__fatal">
          <strong>{this.props.labels.somethingWentWrong}</strong>
          <span>{String(this.state.error?.message || this.state.error)}</span>
        </div>
      )
    return this.props.children
  }
}

const WIDGETS = [
  { type: "header", icon: TbLayoutGrid, data: false },
  { type: "text", icon: TbTextCaption, data: false },
  { type: "title", icon: TbTypography, data: false },
  { type: "kpi", icon: TbGauge, data: true },
  { type: "table", icon: TbTable, data: true },
  { type: "freeTable", icon: TbTable, data: false },
  { type: "bar", icon: TbChartBar, data: true },
  { type: "line", icon: TbChartLine, data: true },
  { type: "scatter", icon: TbChartDots, data: true },
  { type: "pie", icon: TbChartPie, data: true },
  { type: "heatmap", icon: TbFlame, data: true },
  { type: "multiSeries", icon: TbChartLine, data: true },
  { type: "treemap", icon: TbLayoutGrid, data: true },
  { type: "funnel", icon: TbFilter, data: true },
  { type: "filter", icon: TbFilter, data: true },
]

const CELL_COLOR_OPERATOR_OPTIONS = [
  { value: "equals", label: "=" },
  { value: "not_equals", label: "!=" },
  { value: "contains", label: "contains" },
  { value: "gt", label: ">" },
  { value: "gte", label: ">=" },
  { value: "lt", label: "<" },
  { value: "lte", label: "<=" },
]

const CHART_CONTROLS = {
  bar: ["orientation", "legend", "values", "axes", "grid", "colors", "stack", "titles", "format"],
  line: ["legend", "values", "axes", "grid", "stack", "titles", "format"],
  multiSeries: ["legend", "values", "axes", "grid", "stack", "titles", "format"],
  scatter: ["axes", "grid", "titles", "format"],
  pie: ["legend", "values", "adjacent", "format"],
  treemap: [],
  funnel: ["legend", "values"],
  heatmap: ["values"],
}

const DashboardTableContext = React.createContext({ loading: false })

function freeStyleFromTable(table, labels) {
  // Bobur's validator caps a free-style table at 20 columns × 50 rows (dashboard_advanced_schemas.py).
  const headers = table?.headers?.length
    ? table.headers.slice(0, 20)
    : [1, 2].map((count) => labels.columnName.replace("{count}", String(count)))
  const rows = table?.rows?.length
    ? table.rows.slice(0, 50).map((row) => headers.map((header) => row[header] ?? ""))
    : [headers.map(() => "")]
  return {
    headers,
    rows,
    columnWidths: headers.map(() => 168),
    columnTypes: headers.map(() => "text"),
    rowTypes: rows.map(() => "default"),
    showRowNumbers: false,
    highlightHeaderRow: true,
    cellAlign: {},
  }
}

// Bobur's analytics context ({ projectID, dashboardID, revision } and sourceDisabled) for his island modules.
// Explain runs through our panel and `onExplainSource(request, signal)` (decision D).
function dashboardAnalyticsValue(props) {
  return props.analytics ? { ...props.analytics, sourceDisabled: !!props.sourceDisabled } : null
}

export function DatoxComplianceDashboard(props) {
  return (
    <DashboardErrorBoundary labels={props.labels}>
      <DashboardKpiContext.Provider
        value={{
          datasets: props.kpiDatasets || {},
          locale: props.locale,
          onEvaluateKpi: props.onEvaluateKpi,
          kpiValues: props.kpiValues || {},
          dashboardID: props.selectedDashboardID,
          sourceKey: props.sourceKey || props.selectedDashboardID,
          sourceKeys: props.kpiSourceKeys,
          // The header's "Data to …" line and header sort are the V2 design (FLAG-AUDIT.md); his classic render keeps neither.
          dataDate: props.v2 ? dashboardDataDate(props.dashboards, props.selectedDashboardID) : undefined,
          v2: !!props.v2,
        }}
      >
        <DashboardTableContext.Provider value={{ loading: props.loadingTables, onRetry: props.onRetryDataset }}>
          <DashboardAnalyticsContext.Provider value={dashboardAnalyticsValue(props)}>
            <DatoxComplianceDashboardContent {...props} />
          </DashboardAnalyticsContext.Provider>
        </DashboardTableContext.Provider>
      </DashboardKpiContext.Provider>
    </DashboardErrorBoundary>
  )
}

export function DatoxPublishedDashboard(props) {
  return (
    <DashboardKpiContext.Provider
      value={{
        datasets: Object.fromEntries(
          (props.outputTables || []).map((table) => [table.datasetID, { status: "ready", table }]),
        ),
        locale: props.locale,
        onEvaluateKpi: props.onEvaluateKpi,
        kpiValues: props.kpiValues || {},
        sourceKey: props.sourceKey || props.dashboardID || props.selectedDashboardID,
        dataDate: props.v2 ? dashboardDataDate(props.dashboards, props.dashboardID || props.selectedDashboardID) : undefined,
        v2: !!props.v2,
      }}
    >
      <DashboardAnalyticsContext.Provider value={dashboardAnalyticsValue(props)}>
        <DatoxPublishedDashboardContent {...props} />
      </DashboardAnalyticsContext.Provider>
    </DashboardKpiContext.Provider>
  )
}

function DashboardSaveStatus({ store, labels }) {
  const source = store || EMPTY_SAVE_STORE
  const status = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot)
  const [saving, dirty] = status.split(":").map(Number)
  return <span>{saving || dirty ? labels.saving : labels.allChangesSaved}</span>
}

function DashboardCreateButton({ store, label, onClick }) {
  const source = store || EMPTY_SAVE_STORE
  const status = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot)
  return (
    <button
      className="datox-react-dashboard__button"
      type="button"
      onClick={onClick}
      disabled={status.startsWith("1:")}
    >
      {label}
    </button>
  )
}

const EMPTY_SAVE_STORE = {
  subscribe: () => () => {},
  getSnapshot: () => "0:0",
}

function DatoxPublishedDashboardContent(props) {
  const [activeTabId, setActiveTabId] = useState(props.config?.tabs?.[0]?.id || "")
  const [filterSelections, setFilterSelections] = useState({})
  const [sourceWidget, setSourceWidget] = useState(null)
  const labels = props.labels
  const config = props.config || defaultConfig(labels.defaultTabName, labels.defaultTabName)
  const activeTab = config.tabs?.find((tab) => tab.id === activeTabId) || config.tabs?.[0]
  const widgets = (activeTab?.widgets || []).map((widget) =>
    widget.type === "filter"
      ? {
          ...widget,
          data: {
            ...(widget.data || {}),
            selectedValues: filterSelections[widget.id] ?? widget.data?.selectedValues ?? [],
          },
        }
      : widget,
  )
  const filterWidgets = widgets.filter((widget) => widget.type === "filter")
  const activeFilterWidgets = widgets.filter(
    (widget) => widget.type === "filter" && (widget.data?.selectedValues?.length || widget.data?.range),
  )
  const comparisonState = useDashboardKpiComparisons({
    projectID: props.projectID,
    dashboardID: props.selectedDashboardID,
    revision:
      props.analytics?.revision ??
      props.dashboards?.find((dashboard) => dashboard.id === props.selectedDashboardID)?.revision ??
      0,
    widgets,
    outputTables: props.outputTables,
    activeFilterWidgets,
    labels,
    enabled: props.kpiComparisonsEnabled ?? !!props.analytics?.dashboardID,
  })
  useEffect(() => setFilterSelections({}), [props.config])
  const toggleFilterValue = (widget, value) => {
    setFilterSelections((current) => {
      const selectedValues = current[widget.id] ?? widget.data?.selectedValues ?? []
      return { ...current, [widget.id]: nextFilterSelectedValues(widget, value, selectedValues) }
    })
  }
  return (
    <div className="datox-react-dashboard datox-react-dashboard--published">
      <div className="datox-react-dashboard__published-tabs">
        {(config.tabs || []).map((tab) => (
          <button
            key={tab.id}
            className={
              tab.id === activeTab?.id
                ? "datox-react-dashboard__published-tab datox-react-dashboard__published-tab--active"
                : "datox-react-dashboard__published-tab"
            }
            type="button"
            onClick={() => setActiveTabId(tab.id)}
          >
            {tab.name || labels.defaultTabName}
          </button>
        ))}
      </div>
      <div className="datox-react-dashboard__published-canvas">
        <div
          className={`datox-react-dashboard__page ${config.design?.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`}
          data-sheet-style={sheetStyle(config.design)}
          style={{
            "--ds-accent": config.design?.accent || "#2f80ed",
            "--ds-header-color": config.design?.headerColor || "#101828",
            "--ds-heading-font": designFont(config.design?.headingFont),
            "--ds-body-font": designFont(config.design?.bodyFont),
            width: config.canvas?.width || 1123,
            minHeight: dashboardKpiPageHeight(config.canvas?.height, widgets),
          }}
        >
          {widgets.map((widget) => (
            <div
              key={widget.id}
              style={{
                position: "absolute",
                left: widget.layout.x,
                top: widget.layout.y,
                width: widget.layout.w,
                height: dashboardWidgetHeight(widget),
              }}
            >
              <WidgetFrame
                preview
                filterWidgets={filterWidgets}
                labels={labels}
                widget={widget}
                table={applyDashboardFilters(
                  widgetOutputTable(props.outputTables, widget),
                  widget,
                  activeFilterWidgets,
                )}
                accent={config.design?.accent || "#2f80ed"}
                design={config.design || {}}
                selected={false}
                menuOpen={false}
                onToggleMenu={() => {}}
                onRename={() => {}}
                onEditData={() => {}}
                onEditDesign={() => {}}
                onExportTable={() => {}}
                onFullView={() => {}}
                onFilterValueToggle={(value) => toggleFilterValue(widget, value)}
                onDelete={() => {}}
                onDuplicate={() => {}}
                comparison={comparisonState.itemsByWidgetId[widget.id]}
                onExplain={() => setSourceWidget(widget)}
              />
            </div>
          ))}
          {sourceWidget ? (
            <KpiSourcePanel
              widget={sourceWidget}
              filterWidgets={filterWidgets}
              labels={labels}
              onClose={() => setSourceWidget(null)}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function DatoxComplianceDashboardContent(props) {
  // The Lesha Bank design (VITE_DATOX_V2_DESIGN / ?design=new, passed by the host). Off, Bobur's classic editor keeps
  // his defaults and look; the layout fixes stay on for both (see .input/FLAG-AUDIT.md).
  const v2 = !!props.v2
  const [createOpen, setCreateOpen] = useState(false)
  const [renameState, setRenameState] = useState(null)
  const [deleteDashboardOpen, setDeleteDashboardOpen] = useState(false)
  const [publishedLink, setPublishedLink] = useState("")
  const [publishedLinkCopied, setPublishedLinkCopied] = useState(false)
  const [shareAccessOpen, setShareAccessOpen] = useState(false)
  const [clientAccessOpen, setClientAccessOpen] = useState(false)
  const [shareReviewers, setShareReviewers] = useState([
    { id: "reviewer-1", name: "", email: "", clientValue: "", selected: true },
  ])
  const [shareAccessSending, setShareAccessSending] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [sourceExplanation, setSourceExplanation] = useState(null)
  const sourceExplanationRequestRef = useRef({ id: 0, controller: null })
  const sourceExplanationCacheRef = useRef(new Map())
  useEffect(() => () => sourceExplanationRequestRef.current.controller?.abort(), [])
  const [exportingPdf, setExportingPdf] = useState(false)
  const [exportSnapshot, setExportSnapshot] = useState(null)
  const [exportSelection, setExportSelection] = useState(null)
  const [exportError, setExportError] = useState(false)
  const [newName, setNewName] = useState("")
  const [legacyPanel, setLegacyPanel] = useState("widgets")
  const panel = props.studio ? { elements: "widgets", data: "data", brand: "design" }[props.studio.panel] || "widgets" : legacyPanel
  const setPanel = (value) => props.studio ? props.studio.onPanel({ widgets: "elements", data: "data", design: "brand" }[value]) : setLegacyPanel(value)
  const [leftPanelOpen, setLeftPanelOpen] = useState(true)
  const [search, setSearch] = useState("")
  const [widgetLibraryView, setWidgetLibraryView] = useState(props.studio ? "grid" : "list")
  const [activeTabId, setActiveTabId] = useState(props.initialTabID || null)
  const [activeWidgetId, setActiveWidgetId] = useState(props.initialWidgetID || null)
  const [selectedWidgetIds, setSelectedWidgetIds] = useState(props.initialWidgetID ? [props.initialWidgetID] : [])
  const [editorWidgetId, setEditorWidgetId] = useState(props.initialEditorPanel ? props.initialWidgetID || null : null)
  const [sourceWidget, setSourceWidget] = useState(null)
  const [widgetEditorPanel, setWidgetEditorPanel] = useState(props.initialEditorPanel || "data")
  const [fullViewWidgetId, setFullViewWidgetId] = useState(null)
  const [inlineEditWidgetId, setInlineEditWidgetId] = useState(null)
  // Read first (UX research P5): on a V2 report page a press never moves a widget until Drag is switched on. His
  // classic editor keeps Drag on, as he shipped it.
  const [dragEnabled, setDragEnabled] = useState(!(props.studio && v2))
  const [snapEnabled, setSnapEnabled] = useState(() => {
    try {
      return window.localStorage.getItem("datox-dashboard-snap") !== "false"
    } catch {
      return true
    }
  })
  const [draggingWidgetType, setDraggingWidgetType] = useState("")
  // Report grid drag/resize preview: the ghost slot(s) and where the other widgets yield to.
  const [flowPreview, setFlowPreview] = useState(null)
  // The snapped size while a widget is resized on the grid (the live W × H readout).
  const [resizeSize, setResizeSize] = useState(null)
  const [dashboardMenuOpen, setDashboardMenuOpen] = useState(false)
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const [widgetMenuId, setWidgetMenuId] = useState(null)
  const [tabMenuId, setTabMenuId] = useState(null)
  const [historyVersion, setHistoryVersion] = useState(0)
  const [draftConfig, setDraftConfig] = useState(
    cloneDashboardConfig(props.config || defaultConfig(props.labels.defaultTabName, props.labels.defaultTabName)),
  )
  const [cellColorRulesOpen, setCellColorRulesOpen] = useState(false)
  const [cellColorRuleDraft, setCellColorRuleDraft] = useState(null)
  const [zoom, setZoom] = useState(0)
  const [viewport, setViewport] = useState({ width: 1000, height: 800 })
  const [previewFilters, setPreviewFilters] = useState({})
  const [localError, setLocalError] = useState("")
  const workspaceRef = useRef(null)
  const configRef = useRef(draftConfig)
  const dashboardRef = useRef(props.selectedDashboardID)
  const commandRef = useRef(0)
  const logoInputRef = useRef(null)
  const rootRef = useRef(null)
  // The client-side PDF's render root (renderReportPdf): its declaration went in 1c83e8d while the export kept it.
  const exportRef = useRef(null)
  const pageRef = useRef(null)
  const marqueeRef = useRef(null)
  const verticalGuideRef = useRef(null)
  const horizontalGuideRef = useRef(null)
  const widgetRefs = useRef(new Map())
  const dragStateRef = useRef(null)
  // The widget a Shift/Ctrl/⌘ press just added or removed: its focus must not reset the selection.
  const pickedRef = useRef(null)
  // A resize is under way (keys wait for it to end, HUNT M15).
  const resizingRef = useRef(false)
  // A library element dragged over a report page (V2 DESIGN-LOG §31): the last pointer, read once per frame.
  const dropPointerRef = useRef(null)
  const dropFrameRef = useRef(0)
  // Widgets whose element is put back on their displayed position after a grid drop or resize.
  const settleRef = useRef(null)
  // §36: the resize bound. react-rnd caps a resize at its bounds; the page box is the paper (794), so a widget past the
  // bottom was cut to the page on the first move. This layer is the page at rest and grows while a widget is resized.
  const [resizeBounds, setResizeBounds] = useState(null)
  const resizeInvalidRef = useRef(false)
  // Bobur's server PDF (free dashboards only): its snapshot, settings, per-widget tweaks and preview frame.
  const [serverSnapshot, setServerSnapshot] = useState(null)
  const [pdfSettings, setPdfSettings] = useState(null)
  const [pdfWidgetID, setPdfWidgetID] = useState("")
  const pdfPreviewRef = useRef(null)
  const clipboardRef = useRef([])
  // Bobur's clipboard rules (37f2a2b): a copy pastes only into the dashboard it came from, each paste one step
  // further from the original; the count drives the toolbar's Paste.
  const clipboardSourceRef = useRef({ dashboardID: null, pasted: 0 })
  const [clipboardCount, setClipboardCount] = useState(0)
  const undoStackRef = useRef([])
  // Summer undo throttle (Artyom cf6407c92): inline text edits fire per keystroke, so keystrokes
  // with the same key less than UNDO_COALESCE_MS apart share one undo step.
  const undoCoalesceRef = useRef({ key: null, at: 0 })
  const redoStackRef = useRef([])
  // Bobur's studio copy (labels.designer) is optional: hosts and tests without the report studio omit it.
  const labels = useMemo(() => (props.labels.designer ? props.labels : { ...props.labels, designer: {} }), [props.labels])
  const config = draftConfig
  const locked = !!props.readOnly || !!props.studio?.preview || exportingPdf
  const previewing = !!props.readOnly || !!props.studio?.preview || previewOpen
  const canvas = config.canvas || { width: 1123, height: 794 }
  // Report pages (studio) have a fixed page size; free dashboards grow downwards (canvasAutoHeight).
  // Report pages always snap to the summer 24-column × 28px grid (tokens/layout.js) and always get
  // the normalisation and display fit; on a report page the Grid toggle only shows or hides the
  // guides (Artyom, round 6). Free dashboards keep main's 4px snap, which the toggle switches.
  const gridMode = !!props.studio
  const showGridGuides = gridMode && snapEnabled
  const grid = useMemo(() => pageGrid(canvas), [canvas.width, canvas.height])
  const fitLayout = (layout, widget) =>
    gridMode
      ? constrainReportGridLayout(layout, canvas, widget)
      : constrainReportLayout(layout, props.studio ? canvas : { ...canvas, height: Number.POSITIVE_INFINITY })
  // Nearest free spot for a new or copied widget (collision-free, main's rule).
  const placeLayout = (layout, others, widget) =>
    gridMode
      ? nearestFreeGridPlacement(snapGridLayout(layout, grid, widgetMinCells(widget?.type, widget)), others, grid)
      : fitLayout(nearestFreePlacement(layout, others, config.canvas?.width || 1123, snapEnabled ? 4 : 1))
  // Preview reads like paper: every page stacked, at the workspace's width (UX research P10, E16); the editor fits one page.
  const scale = props.studio ? zoom || (props.studio.preview ? Math.min(1, Math.max(.15, (viewport.width - 96) / canvas.width)) : Math.min(1, Math.max(.15, (viewport.width - 96) / canvas.width), Math.max(.15, (viewport.height - 80) / canvas.height))) : 1
  const activeTab =
    config.tabs?.find((tab) => tab.id === activeTabId) ||
    config.tabs?.[0] ||
    defaultConfig(config.title || labels.defaultTabName, labels.defaultTabName).tabs[0]
  // Memoized: main's selection/keyboard effects depend on `widgets`, so a fresh array per render loops.
  // On a grid page, an off-grid (agent-written) layout is shown normalised; it is only saved
  // that way once the user edits the page (every edit writes the widgets shown here).
  const widgets = useMemo(
    () =>
      (gridMode ? normalizeLayout(activeTab.widgets || [], canvas) : activeTab.widgets || []).map((widget) =>
        previewing && widget.type === "filter" && previewFilters[widget.id]
          ? { ...widget, data: { ...widget.data, selectedValues: previewFilters[widget.id] } }
          : widget,
      ),
    [activeTab.widgets, previewing, previewFilters, gridMode, canvas.width, canvas.height],
  )
  // Display fit (fitPageLayout): content heights, summer compaction, growth into free rows and
  // fitting before the veil. Only rendered and printed; edits keep writing `widgets` above.
  const [contentNeeds, setContentNeeds] = useState({})
  const [contentStops, setContentStops] = useState({})
  const [kpiFits, setKpiFits] = useState({})
  // §35 (summer): the report editor never squeezes a page onto the paper; what passes the bottom keeps its saved
  // height, whole under the veil. Preview, paper pages and the PDF keep the squeeze (they print only the page).
  const squeeze = !(props.studio && !props.studio.preview)
  const displayWidgets = useMemo(
    () => (gridMode ? fitPageLayout(widgets, canvas, { needs: contentNeeds, stops: contentStops, squeeze }) : widgets),
    [widgets, gridMode, contentNeeds, contentStops, canvas.width, canvas.height, squeeze],
  )
  const displayById = useMemo(() => new Map(displayWidgets.map((widget) => [widget.id, widget])), [displayWidgets])
  // Preview: the other pages as paper around the live one, in page order (the live page keeps its filters).
  const stacked = !!props.studio?.preview && (config.tabs || []).length > 1
  const activeIndex = Math.max(0, (config.tabs || []).findIndex((tab) => tab.id === activeTab.id))
  // A library drag that ends without a drop (Esc, dragend elsewhere), Preview or a lock: the page goes back, nothing saved.
  useEffect(() => {
    if (!draggingWidgetType || locked || previewing) clearDropPreview()
  }, [draggingWidgetType, locked, previewing])
  const yielded = (widget) => (flowPreview && !flowPreview.ids.includes(widget.id) && flowPreview.layouts[widget.id]) || widget.layout
  // A library drop's preview (§32) shows the rects the drop saves (fitted heights included) and fades what leaves the page.
  const dropShown = (widget) =>
    ((flowPreview?.drop || ((flowPreview?.resize || flowPreview?.fit) && !flowPreview.ids.includes(widget.id))) && flowPreview.layouts[widget.id]) || null
  // §33 (summer index.jsx :1094–1105, :5876–5943): the page stays the print boundary, but the workspace grows to show
  // anything past its bottom, in view under a grey veil, never clipped. Report editor only; Preview shows the page.
  const paper = !!props.studio?.preview
  const offPage = (widget) => !!props.studio && reportWidgetOffPage(yielded(widget), canvas)
  const overflowBottom = props.studio && !paper
    ? Math.max(canvas.height, ...displayWidgets.map((widget) => {
        const rect = dropShown(widget) || yielded(widget)
        return rect.y + Math.max(rect.h, rect === widget.layout ? dashboardWidgetHeight(widget) : 0)
      }))
    : canvas.height
  const overflowing = overflowBottom > canvas.height + 0.5
  const outsideNote = (count) =>
    count ? (count === 1 ? labels.designer.outsidePageOne : (labels.designer.outsidePageMany || "").replace("{{count}}", String(count))) : null
  // R9 (DESIGN-LOG §44): a table whose rows don't all fit its frame prints the rows shown whole on the page and the
  // rest on continuation pages (paginateReportTab, the same stops). The V2 Preview says so under the table and in the
  // note; his classic Preview keeps his own (code review of bd134fb..1a77a15, #8).
  const continuedRows = (widget) => {
    if (!v2 || !["table", "freeTable"].includes(widget.type)) return 0
    const stops = contentStops[widget.id]
    const shown = shownTableRows(stops, widget.layout.h)
    return shown == null ? 0 : Math.max(0, stops.length - shown)
  }
  const plural = (count, one, many) => (count === 1 ? one : (many || "").replace("{{count}}", String(count))) || null
  const pageNote = (outside, rows) =>
    [outsideNote(outside), rows ? plural(rows, labels.designer.rowsContinueOne, labels.designer.rowsContinueMany) : null].filter(Boolean).join(" · ") || null
  // §75: a sheet style with no box round its sections (Swiss, Lesha) puts the note under the table's last whole row:
  // at the frame's foot it sat up to a row lower, nearer the next section's title than its own table (Artyom: «такое
  // же как до другой таблицы сверху»). A row's stop is its foot plus the body's bottom padding.
  const boxless = ["swiss", "lesha"].includes(sheetStyle(config.design))
  const continuesTop = (widget) => {
    const stops = contentStops[widget.id]
    const shown = shownTableRows(stops, widget.layout.h)
    return widget.layout.y + (boxless && shown ? Math.min(stops[shown - 1], widget.layout.h) : widget.layout.h) + 2
  }
  const continuesLines = (shown) => shown.map((widget) => {
    const rows = continuedRows(widget)
    return rows ? (
      <p key={`continues-${widget.id}`} className="datox-react-dashboard__continues" data-testid="table-continues" data-widget-id={widget.id}
        style={{ position: "absolute", left: widget.layout.x, top: continuesTop(widget), width: widget.layout.w }}>
        {plural(rows, labels.designer.continuesOne, labels.designer.continuesMany)}
      </p>
    ) : null
  })
  // After a grid drop or resize, put the moved elements exactly on their new displayed rects: Rnd
  // keeps its own position and size when the props come back unchanged (e.g. compacted back).
  useClientLayoutEffect(() => {
    const ids = settleRef.current
    if (!ids) return
    settleRef.current = null
    ids.forEach((id) => {
      const shown = displayById.get(id)
      const instance = widgetRefs.current.get(id)
      if (!shown || !instance) return
      instance.updatePosition({ x: shown.layout.x, y: shown.layout.y })
      instance.updateSize({ width: shown.layout.w, height: dashboardWidgetHeight(shown) })
      const element = instance.resizableElement?.current
      if (element) element.style.transform = `translate(${shown.layout.x}px, ${shown.layout.y}px)`
    })
  })
  const fitContext = useMemo(
    () => ({
      reportNeed: (id, px, stops) => {
        setContentNeeds((current) => (Math.abs((current[id] || 0) - px) <= 2 ? current : { ...current, [id]: px }))
        // Row stops move with column widths; only a change of more than 2px on any stop is stored.
        setContentStops((current) => {
          const known = current[id]
          const same =
            (!known && !stops) ||
            (known && stops && known.length === stops.length && known.every((value, index) => Math.abs(value - stops[index]) <= 2))
          return same ? current : { ...current, [id]: stops }
        })
      },
      reportKpiFit: (id, fit) =>
        setKpiFits((current) =>
          current[id]?.full === fit?.full || (!fit && !current[id])
            ? current
            : { ...current, [id]: fit || undefined },
        ),
      kpiFits,
      // KPIs share a value baseline per row: same page, same top and height.
      kpiRows: Object.fromEntries(
        displayWidgets
          .filter((widget) => widget.type === "kpi")
          .map((widget) => [widget.id, `${activeTab.id}:${widget.layout.y}:${widget.layout.h}`]),
      ),
    }),
    [kpiFits, displayWidgets, activeTab.id],
  )
  const selectedTable = useMemo(
    () => props.outputTables.find((table) => table.datasetID === props.selectedDatasetID) || props.outputTables[0],
    [props.outputTables, props.selectedDatasetID],
  )
  const selectedDashboard = useMemo(
    () => props.dashboards?.find((dashboard) => dashboard.id === props.selectedDashboardID),
    [props.dashboards, props.selectedDashboardID],
  )
  const selectedDataset = useMemo(
    () => props.datasets.find((dataset) => dataset.id === props.selectedDatasetID) || props.datasets[0],
    [props.datasets, props.selectedDatasetID],
  )
  const paletteItems = useMemo(() => {
    const query = search.trim().toLowerCase()
    return query ? WIDGETS.filter((item) => widgetLabel(labels, item.type).toLowerCase().includes(query)) : WIDGETS
  }, [labels, search])

  useEffect(() => {
    if (!props.selectedDatasetID && selectedTable) props.onSelectDataset?.(selectedTable.datasetID)
  }, [props, selectedTable])

  useEffect(() => {
    const incoming = cloneDashboardConfig(props.config || defaultConfig(labels.defaultTabName, labels.defaultTabName))
    if (dashboardRef.current === props.selectedDashboardID && JSON.stringify(incoming) === JSON.stringify(configRef.current)) return
    // The same dashboard re-read with other content (the agent's save, or another tab's): which widgets are new or
    // changed, for the host to point them out (backlog #25). Optional; never on a switch to another dashboard.
    if (dashboardRef.current === props.selectedDashboardID && configRef.current && props.studio?.onReplaced) {
      const changed = changedWidgetIds(configRef.current, incoming)
      if (changed.length) props.studio.onReplaced(changed)
    }
    dashboardRef.current = props.selectedDashboardID
    configRef.current = incoming
    setDraftConfig(incoming)
    setPreviewFilters({})
    undoStackRef.current = []
    redoStackRef.current = []
    setHistoryVersion((value) => value + 1)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
  }, [props.config, props.configSyncKey, props.selectedDashboardID])

  useEffect(() => {
    try {
      window.localStorage.setItem("datox-dashboard-snap", String(snapEnabled))
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }, [snapEnabled])

  useEffect(() => {
    if (!activeTabId && config.tabs?.[0]?.id) setActiveTabId(config.tabs[0].id)
    if (activeTabId && config.tabs?.length && !config.tabs.some((tab) => tab.id === activeTabId))
      setActiveTabId(config.tabs[0].id)
  }, [activeTabId, config.tabs])

  useEffect(() => {
    const available = new Set(widgets.map((widget) => widget.id))
    setSelectedWidgetIds((ids) => (ids.every((id) => available.has(id)) ? ids : ids.filter((id) => available.has(id))))
    if (activeWidgetId && !available.has(activeWidgetId)) setActiveWidgetId(null)
  }, [activeTab.id, activeWidgetId, widgets])

  useEffect(() => {
    if (!shareAccessOpen) return
    if (!props.dashboardShareReviewers?.length) return
    setShareReviewers(
      props.dashboardShareReviewers.map((reviewer) => ({
        id: widgetIdValue(),
        approvalIds: reviewer.approvalIds || [],
        name: reviewer.name || "",
        email: reviewer.email,
        clientValue: reviewer.clientValue || "",
        selected: true,
        status: reviewer.status === "requested" ? labels.sent : reviewer.status || labels.sent,
      })),
    )
  }, [shareAccessOpen, props.dashboardShareReviewers, labels.sent])

  const updateConfig = (next, options = {}) => {
    if (locked) return
    const nextConfig = cloneDashboardConfig(next)
    if (JSON.stringify(nextConfig) === JSON.stringify(configRef.current)) return
    const now = Date.now()
    const last = undoCoalesceRef.current
    const coalesce = !!options.coalesce && last.key === options.coalesce && now - last.at < UNDO_COALESCE_MS
    undoCoalesceRef.current = options.coalesce ? { key: options.coalesce, at: now } : { key: null, at: 0 }
    if (!coalesce) undoStackRef.current = [...undoStackRef.current.slice(-49), cloneDashboardConfig(configRef.current)]
    redoStackRef.current = []
    configRef.current = nextConfig
    setDraftConfig(nextConfig)
    setHistoryVersion((value) => value + 1)
    props.onConfigChange?.(nextConfig)
  }
  const updateTabs = (nextTabs) => updateConfig({ ...config, tabs: nextTabs })
  // HUNT P10: a new page takes the next "Tab N" no page uses yet (after a delete, the count alone repeats a name).
  const nextTabName = (tabs = config.tabs || []) => {
    const used = new Set(tabs.map((tab) => tab.name))
    let count = tabs.length + 1
    while (used.has(labels.newTabName.replace("{count}", String(count)))) count += 1
    return labels.newTabName.replace("{count}", String(count))
  }
  const updateActiveTab = (patch) =>
    updateTabs((config.tabs || [activeTab]).map((tab) => (tab.id === activeTab.id ? { ...tab, ...patch } : tab)))
  const updateWidgets = (nextWidgets, options) =>
    updateConfig({
      ...config,
      canvas: {
        ...(config.canvas || {}),
        height: props.studio ? config.canvas?.height || 794 : canvasAutoHeight(config.canvas?.height || 794, nextWidgets),
      },
      tabs: (config.tabs || [activeTab]).map((tab) =>
        tab.id === activeTab.id ? { ...tab, widgets: nextWidgets } : tab,
      ),
    }, options)
  const selectedWidget = widgets.find((widget) => widget.id === activeWidgetId)
  const editorWidget = widgets.find((widget) => widget.id === editorWidgetId)
  const fullViewWidget = widgets.find((widget) => widget.id === fullViewWidgetId)
  const closeSourceExplanation = () => {
    sourceExplanationRequestRef.current.id += 1
    sourceExplanationRequestRef.current.controller?.abort()
    sourceExplanationRequestRef.current.controller = null
    setSourceExplanation(null)
  }
  const loadSourceExplanation = async (request, refresh = false) => {
    const cacheKey = JSON.stringify([
      props.configSyncKey,
      request.dashboardID,
      request.tabID,
      request.widgetID,
      request.widgetData,
    ])
    const cached = sourceExplanationCacheRef.current.get(cacheKey)
    if (!refresh && cached?.expiresAt > Date.now()) {
      setSourceExplanation({ loading: false, error: "", data: cached.data, request })
      return
    }
    const id = sourceExplanationRequestRef.current.id + 1
    sourceExplanationRequestRef.current.controller?.abort()
    const controller = new AbortController()
    sourceExplanationRequestRef.current = { id, controller }
    setSourceExplanation({ loading: true, error: "", data: null, request })
    let timedOut = false
    const timeout = window.setTimeout(() => {
      timedOut = true
      controller.abort()
    }, 90_000)
    try {
      const data = await props.onExplainSource(request, controller.signal)
      if (sourceExplanationRequestRef.current.id === id) {
        sourceExplanationCacheRef.current.set(cacheKey, { data, expiresAt: Date.now() + 5 * 60_000 })
        if (sourceExplanationCacheRef.current.size > 50)
          sourceExplanationCacheRef.current.delete(sourceExplanationCacheRef.current.keys().next().value)
        setSourceExplanation({ loading: false, error: "", data, request })
      }
    } catch (error) {
      if (sourceExplanationRequestRef.current.id === id && (timedOut || error?.name !== "AbortError"))
        setSourceExplanation({ loading: false, error: labels.requestFailed, data: null, request })
    } finally {
      window.clearTimeout(timeout)
      if (sourceExplanationRequestRef.current.id === id) sourceExplanationRequestRef.current.controller = null
    }
  }

  const newWidget = (type, position) => {
    const widget = autoTitled(createWidget(type, widgets.length, selectedTable, labels, position))
    if (type === "header" && config.design?.logoUrl) widget.style = { ...widget.style, logoUrl: config.design.logoUrl }
    return widget
  }
  const addWidget = (type, position) => {
    if (locked) return
    const widget = newWidget(type, position)
    // On the grid a new widget gets its type's default footprint (tokens/layout.js), in the first free slot on
    // this page. §33: a full page no longer asks; as summer's findNextAvailablePosition, it goes to the first free
    // spot below the page, in view under the veil, with "Move to next page" on its bar.
    if (!gridMode) return insertWidget({ ...widget, layout: placeLayout(widget.layout, widgets, widget) }, "data")
    // HUNT P6: a Header on a page without a page-top header goes to the top; the rest moves straight down under it
    // (pushed within its columns), in the same save.
    if (type === "header" && !widgets.some((item) => isPageTopHeader(item, canvas))) {
      const top = defaultGridLayout(type, { x: grid.margin, y: grid.margin }, grid)
      const saved = Object.fromEntries(widgets.map((item) => [item.id, item.layout]))
      const pushed = pushLayoutForDrag({ ...saved, [widget.id]: top }, widget.id, top, canvas, { relocate: false })
      const others = widgets.map((item) => (pushed[item.id] && !sameRect(pushed[item.id], item.layout) ? { ...item, layout: { ...item.layout, ...pushed[item.id] } } : item))
      return insertWidget({ ...widget, layout: top }, "data", others)
    }
    const start = defaultGridLayout(type, widget.layout, grid)
    const slot = freeGridSlot(start, widgets, grid, widget)
    if (slot) return insertWidget({ ...widget, layout: slot }, "data")
    // Fit first (§32): the saved heights can be taller than what is shown. On the content-fit heights, compacted, the
    // first free slot takes it, in one save with the fitted widgets; only a page that is full even then asks.
    const fitted = fittedPageWidgets()
    const fitSlot = fitted && freeGridSlot(start, fitted, grid, widget)
    if (fitSlot) return insertWidget({ ...widget, layout: fitSlot }, "data", fitted)
    insertWidget({ ...widget, layout: nearestFreeGridPlacement(start, widgets, grid) }, "data")
  }
  // The page's widgets at their content-fit heights (contentFitLayouts), compacted straight up (summer compaction):
  // null when nothing shrinks. Unchanged widgets keep their identity, so only the fitted ones are written.
  const fittedPageWidgets = () => {
    const { layouts, shrunk } = contentFitLayouts(widgets, canvas, contentNeeds, contentStops)
    if (!shrunk.length) return null
    const settled = compactLayoutOnDrop(layouts, canvas)
    return widgets.map((item) => (settled[item.id] && !sameRect(settled[item.id], item.layout) ? { ...item, layout: { ...item.layout, ...settled[item.id] } } : item))
  }
  // `others`: the page's widgets as they are saved with it (a library drop passes the pushed layout, one save).
  const insertWidget = (widget, panelName, others = widgets) => {
    updateWidgets([...others, widget])
    setActiveWidgetId(widget.id)
    setSelectedWidgetIds([widget.id])
    if (props.studio) { setEditorWidgetId(widget.id); if (panelName) setWidgetEditorPanel(panelName); props.studio.onPanel("properties") }
  }
  const addTab = () => {
    if (locked) return
    const tab = { id: widgetIdValue(), name: nextTabName(), widgets: [] }
    updateTabs([...(config.tabs || []), tab])
    setActiveTabId(tab.id)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
  }
  const duplicateTab = (tab) => {
    if (locked) return
    const copy = { ...copyReportPage(tab), name: `${tab.name || labels.defaultTabName} ${labels.copySuffix}` }
    updateTabs([...(config.tabs || []), copy])
    setActiveTabId(copy.id)
    setTabMenuId(null)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
    setEditorWidgetId(null)
  }
  const deleteTab = (tabId) => {
    if (locked || (config.tabs || []).length <= 1) return
    reportDeleted("page", (config.tabs || []).filter((tab) => tab.id === tabId).map((tab) => tab.name || ""))
    const nextTabs = (config.tabs || []).filter((tab) => tab.id !== tabId)
    updateTabs(nextTabs)
    if (activeTab.id === tabId) setActiveTabId(nextTabs[0]?.id ?? null)
    setTabMenuId(null)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
    setEditorWidgetId(null)
  }

  const openRenameDashboard = () =>
    setRenameState({ kind: "dashboard", title: labels.renameDashboard, name: config.title || labels.dashboardTitle })
  const openRenameTab = (tab) =>
    setRenameState({ kind: "tab", tabId: tab.id, title: labels.renameTab, name: tab.name || labels.defaultTabName })
  const openRenameWidget = (widget) =>
    setRenameState({
      kind: "widget",
      widgetId: widget.id,
      title: labels.renameWidget,
      name: widget.title || widgetLabel(labels, widget.type),
    })
  const submitRename = (event) => {
    event.preventDefault()
    const current = renameState
    const name = current?.name?.trim()
    if (!name) return
    if (current.kind === "dashboard") updateConfig({ ...config, title: name })
    if (current.kind === "tab")
      updateTabs((config.tabs || []).map((tab) => (tab.id === current.tabId ? { ...tab, name } : tab)))
    if (current.kind === "widget") updateWidget(current.widgetId, { title: name })
    setRenameState(null)
  }

  const updateWidget = (widgetId, patch, options) => {
    const target = widgets.find((widget) => widget.id === widgetId)
    // One header across pages (summer syncHeaderConfiguration): its title and style reach every page's header. V2
    // only; his classic editor edits one page's header, as before.
    if (v2 && target?.type === "header" && ("title" in patch || "style" in patch)) {
      const edited = { ...target, ...patch }
      const tabs = (config.tabs || [activeTab]).map((tab) =>
        tab.id === activeTab.id ? { ...tab, widgets: widgets.map((widget) => (widget.id === widgetId ? edited : widget)) } : tab,
      )
      return updateConfig({ ...config, tabs: syncHeaderWidgets(tabs, edited) }, options)
    }
    // Smart titles (backlog #20): a hand-added widget (isAutoTitle) is renamed from its data; a typed title sticks.
    // Bobur's cleanup (37f2a2b) on top: a type change drops the old type's settings his validator rejects.
    updateWidgets(widgets.map((widget) => (widget.id === widgetId ? withSmartTitle(widget, withoutStaleSettings(widget, patch), smartTitleOptions({ ...widget, ...patch })) : widget)), options)
  }
  const smartTitleOptions = (widget) => {
    const table = widgetOutputTable(props.outputTables, widget)
    return { labels, headers: table?.headers, datasetName: table?.name || props.datasets?.find((dataset) => dataset.id === widget?.data?.dataset_id)?.name }
  }
  // A widget added by hand starts with a title from its data ("Sum of Weight by Sector"), else its type's name, and
  // keeps following its data until the author types one. Agent widgets never carry isAutoTitle.
  const autoTitled = (widget) => {
    // V2 only: his classic editor names a new widget as he did (its type's default title).
    if (!v2 || ["text", "title", "header"].includes(widget.type)) return widget
    return { ...widget, isAutoTitle: true, title: buildSmartTitle(widget, smartTitleOptions(widget)) || widget.title }
  }

  // The V2 editor shows "<name> deleted · Undo" after every delete (UX research E1); optional for other hosts.
  const reportDeleted = (kind, names) => props.studio?.onDeleted?.({ kind, names })
  // The page without `ids`. On a report page the rest is compacted straight up, as summer did on delete
  // (compactLayoutVertical, index.jsx :2966; DESIGN-LOG §34): the widgets below close the freed space, columns kept.
  // The delete and the compaction are one updateWidgets: one save, one undo step back to the exact previous layout.
  // HUNT P2: only the deleted widgets' own columns close up (closeDeletedGap); the gaps the user left elsewhere stay.
  const withoutWidgets = (ids) => {
    const rest = widgets.filter((widget) => !ids.includes(widget.id))
    return gridMode ? closeDeletedGap(rest, widgets.filter((widget) => ids.includes(widget.id)), canvas) : rest
  }
  const deleteWidget = (widgetId) => {
    reportDeleted("widget", widgets.filter((widget) => widget.id === widgetId).map((widget) => widget.title || widgetLabel(labels, widget.type)))
    updateWidgets(withoutWidgets([widgetId]))
    if (activeWidgetId === widgetId) setActiveWidgetId(null)
    setSelectedWidgetIds((ids) => ids.filter((id) => id !== widgetId))
    if (editorWidgetId === widgetId) setEditorWidgetId(null)
    if (fullViewWidgetId === widgetId) setFullViewWidgetId(null)
  }

  const duplicateWidget = (widgetId) => {
    const source = widgets.find((widget) => widget.id === widgetId)
    if (!source || locked) return
    const copy = {
      ...cloneDashboardConfig(source),
      id: widgetIdValue(),
      title: `${source.title} ${labels.copySuffix}`,
    }
    const start = { ...source.layout, x: source.layout.x + 28, y: source.layout.y + 28 }
    // On a full report page the copy goes below the page, under the veil, like a new widget (§33).
    const snapped = gridMode ? snapGridLayout(start, grid, widgetMinCells(source.type, source)) : start
    const slot = gridMode ? freeGridSlot(snapped, widgets, grid, source) || nearestFreeGridPlacement(snapped, widgets, grid) : placeLayout(start, widgets, source)
    insertWidget({ ...copy, layout: slot })
  }
  const duplicateSelectedWidgets = () => {
    if (locked) return
    const sources = widgets.filter((widget) => selectedWidgetIds.includes(widget.id))
    // HUNT P5: on the grid each copy goes where the ⋯ Duplicate puts it (on the cells, the first free slot from one cell
    // below and right of its original, no overlap; below the page under the veil when the page is full).
    if (gridMode) {
      const placed = [...widgets]
      const copies = sources.map((source) => {
        const start = snapGridLayout({ ...source.layout, x: source.layout.x + 28, y: source.layout.y + 28 }, grid, widgetMinCells(source.type, source))
        const layout = freeGridSlot(start, placed, grid, source) || nearestFreeGridPlacement(start, placed, grid)
        const copy = { ...cloneDashboardConfig(source), id: widgetIdValue(), title: `${source.title} ${labels.copySuffix}`, layout: { ...source.layout, ...layout } }
        placed.push(copy)
        return copy
      })
      if (!copies.length) return
      updateWidgets(placed)
      setSelectedWidgetIds(copies.map((widget) => widget.id))
      setActiveWidgetId(copies.at(-1).id)
      return
    }
    const canvasWidth = config.canvas?.width || 1123
    const offset = Array.from({ length: 40 }, (_, index) => ({
      x: 28 * ((index % 4) + 1),
      y: 28 * (Math.floor(index / 4) + 1),
    })).find((candidate) =>
      sources.every((source) => {
        const layout = { ...source.layout, x: source.layout.x + candidate.x, y: source.layout.y + candidate.y }
        return layout.x >= 0 && layout.x + layout.w <= canvasWidth && !placementCollides(layout, widgets, [])
      }),
    ) || { x: 28, y: 28 }
    const copies = sources.map((source) => ({
      ...cloneDashboardConfig(source),
      id: widgetIdValue(),
      title: `${source.title} ${labels.copySuffix}`,
      layout: fitLayout({ ...source.layout, x: source.layout.x + offset.x, y: source.layout.y + offset.y }, source),
    }))
    if (!copies.length) return
    updateWidgets([...widgets, ...copies])
    setSelectedWidgetIds(copies.map((widget) => widget.id))
    setActiveWidgetId(copies.at(-1).id)
  }
  const deleteSelectedWidgets = () => {
    if (locked || !selectedWidgetIds.length) return
    reportDeleted("widget", widgets.filter((widget) => selectedWidgetIds.includes(widget.id)).map((widget) => widget.title || widgetLabel(labels, widget.type)))
    updateWidgets(withoutWidgets(selectedWidgetIds))
    setSelectedWidgetIds([])
    setActiveWidgetId(null)
    setEditorWidgetId(null)
  }

  const setOrientation = (orientation) => {
    const oldWidth = config.canvas?.width || 1123
    const oldHeight = config.canvas?.height || 794
    const nextCanvas =
      orientation === "portrait" ? { orientation, width: 794, height: 1123 } : { orientation, width: 1123, height: 794 }
    const xRatio = nextCanvas.width / oldWidth
    // Report pages: x and w through the grid columns, y and h kept (summer rescaleLayoutToCanvas, backlog #15); what
    // no longer fits goes under the off-page veil. Free dashboards: x and w by the width ratio, y and h kept.
    updateConfig({
      ...config,
      canvas: nextCanvas,
      tabs: (config.tabs || []).map((tab) => ({
        ...tab,
        widgets: gridMode
          ? rescaleLayoutToCanvas(tab.widgets || [], { width: oldWidth, height: oldHeight }, nextCanvas)
          : (tab.widgets || []).map((widget) => ({
              ...widget,
              layout: constrainReportLayout({
                x: Math.round(widget.layout.x * xRatio),
                y: widget.layout.y,
                w: Math.max(80, Math.round(widget.layout.w * xRatio)),
                h: widget.layout.h,
              }, { ...nextCanvas, height: Number.POSITIVE_INFINITY }),
            })),
      })),
    })
  }
  const tidyLayout = () => {
    // A report page tidies on its grid, keeping the user's arrangement (backlog #14: normalizeLayout plus the
    // vertical compaction, columns kept); a page nobody arranged (agent-written, or widgets dropped in one spot) is
    // arranged by type first. Free dashboards keep main's arranger.
    if (gridMode) {
      if (locked) return
      const saved = activeTab.widgets || []
      const tidied = tidyReportPage(saved, canvas, isChartWidget).widgets
      const before = new Map(saved.map((widget) => [widget.id, JSON.stringify(widget.layout)]))
      const moved = tidied.some((widget) => JSON.stringify(widget.layout) !== before.get(widget.id))
      setActiveWidgetId(null)
      setSelectedWidgetIds([])
      setEditorWidgetId(null)
      if (!moved) return
      updateWidgets(tidied)
      // The V2 editor says "Page tidied · Undo" (its undo is this canvas's undo).
      props.studio?.onTidied?.()
      return
    }
    const arranged = arrangeByTypeLayout(widgets, config.canvas?.width || 1123, isChartWidget)
    updateConfig({
      ...config,
      canvas: { ...(config.canvas || {}), height: Math.max(config.canvas?.height || 794, arranged.bottom) },
      tabs: (config.tabs || []).map((tab) => (tab.id === activeTab.id ? { ...tab, widgets: arranged.widgets } : tab)),
    })
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
    setEditorWidgetId(null)
  }

  // HUNT P9: Undo and Redo show the page they change. When the page on screen is not among the changed ones, the
  // first changed (or restored) page in page order comes up first; a removed page leaves the view on its neighbour.
  const showChangedPage = (from, to) => {
    const before = new Map((from?.tabs || []).map((tab) => [tab.id, JSON.stringify(tab)]))
    const changed = (to?.tabs || []).filter((tab) => before.get(tab.id) !== JSON.stringify(tab)).map((tab) => tab.id)
    if (!changed.length || changed.includes(activeTab.id)) return
    selectPage(changed[0])
    setSelectedWidgetIds([])
  }
  const undo = () => {
    const previous = undoStackRef.current.at(-1)
    if (!previous || locked) return
    undoStackRef.current = undoStackRef.current.slice(0, -1)
    undoCoalesceRef.current = { key: null, at: 0 }
    redoStackRef.current = [...redoStackRef.current, cloneDashboardConfig(config)]
    showChangedPage(config, previous)
    configRef.current = previous
    setDraftConfig(previous)
    setHistoryVersion((value) => value + 1)
    props.onConfigChange?.(previous)
  }

  const redo = () => {
    const next = redoStackRef.current.at(-1)
    if (!next || locked) return
    redoStackRef.current = redoStackRef.current.slice(0, -1)
    undoCoalesceRef.current = { key: null, at: 0 }
    undoStackRef.current = [...undoStackRef.current, cloneDashboardConfig(config)]
    showChangedPage(config, next)
    configRef.current = next
    setDraftConfig(next)
    setHistoryVersion((value) => value + 1)
    props.onConfigChange?.(next)
  }

  const updateEditorWidgetData = (patch) => {
    if (!editorWidget) return
    updateWidget(editorWidget.id, { data: { ...(editorWidget.data || {}), ...patch } })
  }
  const changeWidgetType = (type, title) => {
    if (!editorWidget) return
    const oldChart = isChartWidget(editorWidget.type)
    const newChart = isChartWidget(type)
    const columns = editorWidgetColumns
    const numeric = editorWidgetNumericColumns
    const data = { ...(editorWidget.data || {}) }
    if (newChart && !oldChart) {
      data.label = columns.includes(data.label) ? data.label : columns[0]
      data.metric = numeric.includes(data.metric) ? data.metric : numeric[0]
      if (!["sum", "avg", "min", "max", "count"].includes(data.aggregation)) data.aggregation = "sum"
    }
    if (type === "scatter") {
      data.xColumn = numeric.includes(data.xColumn)
        ? data.xColumn
        : numeric.find((column) => column !== data.metric) || ""
      data.yColumn = numeric.includes(data.yColumn) ? data.yColumn : data.metric || ""
      data.sizeColumn = numeric.includes(data.sizeColumn) ? data.sizeColumn : ""
    }
    if (
      type === "freeTable" &&
      !data.freeStyle &&
      editorWidgetConversionTable &&
      !editorWidgetConversionTable.loadError &&
      editorWidgetConversionTable.headers.length <= 20 &&
      editorWidgetConversionTable.rows.length <= 50
    )
      data.freeStyle = freeStyleFromTable(editorWidgetConversionTable, labels)
    if (type === "freeTable" && data.freeStyle) delete data.dataset_id
    if (type === "table" && !data.dataset_id) data.dataset_id = selectedTable?.datasetID
    updateWidget(editorWidget.id, {
      type,
      ...(title ? { title } : {}),
      data,
      ...(type !== "kpi" && editorWidget.kpi_calculation ? { kpi_calculation: null } : {}),
    })
  }
  const addEditorFilter = () => {
    if (!editorWidget) return
    updateEditorWidgetData({
      filters: [
        ...(editorWidget.data?.filters || []),
        { id: widgetIdValue(), column: editorWidgetColumns[0] || "", operator: "contains", value: "" },
      ],
    })
  }
  const toggleMeasure = (column) => {
    if (!editorWidget) return
    const measures = editorWidget.data?.measures ?? [editorWidget.data?.metric].filter(Boolean)
    const next = measures.includes(column) ? measures.filter((item) => item !== column) : [...measures, column]
    if (next.length) updateEditorWidgetData({ measures: next })
  }
  const updateEditorFilter = (filterId, patch) => {
    if (!editorWidget) return
    updateEditorWidgetData({
      filters: (editorWidget.data?.filters || []).map((filter) =>
        filter.id === filterId ? { ...filter, ...patch } : filter,
      ),
    })
  }
  const removeEditorFilter = (filterId) => {
    if (!editorWidget) return
    updateEditorWidgetData({ filters: (editorWidget.data?.filters || []).filter((filter) => filter.id !== filterId) })
  }
  const toggleEditorColumn = (column) => {
    if (!editorWidget) return
    const currentColumns = editorWidget.data?.columns?.length ? editorWidget.data.columns : editorWidgetColumns
    const nextColumns = currentColumns.includes(column)
      ? currentColumns.filter((item) => item !== column)
      : [...currentColumns, column]
    if (!nextColumns.length) return
    const nextStyle =
      currentColumns.includes(column) && Array.isArray(editorWidget.style?.cellColorRules)
        ? {
            ...(editorWidget.style || {}),
            cellColorRules: editorWidget.style.cellColorRules.filter((rule) => nextColumns.includes(rule.column)),
          }
        : editorWidget.style
    // Bobur's rules (in `data`) follow the columns too.
    const dataRules =
      currentColumns.includes(column) && Array.isArray(editorWidget.data?.cellColorRules)
        ? { cellColorRules: editorWidget.data.cellColorRules.filter((rule) => nextColumns.includes(rule.column)) }
        : {}
    updateWidget(editorWidget.id, {
      data: { ...(editorWidget.data || {}), columns: nextColumns, ...dataRules },
      style: nextStyle,
    })
  }

  // Bobur (6563385): a widget without a dataset is "not connected"; do not fall back to the selected table.
  const editorWidgetTable = editorWidget?.data?.dataset_id || editorWidget?.data?.dataset_ref
    ? widgetOutputTable(props.outputTables, editorWidget)
    : undefined
  // The filter rows' column kind and value hints, read once per table and column rather than over every row on each
  // render (Review 2 §11). A module cache keyed by the rows array, so it needs no hook and drops with the table.
  const filterColumnInfo = (column) => filterColumnInfoFor(editorWidgetTable?.rows, column)
  const editorWidgetNumericColumns =
    editorWidgetTable?.headers?.filter((header) =>
      editorWidgetTable.rows.some((row) => numericCell(row[header]) !== null),
    ) || []
  const editorWidgetColumns = editorWidgetTable?.headers || []
  const editorWidgetVisibleColumns = editorWidget?.data?.columns?.length
    ? editorWidget.data.columns
    : editorWidgetColumns
  // Bobur's rules (`data.cellColorRules`) are listed with ours and move into `style` on the first edit.
  const editorCellColorRules = [
    ...(editorWidget?.style?.cellColorRules || []),
    ...(Array.isArray(editorWidget?.data?.cellColorRules) ? editorWidget.data.cellColorRules : []),
  ]

  const updateEditorWidgetStyle = (patch) => {
    if (!editorWidget) return
    updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), ...patch } })
  }

  const updateEditorCellColorRules = (nextRules) => {
    if (!editorWidget) return
    if (!editorWidget.data?.cellColorRules) return updateEditorWidgetStyle({ cellColorRules: nextRules })
    const { cellColorRules: _moved, ...data } = editorWidget.data
    updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), cellColorRules: nextRules }, data })
  }

  const createEditorCellColorRule = (column = "") => ({
    id: widgetIdValue(),
    column,
    operator: "equals",
    value: "",
    color: "#dbeafe",
    highlight_row: false,
  })

  const openEditorCellColorRule = (rule) => {
    setCellColorRuleDraft(rule)
    setCellColorRulesOpen(true)
  }

  const closeEditorCellColorRule = () => {
    setCellColorRulesOpen(false)
    setCellColorRuleDraft(null)
  }

  const saveEditorCellColorRule = () => {
    if (!cellColorRuleDraft?.column || !cellColorRuleDraft?.operator) return
    const existing = editorCellColorRules.some((rule) => rule.id === cellColorRuleDraft.id)
    const nextRules = existing
      ? editorCellColorRules.map((rule) => (rule.id === cellColorRuleDraft.id ? cellColorRuleDraft : rule))
      : [...editorCellColorRules, cellColorRuleDraft]
    updateEditorCellColorRules(nextRules)
    closeEditorCellColorRule()
  }

  const updateEditorCellColorRule = (patch) => {
    setCellColorRuleDraft((current) => (current ? { ...current, ...patch } : current))
  }

  const removeEditorCellColorRule = (ruleId) => {
    updateEditorCellColorRules(editorCellColorRules.filter((rule) => rule.id !== ruleId))
  }

  // const moveEditorCellColorRule = (ruleId, direction) => {
  //   const index = editorCellColorRules.findIndex((rule) => rule.id === ruleId)
  //   if (index < 0) return
  //   const targetIndex = direction === "up" ? index - 1 : index + 1
  //   if (targetIndex < 0 || targetIndex >= editorCellColorRules.length) return
  //   const nextRules = [...editorCellColorRules]
  //   const [rule] = nextRules.splice(index, 1)
  //   nextRules.splice(targetIndex, 0, rule)
  //   updateEditorCellColorRules(nextRules)
  // }

  const clientAccess = config.clientAccess || {}
  const clientDatasetID =
    clientAccess.dataset_id || clientAccess.datasetID || props.selectedDatasetID || props.datasets?.[0]?.id || ""
  const clientColumns = datasetColumns(clientDatasetID, props.outputTables, props.datasets)
  const clientColumn = clientAccess.column || clientColumns[0] || ""
  const clientValues = uniqueColumnValues(
    props.outputTables.find((table) => table.datasetID === clientDatasetID),
    clientColumn,
  )
  const activeFilterWidgets = widgets.filter(
    (widget) => widget.type === "filter" && (widget.data?.selectedValues?.length || widget.data?.range),
  )
  const comparisonState = useDashboardKpiComparisons({
    projectID: props.projectID,
    dashboardID: selectedDashboard?.id || props.selectedDashboardID,
    revision: props.analytics?.revision ?? selectedDashboard?.revision ?? 0,
    widgets,
    outputTables: props.outputTables,
    activeFilterWidgets,
    labels,
    // Period-over-period KPI comparison runs on Bobur's /analytics API: on wherever the host passes
    // `analytics` (his prop), unless the host says otherwise.
    enabled: props.kpiComparisonsEnabled ?? !!props.analytics?.dashboardID,
  })
  const editorWidgetConversionTable = editorWidget
    ? applyWidgetTableSettings(
        applyDashboardFilters(editorWidgetTable, editorWidget, activeFilterWidgets),
        editorWidget.data,
      )
    : null
  const openWidgetEditor = (widget, panelName) => {
    setActiveWidgetId(widget.id)
    setEditorWidgetId(widget.id)
    setWidgetEditorPanel(panelName)
    setWidgetMenuId(null)
    props.studio?.onPanel("properties")
  }
  const exportWidgetTable = (widget) => {
    const table = applyWidgetTableSettings(
      applyDashboardFilters(
        widgetOutputTable(props.outputTables, widget),
        widget,
        activeFilterWidgets,
      ),
      widget.data,
    )
    if (!table) return
    const csv = [table.headers, ...table.rows.map((row) => table.headers.map((header) => row[header] ?? ""))]
      .map((row) => row.map(csvCell).join(","))
      .join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${widget.title || widgetLabel(labels, widget.type)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }
  // PDF (decision C): report pages print through our printPageLayout (Preview = PDF, §39). Bobur's server path
  // (serializeDashboardPdf → /render) rebuilds its own layout from the config, so it serves only free dashboards
  // (classic screens), when the host passes onExportDashboardPdf.
  const serverPdf = !props.studio && !!props.onExportDashboardPdf
  const openExport = async () => {
    setExportError(false)
    setExportSelection(config.tabs.map((tab) => tab.id))
    if (!serverPdf || props.readOnly || props.saving || exportingPdf) return
    setServerSnapshot(null)
    setPdfWidgetID("")
    setPdfSettings({ orientation: config.canvas?.orientation === "portrait" ? "portrait" : "landscape", margin: 0, scale: 100, scope: "all", allRows: true, density: "original", fontSize: config.design?.fontSize || "medium", gray: !!config.design?.lightGrayBackground, accent: config.design?.accent || "#2f80ed", widgets: {} })
    setExportingPdf(true)
    try {
      const exportConfig = { ...config, tabs: config.tabs.map((tab) => ({ ...tab, widgets: tab.widgets.map((widget) => widget.type === "filter" && previewFilters[widget.id] ? { ...widget, data: { ...widget.data, selectedValues: previewFilters[widget.id] } } : widget) })) }
      const snapshot = props.onPrepareExport ? await props.onPrepareExport(exportConfig) : { config: cloneDashboardConfig(exportConfig), datasets: props.outputTables.map((table) => ({ id: table.datasetID, name: table.name, columns: table.headers.map((name) => ({ name })), rows: table.rows })) }
      setServerSnapshot(snapshot)
    } catch {
      setExportError(true)
    } finally {
      setExportingPdf(false)
    }
  }
  const closeExport = () => {
    setExportSelection(null)
    setServerSnapshot(null)
  }
  const exportPdf = async (selectedPages) => {
    if (props.readOnly || props.saving || exportingPdf || !selectedPages?.length) return
    if (serverPdf) {
      if (!serverSnapshot || !pdfHtml) return
      setExportingPdf(true)
      setExportError(false)
      try {
        const file = await props.onExportDashboardPdf(serverSnapshot, pdfHtml, serverSnapshot.config.title || labels.dashboardTitle)
        if (props.onSaveExport) await props.onSaveExport(serverSnapshot, selectedPages, file)
        const url = URL.createObjectURL(file)
        const link = document.createElement("a")
        link.href = url
        link.download = `${serverSnapshot.config.title || labels.dashboardTitle}.pdf`
        link.click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        closeExport()
      } catch {
        setExportError(true)
      } finally {
        setExportingPdf(false)
      }
      return
    }
    setExportingPdf(true)
    setExportError(false)
    try {
      // The PDF prints the grid-normalised layout the editor shows (the display fit is applied when
      // the export pages render, so it is never written into the config).
      const exportConfig = { ...config, tabs: config.tabs.map((tab) => ({ ...tab, widgets: (gridMode ? normalizeLayout(tab.widgets, canvas) : tab.widgets).map((widget) => widget.type === "filter" && previewFilters[widget.id] ? { ...widget, data: { ...widget.data, selectedValues: previewFilters[widget.id] } } : widget) })) }
      configRef.current = exportConfig
      setDraftConfig(exportConfig)
      props.onConfigChange?.(exportConfig)
      const snapshot = props.onPrepareExport ? await props.onPrepareExport(exportConfig) : { config: cloneDashboardConfig(exportConfig), datasets: props.outputTables.map((table) => ({ id: table.datasetID, name: table.name, columns: table.headers.map((name) => ({ name })), rows: table.rows })) }
      setExportSnapshot({ ...snapshot, pages: selectedPages })
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      const file = await renderReportPdf(exportRef.current, snapshot.config)
      if (props.onSaveExport) await props.onSaveExport(snapshot, selectedPages, file)
      const url = URL.createObjectURL(file)
      const link = document.createElement("a")
      link.href = url
      link.download = `${snapshot.config.title || labels.dashboardTitle}.pdf`
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setExportSelection(null)
    } catch {
      setExportError(true)
    } finally {
      setExportingPdf(false)
      setExportSnapshot(null)
    }
  }
  const publishDashboard = async () => {
    const url = await props.onPublishDashboard?.()
    if (url) {
      setPublishedLink(url)
      setPublishedLinkCopied(false)
    }
  }
  const copyPublishedLink = async () => {
    await navigator.clipboard?.writeText(publishedLink)
    setPublishedLinkCopied(true)
    window.setTimeout(() => setPublishedLinkCopied(false), 1800)
  }
  const submitShareAccess = async (event) => {
    event.preventDefault()
    const reviewersToSend = uniqueReviewers(
      shareReviewers.filter(
        (reviewer) => reviewer.selected && reviewer.email.trim() && reviewer.status !== labels.duplicateEmail,
      ),
    )
    if (!reviewersToSend.length || hasDuplicateReviewerEmails(shareReviewers)) return
    const pendingEmails = new Set(reviewersToSend.map((reviewer) => reviewer.email))
    setShareAccessSending(true)
    setShareReviewers((items) =>
      items.map((item) =>
        pendingEmails.has(item.email.trim().toLowerCase()) ? { ...item, status: labels.sendingShareAccess } : item,
      ),
    )
    try {
      const url = await props.onShareAccess?.(reviewersToSend)
      if (url)
        setShareReviewers((items) =>
          items.map((item) =>
            pendingEmails.has(item.email.trim().toLowerCase()) ? { ...item, status: labels.sent } : item,
          ),
        )
      if (!url)
        setShareReviewers((items) =>
          items.map((item) =>
            pendingEmails.has(item.email.trim().toLowerCase()) ? { ...item, status: labels.notSent } : item,
          ),
        )
    } finally {
      setShareAccessSending(false)
    }
  }
  const updateShareReviewer = (id, patch) =>
    setShareReviewers((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  const updateShareReviewerEmail = (id, value) => {
    const emails = value
      .split(/[\n,;]/)
      .map((email) => email.trim())
      .filter(Boolean)
    if (emails.length <= 1) {
      const email = value.trim()
      const duplicate =
        email &&
        shareReviewers.some(
          (reviewer) => reviewer.id !== id && reviewer.email.trim().toLowerCase() === email.toLowerCase(),
        )
      updateShareReviewer(id, { email, status: duplicate ? labels.duplicateEmail : labels.notSent })
      return
    }
    setShareReviewers((items) =>
      items.flatMap((item) => {
        if (item.id !== id) return [item]
        return emails.map((email, index) =>
          index === 0
            ? { ...item, email, status: item.status || labels.notSent }
            : { id: widgetIdValue(), name: "", email, clientValue: "", selected: true, status: labels.notSent },
        )
      }),
    )
  }
  const removeShareReviewer = async (reviewer) => {
    if (reviewer.approvalIds?.length) await props.onRemoveShareReviewer?.(reviewer.approvalIds)
    setShareReviewers((items) =>
      items.length <= 1
        ? [{ id: "reviewer-1", name: "", email: "", clientValue: "", selected: true, status: labels.notSent }]
        : items.filter((item) => item.id !== reviewer.id),
    )
  }
  const addShareReviewer = () =>
    setShareReviewers((items) => [
      ...items,
      { id: widgetIdValue(), name: "", email: "", clientValue: "", selected: true, status: labels.notSent },
    ])
  const closeMenusOnOutsideClick = (event) => {
    if (
      event.target.closest(".datox-react-dashboard__menu-wrap") ||
      event.target.closest(".datox-react-dashboard__widget-actions") ||
      event.target.closest(".datox-react-dashboard__tab")
    )
      return
    setDashboardMenuOpen(false)
    setExportMenuOpen(false)
    setWidgetMenuId(null)
    setTabMenuId(null)
  }
  const updateFilterTarget = (datasetID, patch) => {
    if (!editorWidget) return
    const targets = editorWidget.data?.targets || []
    const existing = targets.find((target) => target.dataset_id === datasetID)
    const nextTarget = {
      dataset_id: datasetID,
      column:
        patch.column ?? existing?.column ?? datasetColumns(datasetID, props.outputTables, props.datasets)[0] ?? "",
    }
    updateEditorWidgetData({
      targets: existing
        ? targets.map((target) => (target.dataset_id === datasetID ? { ...target, ...nextTarget } : target))
        : [...targets, nextTarget],
    })
  }
  const removeFilterTarget = (datasetID) => {
    if (!editorWidget) return
    updateEditorWidgetData({
      targets: (editorWidget.data?.targets || []).filter((target) => target.dataset_id !== datasetID),
    })
  }
  const toggleFilterValue = (widget, value) => {
    if (previewing) {
      setPreviewFilters((previous) => ({ ...previous, [widget.id]: nextFilterSelectedValues(widget, value, previous[widget.id] ?? widget.data?.selectedValues ?? []) }))
      return
    }
    const selectedValues = widget.data?.selectedValues || []
    updateWidget(widget.id, {
      data: { ...(widget.data || {}), selectedValues: nextFilterSelectedValues(widget, value, selectedValues) },
    })
  }
  // main's html-to-image + jspdf@3 snapshot export (exportDashboardSnapshot) is not brought in: it needs three
  // new dependencies, and the report studio keeps Bobur's client-side export (renderReportPdf).
  const pickWidget = (widgetId, event) => {
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      pickedRef.current = widgetId
      // A plain click in the V2 editor only sets the active widget (its press-slop hold), so the active one joins first.
      setSelectedWidgetIds((selected) => {
        const ids = activeWidgetId && !selected.includes(activeWidgetId) ? [...selected, activeWidgetId] : selected
        return ids.includes(widgetId) ? ids.filter((id) => id !== widgetId) : [...ids, widgetId]
      })
      setActiveWidgetId(widgetId)
      return
    }
    if (selectedWidgetIds.includes(widgetId)) {
      setActiveWidgetId(widgetId)
      return
    }
    setSelectedWidgetIds([widgetId])
    setActiveWidgetId(widgetId)
  }
  const hideGuides = () => {
    if (verticalGuideRef.current) verticalGuideRef.current.style.display = "none"
    if (horizontalGuideRef.current) horizontalGuideRef.current.style.display = "none"
  }
  const setCollisionPreview = (ids, invalid) => {
    ids.forEach((id) =>
      widgetRefs.current
        .get(id)
        ?.resizableElement?.current?.classList.toggle("datox-react-dashboard__rnd--collision", invalid),
    )
  }
  // Report grid: the layout every drag, resize and arrow move starts from is the displayed one
  // (display fit), so widgets yield and land where they were seen.
  const displayLayoutMap = () => Object.fromEntries(displayWidgets.map((item) => [item.id, item.layout]))
  // The page-top header(s) (isPageTopHeader): a move or a drop never lands on one or pushes it (HUNT M8, P7).
  const topHeaderIds = () => widgets.filter((item) => isPageTopHeader(item, canvas)).map((item) => item.id)
  // HUNT round 2 (DESIGN-LOG §38): a move follows the §36 resize recipe. The moved widgets take their slots (never on
  // the page-top header, M8); a slot never cuts a widget above it (round 3 (a), §40): it stops under that widget as
  // shown, and under its saved rows too; the widgets under the slots are pushed straight down within their own columns
  // (`relocate: false`, one anchor per member, M2 M6); every other widget keeps its saved rect (no page compaction,
  // M1 M3 M9 R6). Saved heights stay (an `autoHeight: false` height too). The preview is
  // fitPageLayout of exactly the widgets saved, so the release shows what the drag showed (M11).
  const moveFlow = (moved) => {
    const ids = [...moved.keys()]
    const shown = displayLayoutMap()
    const headers = topHeaderIds().filter((id) => !ids.includes(id))
    // Under the header as shown and as saved (its saved rows count when the page is normalised, M8).
    const headerRects = headers.flatMap((key) => [shown[key], widgets.find((item) => item.id === key)?.layout])
    const slots = new Map(ids.map((id) => [id, clearOfHeaders(moved.get(id), headerRects, canvas)]))
    // HUNT round 3 (a): a move never shrinks another widget (Artyom: a resize «двигает верхний»; live, Top holdings up
    // one row cut Top P&L 208 → 180). As summer's moveLayoutWithGuides/resolveCollision, a slot that reaches up into
    // a widget that starts above it, and above where the moved widget was, stops under that widget; the whole group
    // stops together. Anything else the slot lands on (a widget below, met moving down) is pushed down as before.
    for (let round = 0; round < 20; round += 1) {
      let down = 0
      slots.forEach((slot, id) =>
        widgets.forEach((item) => {
          const rect = shown[item.id]
          // Where the user put it (before the slot went under the header) and where it was: both below the top.
          const target = moved.get(id).y
          const from = shown[id]?.y ?? slot.y
          if (ids.includes(item.id) || headers.includes(item.id) || !rect || !(rect.y < target - 0.5) || !(rect.y < from - 0.5) || !rectanglesIntersect(rect, slot)) return
          down = Math.max(down, rowLineAfter(rect.y + rect.h, grid) - slot.y)
        }),
      )
      if (down <= 0.5) break
      slots.forEach((slot, id) => slots.set(id, { ...slot, y: slot.y + down }))
    }
    const base = { ...shown, ...Object.fromEntries(slots) }
    const pushed = pushLayoutForDrag(base, ids.length === 1 ? ids[0] : ids, layoutBounds([...slots.values()]), canvas, { relocate: false, fixed: headers })
    const saved = Object.fromEntries(widgets.map((item) => [item.id, item.layout]))
    const raw = new Map((activeTab.widgets || []).map((item) => [item.id, item]))
    const settle = (shift) => {
      const settled = Object.fromEntries(widgets.map((item) => [
        item.id,
        slots.has(item.id)
          ? { ...slots.get(item.id), y: slots.get(item.id).y + (shift[item.id] || 0) }
          : pushed[item.id] && base[item.id] && pushed[item.id].y !== base[item.id].y ? pushed[item.id] : item.layout,
      ]))
      // Review (§40, 4): the stop above runs on the shown rects, but persistFlowLayout cuts any widget above an anchor
      // whose *saved* height reaches into it (a content-fitted table shown 1 row, saved 9). So the slots also go under
      // the saved rows of every widget that starts above them, together; the fit closes those hidden rows on screen
      // (alignShown), and no other widget is ever cut.
      for (let round = 0; round < 20; round += 1) {
        let down = 0
        ids.forEach((id) => {
          const slot = settled[id]
          if (!slot) return
          widgets.forEach((item) => {
            const rect = settled[item.id]
            if (ids.includes(item.id) || !rect || !(rect.y < slot.y - 0.5) || !rectanglesIntersect(rect, slot)) return
            down = Math.max(down, rowLineAfter(rect.y + rect.h, grid) - slot.y)
          })
        })
        if (down <= 0.5) break
        ids.forEach((id) => settled[id] && (settled[id] = { ...settled[id], y: settled[id].y + down }))
      }
      const persisted = persistFlowLayout(settled, saved, canvas, { anchor: ids })
      // A widget the move leaves alone keeps exactly what is saved (an agent's off-grid rect too: M8, the header's 100px).
      const next = widgets.map((item) =>
        persisted[item.id] && !sameRect(persisted[item.id], item.layout) ? { ...item, layout: { ...item.layout, ...persisted[item.id] } } : raw.get(item.id) || item,
      )
      return { next, display: fitPageLayout(normalizeLayout(next, canvas), canvas, { needs: contentNeeds, stops: contentStops, squeeze }) }
    }
    const { next, display } = alignShown(ids, (id) => slots.get(id).y, settle)
    return { next, layouts: Object.fromEntries(display.map((item) => [item.id, item.layout])) }
  }
  // Saved and shown rows can differ: the display fit closes the rows a content-fitted table hides (M10: Bottom P&L shown
  // 5 rows, saved 9) and shows an agent's short page-top header as the taller band (R11: everything under it one row
  // lower than saved). So the saved row of a moved or resized widget is shifted, whole rows at a time, until it shows
  // where the user put it (the closest one when it cannot). `settle(shift)` gives { next, display } for the shifts.
  const alignShown = (ids, target, settle) => {
    const shift = {}
    let best = settle(shift)
    const miss = (result) => {
      const at = new Map(result.display.map((item) => [item.id, item.layout]))
      return ids.map((id) => (at.get(id) ? Math.round((target(id) - at.get(id).y) / grid.rowPitch) : 0))
    }
    const total = (values) => values.reduce((sum, value) => sum + Math.abs(value), 0)
    let off = miss(best)
    let bestOff = total(off)
    // A shift can land on a row the fit still closes (a widget's hidden saved rows), so a step that does not help yet
    // is kept going a few times; one that makes it worse stops.
    for (let round = 0; round < 8 && bestOff; round += 1) {
      ids.forEach((id, index) => (shift[id] = (shift[id] || 0) + off[index] * grid.rowPitch))
      const tried = settle(shift)
      const left = miss(tried)
      if (total(left) > total(off)) break
      if (total(left) < bestOff) {
        best = tried
        bestOff = total(left)
      }
      off = left
    }
    return best
  }
  // Summer push preview (Artyom 14cdf9f55): widgets under the ghost slot(s) move down, live, shown as the page will be
  // after the release (moveFlow); the ghost is where the widget lands.
  const previewFlow = (moved) => {
    const ids = [...moved.keys()]
    const { layouts } = moveFlow(moved)
    setFlowPreview({ ids, ghosts: ids.map((id) => layouts[id] || moved.get(id)), layouts, fit: true })
  }
  // One commit per drop or arrow move: one save, one undo step, exactly the page the preview showed (moveFlow).
  const commitFlow = (moved) => {
    const ids = [...moved.keys()]
    const shown = displayLayoutMap()
    // A widget whose snapped slot is the one it was shown in moved nothing: no save (V2 DESIGN-LOG §22).
    if (ids.every((id) => shown[id] && sameRect(moved.get(id), shown[id]))) return
    const { next } = moveFlow(moved)
    if (next.every((item, index) => item === (activeTab.widgets || [])[index])) return
    updateWidgets(next)
  }
  // §36 A grid resize (summer resizeLayoutWithGuides + handleWidgetResize): only the dragged edges move. The widgets
  // under it are pushed straight down (never to another column), nothing above or beside it moves, and a height drag
  // marks the widget `autoHeight: false` so the display fit keeps its height and its row. The preview is the page as
  // it shows after the save (fitPageLayout of the very widgets saved), so the release reflows nothing. No compaction.
  const resizeLayoutFor = (widget, raw, direction) => {
    const min = widgetMinCells(widget.type, widget)
    return gridResizeLayout(widget.layout, raw, direction, canvas, { c: Math.min(grid.cols, min.c), r: Math.min(grid.rows, min.r) },
      displayWidgets.filter((item) => item.id !== widget.id))
  }
  const resizeFlow = (widget, rect, direction) => {
    const vertical = /top|bottom/i.test(direction)
    const shown = { ...displayLayoutMap(), [widget.id]: rect }
    const pushed = pushLayoutForDrag(shown, widget.id, rect, canvas, { relocate: false })
    const saved = Object.fromEntries(widgets.map((item) => [item.id, item.layout]))
    // HUNT R2: a width-only drag changes only x and w; the saved top and height stay (the shown height is the display
    // fit's, and saving it would cap the content fit).
    const own = saved[widget.id] || rect
    const raw = new Map((activeTab.widgets || []).map((item) => [item.id, item]))
    const settle = (shift) => {
      const mine = vertical ? { ...rect, y: rect.y + (shift[widget.id] || 0) } : { ...own, x: rect.x, w: rect.w }
      // Only the resized widget and the ones it pushed change; every other widget keeps its saved rect.
      const settled = Object.fromEntries(widgets.map((item) => [
        item.id,
        item.id === widget.id ? mine : pushed[item.id] && shown[item.id] && pushed[item.id].y !== shown[item.id].y ? pushed[item.id] : item.layout,
      ]))
      const persisted = persistFlowLayout(settled, saved, canvas, { resized: vertical ? [widget.id] : [], anchor: vertical ? [widget.id] : [] })
      // Review (§40, 5): as moveFlow (M8), a widget the resize leaves alone keeps exactly what is saved (an agent's
      // off-grid rect too), and the preview normalises what is saved, as the page shows it after the save.
      const next = widgets.map((item) => {
        const layout = persisted[item.id]
        const sized = vertical && item.id === widget.id && item.layout.autoHeight !== false
        if (!layout || (sameRect(layout, item.layout) && !sized)) return raw.get(item.id) || item
        return { ...item, layout: { ...item.layout, ...layout, ...(sized ? { autoHeight: false } : {}) } }
      })
      return { next, display: fitPageLayout(normalizeLayout(next, canvas), canvas, { needs: contentNeeds, stops: contentStops, squeeze }) }
    }
    // HUNT R11: a height drag shows the widget where it was dragged, even where saved and shown rows differ.
    const { next, display } = vertical ? alignShown([widget.id], () => rect.y, settle) : settle({})
    return { next, layouts: Object.fromEntries(display.map((item) => [item.id, item.layout])) }
  }
  // Returns the rect the release would save for the widget (HUNT R3: the size readout shows that, not the shown rect).
  const previewResize = (widget, rect, direction) => {
    const flow = resizeFlow(widget, rect, direction)
    const saved = flow.next.find((item) => item.id === widget.id)?.layout || rect
    setFlowPreview({ ids: [widget.id], ghosts: [rect], layouts: flow.layouts, resize: true, saved })
    return saved
  }
  const growResizeBounds = (grow) => {
    if (!resizeBounds) return
    resizeBounds.style.height = `${grow ? Math.max(canvas.height, overflowBottom) + canvas.height : canvas.height}px`
  }
  // Group members other than the grabbed widget follow by an imperative transform; put them back.
  const resetMemberTransforms = (state) =>
    state.widgets.forEach((item) => {
      if (item.id === state.activeId) return
      const element = widgetRefs.current.get(item.id)?.resizableElement?.current
      if (element) element.style.transform = `translate(${item.layout.x}px, ${item.layout.y}px)`
    })
  // Esc during a grid drag (summer useCanvasInteractions): everything returns, nothing is saved.
  // The next pointer move ends react-draggable's drag (onDrag returns false), then onDragStop.
  const cancelGridDrag = () => {
    const state = dragStateRef.current
    if (!state || state.cancelled) return
    state.cancelled = true
    setFlowPreview(null)
    hideGuides()
    resetMemberTransforms(state)
    const active = state.widgets.find((item) => item.id === state.activeId)
    if (active) widgetRefs.current.get(active.id)?.updatePosition(active.layout)
  }
  const previewGroupDrag = (widget, data) => {
    const state = dragStateRef.current
    // No armed drag (it was dropped, HUNT M16): react-draggable's drag ends here and the widget never moves.
    if (!state) return false
    if (state.cancelled) return false
    // HUNT M12: react-draggable's position comes from its state, which React may not have flushed when two moves
    // land in one task, so the second move would start again from the first. The drag adds up the moves itself
    // (lastX + deltaX is his raw position; when his state is current the sum is exactly data.x).
    const offset = { x: data.x - data.lastX - data.deltaX, y: data.y - data.lastY - data.deltaY }
    state.track = state.track || { x: data.lastX, y: data.lastY }
    state.track = { x: state.track.x + data.deltaX, y: state.track.y + data.deltaY }
    data = { ...data, x: state.track.x + offset.x, y: state.track.y + offset.y }
    const step = snapEnabled ? 4 : 1
    const others = displayWidgets.filter((item) => !state.ids.includes(item.id))
    const proposed = gridMode
      ? { ...widget.layout, ...snapGridPosition(data, widget.layout, grid) }
      : { ...widget.layout, x: snapValue(data.x, step), y: snapValue(data.y, step) }
    // On the grid, guides only mark exact alignments so they never pull a widget off the grid.
    const guides = alignmentGuides(proposed, others, gridMode ? 1 : 4)
    const requested = { x: guides.x - widget.layout.x, y: guides.y - widget.layout.y }
    const delta = boundedGroupMove(state.widgets, requested, {
      width: config.canvas?.width || 1123,
      // HUNT M4: on the grid a drag may go past the page bottom (under the veil), as far as the grown bounds layer.
      height: gridMode && props.studio
        ? Math.max(canvas.height, overflowBottom) + canvas.height
        : Math.max(config.canvas?.height || 794, canvasAutoHeight(config.canvas?.height || 794, displayWidgets)),
    })
    const layouts = state.widgets.map((item) => ({
      ...item.layout,
      x: item.layout.x + delta.x,
      y: item.layout.y + delta.y,
    }))
    // On the grid nothing is blocked: the ghost takes the slot and the widgets under it yield.
    const invalid = !gridMode && layouts.some((layout) => placementCollides(layout, displayWidgets, state.ids))
    state.delta = delta
    state.invalid = invalid
    state.widgets.forEach((item, index) => {
      if (item.id === state.activeId) return
      const element = widgetRefs.current.get(item.id)?.resizableElement?.current
      if (element) element.style.transform = `translate(${layouts[index].x}px, ${layouts[index].y}px)`
    })
    if (gridMode) {
      // Re-render only when the slot changes cell, not on every pointer move.
      const key = `${delta.x},${delta.y}`
      if (key !== state.key) {
        state.key = key
        if (delta.x || delta.y) previewFlow(new Map(state.widgets.map((item, index) => [item.id, layouts[index]])))
        else setFlowPreview(null)
      }
    } else setCollisionPreview(state.ids, invalid)
    if (verticalGuideRef.current) {
      verticalGuideRef.current.style.display = guides.vertical == null ? "none" : "block"
      verticalGuideRef.current.style.left = `${guides.vertical || 0}px`
    }
    if (horizontalGuideRef.current) {
      horizontalGuideRef.current.style.display = guides.horizontal == null ? "none" : "block"
      horizontalGuideRef.current.style.top = `${guides.horizontal || 0}px`
    }
  }
  // A drag whose end never came (its widget left the page mid-drag, or the release was lost): everything returns,
  // nothing is saved, and Esc and the keys work again (HUNT M16).
  const dropStaleDrag = () => {
    const state = dragStateRef.current
    if (!state) return
    dragStateRef.current = null
    pageRef.current?.classList.remove("datox-react-dashboard__page--interacting")
    if (gridMode) growResizeBounds(false)
    setCollisionPreview(state.ids, false)
    hideGuides()
    setFlowPreview(null)
    resetMemberTransforms(state)
    const active = state.widgets.find((item) => item.id === state.activeId)
    if (active) widgetRefs.current.get(active.id)?.updatePosition(active.layout)
  }
  const dropStaleDragRef = useRef(dropStaleDrag)
  dropStaleDragRef.current = dropStaleDrag
  const finishGroupDrag = () => {
    pageRef.current?.classList.remove("datox-react-dashboard__page--interacting")
    if (gridMode) growResizeBounds(false)
    const state = dragStateRef.current
    if (!state) return
    setCollisionPreview(state.ids, false)
    hideGuides()
    dragStateRef.current = null
    if (gridMode) {
      setFlowPreview(null)
      // A click, a drag back to the start, or Esc: no save.
      if (state.cancelled || (!state.delta.x && !state.delta.y)) return resetMemberTransforms(state)
      settleRef.current = state.ids
      commitFlow(new Map(state.widgets.map((item) => [item.id, { ...item.layout, x: item.layout.x + state.delta.x, y: item.layout.y + state.delta.y }])))
      return
    }
    if (state.invalid) {
      state.widgets.forEach((item) => {
        const instance = widgetRefs.current.get(item.id)
        if (item.id === state.activeId) instance?.updatePosition(item.layout)
        const element = instance?.resizableElement?.current
        if (element) element.style.transform = `translate(${item.layout.x}px, ${item.layout.y}px)`
      })
      return
    }
    updateWidgets(
      widgets.map((widget) =>
        state.ids.includes(widget.id)
          ? {
              ...widget,
              // The dragged widget lands where it was shown (display fit), moved by the drag.
              layout: {
                ...(displayById.get(widget.id) || widget).layout,
                x: (displayById.get(widget.id) || widget).layout.x + state.delta.x,
                y: (displayById.get(widget.id) || widget).layout.y + state.delta.y,
              },
            }
          : widget,
      ),
    )
  }
  const startMarquee = (event) => {
    if (event.button !== 0 || event.target !== event.currentTarget) return
    const rect = pageRef.current?.getBoundingClientRect()
    if (!rect) return
    const start = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
      setSelectedWidgetIds([])
      setActiveWidgetId(null)
    }
    const move = (pointerEvent) => {
      const current = { x: pointerEvent.clientX - rect.left, y: pointerEvent.clientY - rect.top }
      const marquee = {
        x: Math.max(0, Math.min(start.x, current.x)),
        y: Math.max(0, Math.min(start.y, current.y)),
        w: Math.abs(current.x - start.x),
        h: Math.abs(current.y - start.y),
      }
      if (marqueeRef.current) {
        Object.assign(marqueeRef.current.style, {
          display: "block",
          left: `${marquee.x}px`,
          top: `${marquee.y}px`,
          width: `${marquee.w}px`,
          height: `${marquee.h}px`,
        })
      }
    }
    const finish = (pointerEvent) => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", finish)
      const current = { x: pointerEvent.clientX - rect.left, y: pointerEvent.clientY - rect.top }
      const ids = marqueeSelection(
        {
          x: Math.min(start.x, current.x),
          y: Math.min(start.y, current.y),
          w: Math.abs(current.x - start.x),
          h: Math.abs(current.y - start.y),
        },
        widgets,
      )
      if (marqueeRef.current) marqueeRef.current.style.display = "none"
      setSelectedWidgetIds((selected) =>
        event.shiftKey || event.ctrlKey || event.metaKey ? [...new Set([...selected, ...ids])] : ids,
      )
      setActiveWidgetId(ids.at(-1) || null)
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", finish)
  }
  // A library element over a report page (V2 DESIGN-LOG §31): the slot it lands in (dropSlotLayout) as a ghost, and the
  // widgets under it pushed out of the way, live, with the same summer push a moved widget uses (pushLayoutForDrag).
  // §32: fit first, then push anyway (planGridDrop). The preview shows the layout the drop saves; the widgets that
  // still pass the page bottom are faded with a "→ next page" chip. Preview only: nothing is written until the drop.
  const dropPagePoint = (pointer) => {
    const rect = pageRef.current?.getBoundingClientRect()
    return rect ? { x: (pointer.clientX - rect.left) / scale, y: (pointer.clientY - rect.top) / scale } : null
  }
  // HUNT P7: the slot never covers the page-top header; it goes under its band (the ghost and the drop agree).
  const dropSlot = (type, point) => {
    const shown = displayLayoutMap()
    return clearOfHeaders(dropSlotLayout(type, point, canvas), topHeaderIds().flatMap((id) => [shown[id], widgets.find((item) => item.id === id)?.layout]), canvas)
  }
  const dropPlan = (id, slot) =>
    planGridDrop(
      displayLayoutMap(),
      Object.fromEntries(widgets.map((item) => [item.id, item.layout])),
      contentFitLayouts(widgets, canvas, contentNeeds, contentStops).layouts,
      id,
      slot,
      canvas,
      { fixed: topHeaderIds() },
    )
  const dragOverWorkspace = (event) => {
    event.preventDefault()
    if (!gridMode || locked || previewing || !draggingWidgetType) return
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    // The ghost slot and the live push while dragging are the V2 look; his classic editor drops into the same slot
    // without them.
    if (!v2) return
    dropPointerRef.current = { clientX: event.clientX, clientY: event.clientY }
    if (dropFrameRef.current) return
    dropFrameRef.current = requestAnimationFrame(() => {
      dropFrameRef.current = 0
      const point = dropPointerRef.current && dropPagePoint(dropPointerRef.current)
      if (!point) return
      const type = draggingWidgetType
      const slot = dropSlot(type, point)
      if (flowPreview?.drop?.type === type && sameRect(flowPreview.ghosts[0], slot)) return
      const plan = dropPlan(DROP_GHOST_ID, slot)
      // Shown: the widgets the drop moves (pushed, compacted or sent on) at the rects it saves; the rest stay as displayed.
      const shown = displayLayoutMap()
      const layouts = Object.fromEntries(
        Object.keys(shown)
          .filter((id) => plan.overflow.includes(id) || !sameRect(plan.settled[id], shown[id]))
          .map((id) => [id, plan.layouts[id]]),
      )
      setFlowPreview({ ids: [DROP_GHOST_ID], ghosts: [slot], layouts, drop: { type, overflow: plan.overflow } })
    })
  }
  const clearDropPreview = () => {
    if (dropFrameRef.current) cancelAnimationFrame(dropFrameRef.current)
    dropFrameRef.current = 0
    dropPointerRef.current = null
    setFlowPreview((current) => (current?.drop ? null : current))
  }
  const dragLeaveWorkspace = (event) => {
    // Moving onto a child of the workspace is not leaving it.
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return
    clearDropPreview()
  }
  // The drop: the new widget in the ghost's slot and the widgets it pushed, then summer compaction around it, as a moved
  // widget's drop (commitFlow). §32: on the content-fit heights when the saved ones leave no room (those fitted heights
  // are saved with it). §33 (summer): what still passes the page bottom stays there, in view under the veil; nothing is
  // moved for the user. One save, one undo step; the V2 editor says "N widgets don't fit on this page · Move to next page".
  const dropOnGrid = (type, slot) => {
    const widget = newWidget(type, slot)
    const plan = dropPlan(widget.id, slot)
    const placed = (item) =>
      plan.layouts[item.id] && !sameRect(plan.layouts[item.id], item.layout) ? { ...item, layout: { ...item.layout, ...plan.layouts[item.id] } } : item
    const added = { ...widget, layout: { ...widget.layout, ...plan.layouts[widget.id] } }
    const staying = widgets.map(placed)
    settleRef.current = staying.map((item) => item.id)
    insertWidget(added, "data", staying)
    // What the drop sent past the bottom: below the edge as displayed now (the display fit keeps on the page what it
    // can) and on the page as displayed before.
    const before = new Set(displayWidgets.filter((item) => reportWidgetOffPage(item.layout, canvas)).map((item) => item.id))
    const ids = fitPageLayout([...staying, added], canvas, { needs: contentNeeds, stops: contentStops, squeeze })
      .filter((item) => !before.has(item.id) && reportWidgetOffPage(item.layout, canvas))
      .map((item) => item.id)
    if (ids.length) props.studio?.onDropped?.({ name: widgetLabel(labels, type) || type, outside: ids.length, moveToNextPage: () => moveToNextPageRef.current(ids) })
  }
  // "Move to next page" (§33, the bar of a widget below the page edge, or the drop toast): the widgets at the top of the
  // next page in their order and relative layout, under a page-top header, that page's own widgets moved down
  // (moveToPageTop); with no next page, a new one after this one, named as Add page names it. One save, one undo step.
  const moveToNextPage = (ids) => {
    if (locked || !gridMode) return
    const going = widgets.filter((item) => ids.includes(item.id))
    if (!going.length) return
    const tabs = config.tabs || [activeTab]
    const index = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab.id))
    const target = tabs[index + 1] || { id: widgetIdValue(), name: nextTabName(tabs), widgets: [] }
    const moved = moveToPageTop(Object.fromEntries(going.map((item) => [item.id, item.layout])), target.widgets || [], canvas)
    const nextPage = {
      ...target,
      widgets: [
        ...going.map((item) => ({ ...item, layout: { ...item.layout, ...moved.moved[item.id] } })),
        ...(target.widgets || []).map((item) => (moved.others[item.id] ? { ...item, layout: { ...item.layout, ...moved.others[item.id] } } : item)),
      ],
    }
    const nextTabs = tabs.map((tab) => (tab.id === activeTab.id ? { ...tab, widgets: widgets.filter((item) => !ids.includes(item.id)) } : tab.id === target.id ? nextPage : tab))
    updateTabs(tabs[index + 1] ? nextTabs : [...nextTabs.slice(0, index + 1), nextPage, ...nextTabs.slice(index + 1)])
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
    setEditorWidgetId(null)
  }
  // The drop toast's action runs after the save re-rendered the page: always the current page's widgets.
  const moveToNextPageRef = useRef(moveToNextPage)
  moveToNextPageRef.current = moveToNextPage
  const dropWidget = (event) => {
    event.preventDefault()
    clearDropPreview()
    // Dropping a new element from the library is a deliberate add, like a click: the Drag switch only guards moving
    // widgets already on the page, so it doesn't block this.
    if (locked) return
    const type = event.dataTransfer.getData("application/x-datox-widget") || draggingWidgetType
    if (!type) return
    if (gridMode) {
      // The same slot the ghost showed: one helper, the same pointer.
      const point = dropPagePoint(event)
      setDraggingWidgetType("")
      if (point) dropOnGrid(type, dropSlot(type, point))
      return
    }
    const rect = pageRef.current?.getBoundingClientRect()
    if (!rect) return
    const canvasWidth = config.canvas?.width || 1123
    addWidget(type, {
      x: Math.max(0, Math.min(canvasWidth - 140, Math.round((event.clientX - rect.left) / scale - 120))),
      y: props.studio
        ? Math.max(0, Math.min(canvas.height - 80, Math.round((event.clientY - rect.top) / scale - 40)))
        : Math.max(0, Math.round(event.clientY - rect.top - 40)),
    })
    setDraggingWidgetType("")
  }

  const submitCreate = (event) => {
    event.preventDefault()
    const name = newName.trim()
    if (!name) return
    setCreateOpen(false)
    setNewName("")
    props.onCreateDashboard?.(name)
  }

  const selectPage = (id) => { setActiveTabId(id); setActiveWidgetId(null); setEditorWidgetId(null); setInlineEditWidgetId(null) }
  const selectWidget = (widget, event) => {
    pickedRef.current = null
    // A plain click outside a multi-selection leaves it (the V2 editor holds plain presses back from pickWidget).
    // It makes the clicked widget the selection, so Delete, the arrows, ⌘D and ⌘C act on it (HUNT P1).
    if (event && !event.shiftKey && !event.ctrlKey && !event.metaKey) setSelectedWidgetIds((ids) => (ids.includes(widget.id) ? ids : [widget.id]))
    setActiveWidgetId(widget.id)
    if (props.studio && !previewing) { setEditorWidgetId(widget.id); props.studio.onPanel("properties") }
  }
  // A plain press focuses the widget before its click (the V2 hold keeps it from pickWidget): it becomes the selection
  // there and then, so a widget selected before never shows as a second one under the pointer (the "2 widgets" bar
  // would take the release and the click). Keyboard focus selects the same way (HUNT P1).
  const focusWidget = (widget) => {
    const picked = pickedRef.current === widget.id
    pickedRef.current = null
    if (!picked) setSelectedWidgetIds((ids) => (ids.includes(widget.id) ? ids : [widget.id]))
    setActiveWidgetId(widget.id)
  }
  const closeWidgetEditor = () => { setEditorWidgetId(null); props.studio?.onPanel("") }
  // `widgetIDs` (Bobur's dashboard assistant sends them as selected_widget_ids) next to our single widget.
  const selection = () => ({ pageID: activeTab.id, pageName: activeTab.name, widgetID: selectedWidget?.id, widgetIDs: selectedWidgetIds.length ? selectedWidgetIds : selectedWidget ? [selectedWidget.id] : [], title: selectedWidget?.title, datasetID: selectedWidget?.data?.dataset_id })
  const movePage = (offset) => {
    const tabs = [...config.tabs]
    const index = tabs.findIndex((tab) => tab.id === activeTab.id)
    if (locked || !tabs[index + offset]) return
    ;[tabs[index], tabs[index + offset]] = [tabs[index + offset], tabs[index]]
    updateTabs(tabs)
  }
  const addTemplate = (layout) => {
    if (locked) return
    // The template's own grid layout (reportTemplateLayout: the panel's previews draw the same one).
    // Bound by table name (summer dataset_ref, backlog #23): a slot whose ref names one of this client's tables is
    // connected to it (its first columns, as a hand-added widget) and keeps the ref (the names, not this client's
    // "Hamad_70714 — top_holdings"), so another client's copy of the page finds its own table. A slot with no match
    // comes in unconnected, as before.
    let kpis = 0
    const items = reportTemplateLayout(layout, canvas).map((item, index) => {
      const table = item.ref ? resolveDatasetRef(item.ref, props.outputTables) : undefined
      const created = createWidget(item.type, index, table, labels)
      // A row of KPIs on one table shows its first numeric columns in turn, not the same one three times.
      const numeric = item.type === "kpi" && table ? (table.headers || []).filter((header) => table.rows?.some((row) => numericCell(row[header]) !== null)) : []
      const metric = numeric.length ? numeric[kpis++ % numeric.length] : undefined
      const bound = table
        ? { ...created, data: { ...(created.data || {}), ...(metric ? { metric } : {}), dataset_id: table.datasetID, dataset_ref: item.ref } }
        : created
      const widget = { ...(table ? autoTitled(bound) : bound), layout: item.layout }
      return item.type === "header" ? { ...widget, title: config.title, style: { logoUrl: config.design?.logoUrl } } : widget
    })
    const tab = { id: widgetIdValue(), name: nextTabName(), widgets: items }
    // HUNT P11: the template page goes right after the page you are on, not last.
    const at = Math.max(0, config.tabs.findIndex((item) => item.id === activeTab.id)) + 1
    updateTabs([...config.tabs.slice(0, at), tab, ...config.tabs.slice(at)])
    selectPage(tab.id)
  }
  const setLogo = (logoUrl) => updateConfig({ ...config, design: { ...config.design, logoUrl }, tabs: config.tabs.map((tab) => ({ ...tab, widgets: tab.widgets.map((widget) => widget.type === "header" ? { ...widget, style: { ...widget.style, logoUrl } } : widget) })) })
  const uploadLogo = async (file) => {
    if (!file || locked) return
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 2 * 1024 * 1024) { setLocalError(labels.designer.logoError); return }
    try {
      const data = await new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file) })
      setLogo(data)
      setLocalError("")
    } catch { setLocalError(labels.designer.logoError) }
  }
  useEffect(() => {
    if (!props.studio || !workspaceRef.current) return
    const observer = new ResizeObserver(([entry]) => setViewport({ width: entry.contentRect.width, height: entry.contentRect.height }))
    observer.observe(workspaceRef.current)
    return () => observer.disconnect()
  }, [!!props.studio])
  useEffect(() => { props.studio?.onSelection?.(selection()) }, [activeTab.id, activeTab.name, selectedWidgetIds.join(","), selectedWidget?.id, selectedWidget?.title, selectedWidget?.data?.dataset_id])
  useEffect(() => {
    const command = props.studio?.command
    if (!command || command.dashboardID !== props.selectedDashboardID || commandRef.current === command.id) return
    commandRef.current = command.id
    if (command.type === "export") openExport()
    props.studio?.onCommandHandled?.(command.id)
  }, [props.studio?.command, props.selectedDashboardID])
  useEffect(() => {
    if (!props.autoExport) return
    openExport()
    props.onAutoExportHandled?.()
  }, [props.autoExport])
  const livePageNote = paper
    ? pageNote(displayWidgets.filter(offPage).length, displayWidgets.filter((widget) => !offPage(widget)).reduce((sum, widget) => sum + continuedRows(widget), 0))
    : null
  // One page of the stacked Preview: the page as it prints (display fit, nothing off the page), read only.
  const paperPage = (tab) => {
    const fitted = printPageLayout(tab.widgets, canvas, { needs: contentNeeds, stops: contentStops })
    const shown = fitted.filter((widget) => !reportWidgetOffPage(widget.layout, canvas))
    const note = pageNote(fitted.length - shown.length, shown.reduce((sum, widget) => sum + continuedRows(widget), 0))
    const filters = shown.filter((widget) => widget.type === "filter" && widget.data?.selectedValues?.length)
    const design = config.design || {}
    const noop = () => {}
    return <React.Fragment key={tab.id}><div className="datox-react-dashboard__page-wrap datox-react-dashboard__page-wrap--paper" data-page-id={tab.id} style={{ width: canvas.width * scale, minWidth: canvas.width * scale, height: canvas.height * scale }}>
      <div className={`datox-react-dashboard__page datox-react-dashboard__page--flow datox-react-dashboard__page--paper ${design.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`} data-sheet-style={sheetStyle(design)} role="img" aria-label={tab.name}
        style={{ "--ds-accent": design.accent || "#2f80ed", "--ds-header-color": design.headerColor || "#101828", "--ds-heading-font": designFont(design.headingFont), "--ds-body-font": designFont(design.bodyFont), width: canvas.width, minHeight: canvas.height, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <DashboardFitContext.Provider value={fitContext}>{shown.map((widget) => (
          <div key={widget.id} style={{ position: "absolute", left: widget.layout.x, top: widget.layout.y, width: widget.layout.w, height: dashboardWidgetHeight(widget) }}>
            <WidgetFrame preview labels={labels} widget={widget} filterWidgets={filters} table={applyDashboardFilters(widgetOutputTable(props.outputTables, widget), widget, filters)}
              accent={design.accent || "#2f80ed"} design={design} selected={false} menuOpen={false} onToggleMenu={noop} onRename={noop} onEditData={noop} onEditDesign={noop} onExportTable={noop} onFullView={noop} onFilterValueToggle={noop} onDelete={noop} onDuplicate={noop} />
          </div>
        ))}</DashboardFitContext.Provider>
        {continuesLines(shown)}
      </div>
    </div>
    {note ? <p className="datox-react-dashboard__page-note" data-testid="page-outside-note" data-page-id={tab.id}>{note}</p> : null}
    </React.Fragment>
  }
  // §75: the Export PDF dialog shows each page as it prints, small (V2; Artyom: «превью страниц бы показывать»): the
  // same read-only render as the Preview stack, at the thumbnail's width, its labels hidden from screen readers.
  // Review #13: each page's render waits in PageThumb until it nears the list's view.
  const pageThumb = (tab, width = 136) => {
    const k = width / canvas.width
    const fitted = printPageLayout(tab.widgets, canvas, { needs: contentNeeds, stops: contentStops })
    const shown = fitted.filter((widget) => !reportWidgetOffPage(widget.layout, canvas))
    const filters = shown.filter((widget) => widget.type === "filter" && widget.data?.selectedValues?.length)
    const design = config.design || {}
    const noop = () => {}
    return (
      <PageThumb width={width} height={Math.round(canvas.height * k)}>
        <div className={`datox-react-dashboard__page datox-react-dashboard__page--flow datox-react-dashboard__page--paper ${design.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`} data-sheet-style={sheetStyle(design)}
          style={{ "--ds-accent": design.accent || "#2f80ed", "--ds-header-color": design.headerColor || "#101828", "--ds-heading-font": designFont(design.headingFont), "--ds-body-font": designFont(design.bodyFont), width: canvas.width, minHeight: canvas.height, transform: `scale(${k})`, transformOrigin: "top left" }}>
          <DashboardFitContext.Provider value={fitContext}>{shown.map((widget) => (
            <div key={widget.id} style={{ position: "absolute", left: widget.layout.x, top: widget.layout.y, width: widget.layout.w, height: dashboardWidgetHeight(widget) }}>
              <WidgetFrame preview labels={labels} widget={widget} filterWidgets={filters} table={applyDashboardFilters(widgetOutputTable(props.outputTables, widget), widget, filters)}
                accent={design.accent || "#2f80ed"} design={design} selected={false} menuOpen={false} onToggleMenu={noop} onRename={noop} onEditData={noop} onEditDesign={noop} onExportTable={noop} onFullView={noop} onFilterValueToggle={noop} onDelete={noop} onDuplicate={noop} />
            </div>
          ))}</DashboardFitContext.Provider>
        </div>
      </PageThumb>
    )
  }
  // Review #13: the thumbnails built once per page and what they show, not per render. A page picked or cleared in the
  // dialog re-renders this whole canvas; the same elements come back, so React skips every thumbnail's widgets.
  const pageThumbs = useMemo(
    () => (v2 && exportSelection ? new Map(config.tabs.map((tab) => [tab.id, pageThumb(tab)])) : null),
    [v2, !!exportSelection, config.tabs, config.design, canvas.width, canvas.height, contentNeeds, contentStops, props.outputTables, labels, fitContext],
  )
  // In Preview the page picked in the tabs (or the one being edited) is scrolled to the top of the stack.
  useEffect(() => {
    if (!stacked) return
    workspaceRef.current?.querySelector(`[data-page-id="${activeTab.id}"]`)?.scrollIntoView?.({ block: "start" })
  }, [stacked, activeTab.id])
  const selectAllWidgets = () => {
    const ids = widgets.map((widget) => widget.id)
    setSelectedWidgetIds(ids)
    setActiveWidgetId(ids.at(-1) || null)
    if (ids.length !== 1) setEditorWidgetId(null)
  }
  const copySelectedWidgets = () => {
    if (locked || !selectedWidgetIds.length) return
    clipboardRef.current = widgets.filter((widget) => selectedWidgetIds.includes(widget.id)).map(cloneDashboardConfig)
    clipboardSourceRef.current = { dashboardID: props.selectedDashboardID ?? null, pasted: 0 }
    setClipboardCount(clipboardRef.current.length)
  }
  // A paste lands like a duplicate on our model: on a report page each copy takes the first free grid slot from
  // one step below and right of its original (below the page under the veil when the page is full); never on top.
  const pasteWidgets = () => {
    if (locked || !clipboardRef.current.length) return
    if (clipboardSourceRef.current.dashboardID !== (props.selectedDashboardID ?? null)) return
    clipboardSourceRef.current.pasted += 1
    const step = 28 * clipboardSourceRef.current.pasted
    const placed = []
    clipboardRef.current.forEach((source) => {
      const copy = { ...cloneDashboardConfig(source), id: widgetIdValue() }
      const start = { ...copy.layout, x: copy.layout.x + step, y: copy.layout.y + step }
      const others = [...widgets, ...placed]
      copy.layout = gridMode
        ? { ...copy.layout, ...(freeGridSlot(snapGridLayout(start, grid, widgetMinCells(copy.type, copy)), others, grid, copy) || placeLayout(start, others, copy)) }
        : placeLayout(start, others, copy)
      placed.push(copy)
    })
    updateWidgets([...widgets, ...placed])
    setSelectedWidgetIds(placed.map((widget) => widget.id))
    setActiveWidgetId(placed.at(-1)?.id || null)
    if (placed.length !== 1) setEditorWidgetId(null)
  }
  // A copy from another dashboard is dropped when the dashboard changes.
  useEffect(() => {
    if (clipboardSourceRef.current.dashboardID === (props.selectedDashboardID ?? null)) return
    clipboardRef.current = []
    clipboardSourceRef.current = { dashboardID: null, pasted: 0 }
    setClipboardCount(0)
  }, [props.selectedDashboardID])

  const pdfHtml = useMemo(() => {
    if (!serverSnapshot || !exportSelection?.length || !pdfSettings) return ""
    const tabs = serverSnapshot.config.tabs.filter((tab) => exportSelection.includes(tab.id))
    const tables = serverSnapshot.datasets.map((dataset) => ({ datasetID: dataset.id, name: dataset.name, headers: dataset.columns.map((column) => typeof column === "string" ? column : column.name), rows: dataset.rows }))
    return serializeDashboardPdf({ config: { ...serverSnapshot.config, tabs }, tables, activeTabID: tabs[0]?.id, settings: { ...pdfSettings, scope: "all" }, dashboardTitle: labels.dashboardTitle, scopeLabel: serverSnapshot.scopeLabel, draftLabel: serverSnapshot.draft ? labels.exportDraft : undefined })
  }, [serverSnapshot, exportSelection, pdfSettings, labels.dashboardTitle, labels.exportDraft])
  const pdfWidgets = serverSnapshot
    ? serverSnapshot.config.tabs.filter((tab) => exportSelection?.includes(tab.id)).flatMap((tab) => tab.widgets.map((widget) => ({ tab, widget, key: JSON.stringify([tab.id, widget.id]) })))
    : []
  const pdfWidget = pdfWidgets.find((item) => item.key === pdfWidgetID) || pdfWidgets[0]
  const pdfWidgetSettings = pdfSettings?.widgets?.[pdfWidget?.tab.id]?.[pdfWidget?.widget.id] || {}
  const setPdfWidgetSettings = (tabID, widgetID, patch) =>
    setPdfSettings((current) => ({ ...current, widgets: { ...current.widgets, [tabID]: { ...current.widgets[tabID], [widgetID]: patch === null ? {} : { ...current.widgets[tabID]?.[widgetID], ...patch } } } }))
  // Bobur's preview: drag a widget to move it, its bottom-right corner to resize it (PDF-only layout tweaks).
  const bindPdfPreview = () => {
    const doc = pdfPreviewRef.current?.contentDocument
    if (!doc || doc.body.dataset.layoutEditor) return
    doc.body.dataset.layoutEditor = "true"
    doc.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return
      const widget = event.target.closest(".widget[data-widget-id]")
      const area = widget?.parentElement
      if (!widget || !area) return
      event.preventDefault()
      const rect = widget.getBoundingClientRect()
      const bounds = area.getBoundingClientRect()
      const resizing = event.clientX >= rect.right - 14 && event.clientY >= rect.bottom - 14
      const start = { x: event.clientX, y: event.clientY, left: rect.left - bounds.left, top: rect.top - bounds.top, width: rect.width, height: rect.height }
      const move = (next) => {
        const width = resizing ? Math.max(80, Math.min(bounds.width - start.left, start.width + next.clientX - start.x)) : start.width
        const height = resizing ? Math.max(48, Math.min(bounds.height - start.top, start.height + next.clientY - start.y)) : start.height
        Object.assign(widget.style, {
          left: `${resizing ? start.left : Math.max(0, Math.min(bounds.width - width, start.left + next.clientX - start.x))}px`,
          top: `${resizing ? start.top : Math.max(0, Math.min(bounds.height - height, start.top + next.clientY - start.y))}px`,
          width: `${width}px`, height: `${height}px`,
        })
      }
      const stop = () => {
        doc.removeEventListener("pointermove", move)
        doc.removeEventListener("pointerup", stop)
        doc.removeEventListener("pointercancel", stop)
        const position = widget.getBoundingClientRect()
        const page = serverSnapshot?.config.canvas || { width: 1123, height: 794 }
        const layout = { x: Math.round((position.left - bounds.left) / bounds.width * page.width), y: Math.round((position.top - bounds.top) / bounds.height * page.height), w: Math.round(position.width / bounds.width * page.width), h: Math.round(position.height / bounds.height * page.height) }
        setPdfWidgetSettings(widget.dataset.tabId, widget.dataset.widgetId, { layout })
        setPdfWidgetID(JSON.stringify([widget.dataset.tabId, widget.dataset.widgetId]))
      }
      doc.addEventListener("pointermove", move)
      doc.addEventListener("pointerup", stop)
      doc.addEventListener("pointercancel", stop)
    })
  }

  const studioEditor = {
    locked, readOnly: props.readOnly, preview: !!props.studio?.preview, saving: props.saving, page: activeTab, pages: config.tabs,
    hasFilters: (config.tabs || []).some((tab) => (tab.widgets || []).some((widget) => widget.type === "filter")),
    selected: selectedWidget, canUndo: undoStackRef.current.length > 0, canRedo: redoStackRef.current.length > 0, undo, redo,
    // Bobur's chrome (37f2a2b): select all, copy and paste, the selection count, and his "Snap" name for our Grid.
    selectedCount: selectedWidgetIds.length || (selectedWidget ? 1 : 0), selectAll: selectAllWidgets,
    copyWidgets: copySelectedWidgets, pasteWidgets, canPaste: clipboardCount > 0,
    snapEnabled, setSnapEnabled,
    renamePage: (name) => updateActiveTab({ name }), renamePageDialog: openRenameTab,
    editWidget: (mode) => selectedWidget && openWidgetEditor(selectedWidget, mode),
    duplicateWidget: () => selectedWidget && duplicateWidget(selectedWidget.id), deleteWidget: () => selectedWidget && deleteWidget(selectedWidget.id),
    exportTable: () => selectedWidget && exportWidgetTable(selectedWidget), fullView: () => selectedWidget && setFullViewWidgetId(selectedWidget.id),
    tidy: tidyLayout, dragEnabled, setDrag: setDragEnabled, grid: snapEnabled, setGrid: setSnapEnabled, orientation: setOrientation, portrait: config.canvas?.orientation === "portrait", renameDashboard: openRenameDashboard,
    exportPdf: openExport,
    published: !!selectedDashboard?.published_config || selectedDashboard?.status === "published",
    publish: () => selectedDashboard?.published_config || selectedDashboard?.status === "published" ? props.onUnpublishDashboard?.() : publishDashboard(),
    share: () => setShareAccessOpen(true), selectPage, addPage: addTab, movePage,
    duplicatePage: (page = activeTab) => duplicateTab(page), deletePage: (page = activeTab) => deleteTab(page.id), setZoom, scale,
  }

  // HUNT M12: a press, its moves and its release can land in one task (a quick flick, Chrome automation). React has
  // then not flushed react-draggable's `dragging` (set on the press), so the moves and the release were dropped and
  // his drag stayed armed for the next pointer move (the DESIGN-LOG §22 family). Before each move or release of a
  // drag that has started, the pending press update is flushed. This only acts on a drag his mousedown already
  // started: the V2 press hold still keeps plain clicks and scripted presses from ever starting one.
  useEffect(() => {
    const flush = () => { if (dragStateRef.current || resizingRef.current) flushSync(() => {}) }
    let pending = 0
    const release = () => {
      flush()
      resizingRef.current = false
      const armed = dragStateRef.current
      if (!armed) return
      // Any release ends the drag; if his drag-stop never came, the drag is dropped after this event.
      clearTimeout(pending)
      pending = window.setTimeout(() => { if (dragStateRef.current === armed) dropStaleDragRef.current() })
    }
    document.addEventListener("mousemove", flush, true)
    document.addEventListener("mouseup", release, true)
    return () => {
      clearTimeout(pending)
      document.removeEventListener("mousemove", flush, true)
      document.removeEventListener("mouseup", release, true)
    }
  }, [])
  // The dragged widget left the page mid-drag (an undo, an agent write, a page switch): the drag is dropped (HUNT M16).
  useEffect(() => {
    const state = dragStateRef.current
    if (state && !widgets.some((widget) => widget.id === state.activeId)) dropStaleDrag()
  }, [activeTab.id, widgets])

  useEffect(() => {
    // One keyboard handler for both modes (main's multi-select shortcuts + Bobur's studio keys).
    // It only reacts to keys aimed at this canvas (or the page body), never at other app panels or dialogs.
    const keydown = (event) => {
      // While a widget is dragged or resized only Esc counts (it cancels a grid drag); Delete or an arrow mid-gesture
      // would act on the page under the pointer (HUNT M15).
      if (dragStateRef.current || resizingRef.current) {
        if (event.key === "Escape" && gridMode && dragStateRef.current) {
          event.preventDefault()
          cancelGridDrag()
        }
        return
      }
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      )
        return
      if (target instanceof Element && target !== document.body && !rootRef.current?.contains(target)) return
      // ⌘Z / ⇧⌘Z / ⌘Y also work with the focus on a button (Tidy, a library tile, a page tab), HUNT P3.
      const history = (event.ctrlKey || event.metaKey) && !event.altKey && ["z", "y"].includes(event.key.toLowerCase())
      if (target instanceof Element && target.closest(history ? "[contenteditable=true], [role=dialog]" : "button, [contenteditable=true], [role=dialog]")) return
      if (event.key === "Escape") {
        setWidgetMenuId(null)
        setActiveWidgetId(null)
        setSelectedWidgetIds([])
        setEditorWidgetId(null)
        return
      }
      if (locked) return
      const modifier = event.ctrlKey || event.metaKey
      if (event.key === "Enter" && selectedWidget) {
        event.preventDefault()
        openWidgetEditor(selectedWidget, "data")
        return
      }
      if (modifier && event.key.toLowerCase() === "a") {
        event.preventDefault()
        selectAllWidgets()
        return
      }
      // ⌘C copies widgets only when no text is selected on the page (Bobur: a text copy stays a text copy).
      if (modifier && event.key.toLowerCase() === "c" && selectedWidgetIds.length && window.getSelection?.()?.isCollapsed !== false) {
        event.preventDefault()
        copySelectedWidgets()
        return
      }
      if (modifier && event.key.toLowerCase() === "d" && selectedWidgetIds.length) {
        event.preventDefault()
        duplicateSelectedWidgets()
        return
      }
      if (modifier && event.key.toLowerCase() === "v" && clipboardRef.current.length) {
        event.preventDefault()
        pasteWidgets()
        return
      }
      if (modifier && event.key.toLowerCase() === "z") {
        event.preventDefault()
        if (event.shiftKey) redo()
        else undo()
        return
      }
      if (modifier && event.key.toLowerCase() === "y") {
        event.preventDefault()
        redo()
        return
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selectedWidgetIds.length) {
        event.preventDefault()
        reportDeleted("widget", widgets.filter((widget) => selectedWidgetIds.includes(widget.id)).map((widget) => widget.title || widgetLabel(labels, widget.type)))
        updateWidgets(withoutWidgets(selectedWidgetIds))
        setSelectedWidgetIds([])
        setActiveWidgetId(null)
        setEditorWidgetId(null)
        return
      }
      if (!selectedWidgetIds.length || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return
      event.preventDefault()
      // On the grid an arrow moves one cell (Shift: four cells).
      const cells = event.shiftKey ? 4 : 1
      const amount = event.shiftKey ? 10 : snapEnabled ? 4 : 1
      const requested = {
        x: (event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0) * (gridMode ? cells * grid.colPitch : amount),
        y: (event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0) * (gridMode ? cells * grid.rowPitch : amount),
      }
      const selected = (gridMode ? displayWidgets : widgets).filter((widget) => selectedWidgetIds.includes(widget.id))
      const delta = boundedGroupMove(selected, requested, {
        width: config.canvas?.width || 1123,
        height: config.canvas?.height || 794,
      })
      const moved = selected.map((widget) => {
        const position = { x: widget.layout.x + delta.x, y: widget.layout.y + delta.y }
        return { ...widget.layout, ...(gridMode ? snapGridPosition(position, widget.layout, grid) : position) }
      })
      // On the grid one arrow step pushes like a drag (and is one undo step).
      if (gridMode) {
        if (!delta.x && !delta.y) return
        commitFlow(new Map(selected.map((widget, index) => [widget.id, moved[index]])))
        return
      }
      if (moved.some((layout) => placementCollides(layout, widgets, selectedWidgetIds))) return
      const movedByID = new Map(selected.map((widget, index) => [widget.id, moved[index]]))
      updateWidgets(
        widgets.map((widget) => (movedByID.has(widget.id) ? { ...widget, layout: movedByID.get(widget.id) } : widget)),
      )
    }
    window.addEventListener("keydown", keydown)
    return () => window.removeEventListener("keydown", keydown)
  }, [config.canvas?.height, config.canvas?.width, selectedWidgetIds, snapEnabled, gridMode, widgets, displayWidgets, locked, selectedWidget])

  return (
      <div ref={rootRef} className={`datox-react-dashboard${props.studio ? " report-unified-editor" : ""}${exportingPdf ? " datox-react-dashboard--exporting" : ""}${exportMenuOpen || dashboardMenuOpen ? " datox-react-dashboard--menu-open" : ""}`} data-panel={props.studio?.panel} data-preview={!!props.studio?.preview} onClick={closeMenusOnOutsideClick}>
        {props.studio ? <ReportCanvasToolbar labels={labels} editor={studioEditor} /> : <div className="datox-react-dashboard__topbar">
          <div className="datox-react-dashboard__topbar-left">
            <h2 className="datox-react-dashboard__title">{labels.dashboardTitle}</h2>
            <select className="datox-react-dashboard__select" disabled={props.scoped} value={props.selectedDashboardID || ""} onChange={(event) => props.onSelectDashboard?.(event.currentTarget.value)}>
              {(props.dashboards || []).map((dashboard) => <option key={dashboard.id} value={dashboard.id}>{dashboard.name}</option>)}
            </select>
            {!props.scoped ? <DashboardCreateButton store={props.saveStatusStore} label={labels.createDashboard} onClick={() => setCreateOpen(true)} /> : null}
          </div>
          <div className="datox-react-dashboard__topbar-right">
            <div className="datox-react-dashboard__toolbar-group datox-react-dashboard__toolbar-group--plain">
              <span className={selectedDashboard?.published_config || selectedDashboard?.status === "published" ? "datox-react-dashboard__publish-status datox-react-dashboard__publish-status--published" : "datox-react-dashboard__publish-status"}>{selectedDashboard?.published_config || selectedDashboard?.status === "published" ? labels.publishedStatus : labels.unpublishedStatus}</span>
              <button className="datox-react-dashboard__toolbar-button" type="button" title={labels.preview} onClick={() => setPreviewOpen(true)}>▶</button>
            </div>
            <div className="datox-react-dashboard__toolbar-group">
              <button className={`datox-react-dashboard__toolbar-button ${config.canvas?.orientation === "portrait" ? "datox-react-dashboard__toolbar-button--active" : ""}`} type="button" title={labels.portrait} onClick={() => setOrientation("portrait")}>▯</button>
              <button className={`datox-react-dashboard__toolbar-button ${config.canvas?.orientation !== "portrait" ? "datox-react-dashboard__toolbar-button--active" : ""}`} type="button" title={labels.landscape} onClick={() => setOrientation("landscape")}>▭</button>
            </div>
            <div className="datox-react-dashboard__toolbar-group datox-react-dashboard__toolbar-group--continuation" aria-hidden={historyVersion < 0}>
              <button className="datox-react-dashboard__toolbar-button" type="button" title={labels.undo} onClick={undo} disabled={undoStackRef.current.length === 0}>↶</button>
              <button className="datox-react-dashboard__toolbar-button" type="button" title={labels.redo} onClick={redo} disabled={redoStackRef.current.length === 0}>↷</button>
            </div>
            <label className="datox-react-dashboard__drag-toggle"><span>{labels.drag}</span><input type="checkbox" checked={dragEnabled} onChange={(event) => setDragEnabled(event.currentTarget.checked)} /></label>
            <label className="datox-react-dashboard__drag-toggle"><span>{labels.grid}</span><input type="checkbox" checked={snapEnabled} onChange={(event) => setSnapEnabled(event.currentTarget.checked)} /></label>
            <div className="datox-react-dashboard__menu-wrap">
              <button className="datox-react-dashboard__toolbar-button" type="button" title={labels.exportMenu} aria-label={labels.exportMenu} onClick={() => setExportMenuOpen(!exportMenuOpen)}><TbFileExport size={18} /></button>
              {exportMenuOpen ? (
                <div className="datox-react-dashboard__menu">
                  <button type="button" disabled={props.saving || exportingPdf} onClick={() => { setExportMenuOpen(false); openExport() }}>{labels.exportPdf}</button>
                  <button type="button" disabled={!(selectedDashboard?.published_config || selectedDashboard?.status === "published")} onClick={() => { setExportMenuOpen(false); setShareAccessOpen(true) }}>{labels.shareAccess}</button>
                </div>
              ) : null}
            </div>
            <div className="datox-react-dashboard__menu-wrap">
              <button className="datox-react-dashboard__toolbar-button" type="button" title={labels.more} onClick={() => setDashboardMenuOpen(!dashboardMenuOpen)}>...</button>
              {dashboardMenuOpen ? (
                <div className="datox-react-dashboard__menu">
                  <button type="button" onClick={() => { setDashboardMenuOpen(false); setPreviewOpen(true) }}>{labels.preview}</button>
                  {selectedDashboard?.published_config || selectedDashboard?.status === "published" ? <button type="button" onClick={() => { setDashboardMenuOpen(false); props.onUnpublishDashboard?.() }}>{labels.unpublishDashboard}</button> : <button type="button" onClick={() => { setDashboardMenuOpen(false); publishDashboard() }}>{labels.publishDashboard}</button>}
                  <button type="button" onClick={() => { setDashboardMenuOpen(false); openRenameDashboard() }}>{labels.renameDashboard}</button>
                  {!props.scoped ? <button type="button" className="datox-react-dashboard__menu-danger" onClick={() => { setDashboardMenuOpen(false); setDeleteDashboardOpen(true) }}>{labels.deleteDashboard}</button> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        }
        {props.error || localError ? <div className="datox-react-dashboard__error" role="alert">{props.error || localError}</div> : null}
        {!props.studio ? <div className="datox-react-dashboard__tabs">
          {(config.tabs || []).map((tab) => <div key={tab.id} className={`datox-react-dashboard__tab ${tab.id === activeTab.id ? "datox-react-dashboard__tab--active" : ""}`}><button className="datox-react-dashboard__tab-name" type="button" onClick={() => { setActiveTabId(tab.id); setActiveWidgetId(null); setSelectedWidgetIds([]) }} onDoubleClick={() => openRenameTab(tab)} title={labels.doubleClickRename}>{tab.name || labels.defaultTabName}</button><button className="datox-react-dashboard__tab-menu-button" type="button" onClick={() => setTabMenuId(tabMenuId === tab.id ? null : tab.id)}>⌄</button>{tabMenuId === tab.id ? <div className="datox-react-dashboard__tab-menu"><button type="button" onClick={() => { setTabMenuId(null); openRenameTab(tab) }}>{labels.rename}</button><button type="button" onClick={() => duplicateTab(tab)}>{labels.duplicateTab}</button><button type="button" className="datox-react-dashboard__tab-menu-danger" disabled={(config.tabs || []).length <= 1} onClick={() => deleteTab(tab.id)}>{labels.deleteTab}</button></div> : null}</div>)}
          <button className="datox-react-dashboard__tab" type="button" onClick={addTab}>+</button>
        </div> : null}
        <div className={`datox-react-dashboard__body ${!leftPanelOpen ? "datox-react-dashboard__body--left-collapsed" : ""} ${editorWidget ? "datox-react-dashboard__body--editor" : ""}`}>
          {props.studio?.panel === "templates" && !previewing ? <ReportTemplatePanel labels={labels} canvas={canvas} tables={props.outputTables} disabled={locked} onAdd={addTemplate} onClose={() => props.studio.onPanel("")} /> : null}
          {(props.studio ? !previewing && ["elements", "data", "brand"].includes(props.studio.panel) : leftPanelOpen) ? <aside className="datox-react-dashboard__sidebar">
            <div className="datox-react-dashboard__panel-header"><h3>{labels.panel}</h3><button className="datox-react-dashboard__panel-close" type="button" onClick={() => props.studio ? props.studio.onPanel("") : setLeftPanelOpen(false)} title={labels.closePanel} aria-label={labels.closePanel}>×</button></div>
            <div className="datox-react-dashboard__segmented datox-react-dashboard__segmented--three" role="tablist" aria-label={labels.panel} onKeyDown={(event) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return
              event.preventDefault()
              const panels = ["widgets", "data", "design"]
              const index = event.key === "Home" ? 0 : event.key === "End" ? 2 : (panels.indexOf(panel) + (event.key === "ArrowRight" ? 1 : 2)) % 3
              setPanel(panels[index])
              event.currentTarget.querySelectorAll('[role="tab"]')[index]?.focus()
            }}>
              <button className={`datox-react-dashboard__segment ${panel === "widgets" ? "datox-react-dashboard__segment--active" : ""}`} type="button" role="tab" tabIndex={panel === "widgets" ? 0 : -1} aria-selected={panel === "widgets"} onClick={() => setPanel("widgets")}>{labels.widgetsPanel}</button>
              <button className={`datox-react-dashboard__segment ${panel === "data" ? "datox-react-dashboard__segment--active" : ""}`} type="button" role="tab" tabIndex={panel === "data" ? 0 : -1} aria-selected={panel === "data"} onClick={() => setPanel("data")}>{labels.data}</button>
              <button className={`datox-react-dashboard__segment ${panel === "design" ? "datox-react-dashboard__segment--active" : ""}`} type="button" role="tab" tabIndex={panel === "design" ? 0 : -1} aria-selected={panel === "design"} onClick={() => setPanel("design")}>{labels.design}</button>
            </div>
            {panel === "widgets" ? (
              <>
                <div className="datox-react-dashboard__search-row">
                  <div className="datox-react-dashboard__search-wrap">
                    <span>/</span>
                    <input
                      className="datox-react-dashboard__input"
                      placeholder={labels.designer.searchElements || labels.searchWidgets}
                      value={search}
                      onChange={(event) => setSearch(event.currentTarget.value)}
                    />
                  </div>
                  <div className="datox-react-dashboard__view-toggle">
                    <button
                      className={widgetLibraryView === "list" ? "datox-react-dashboard__view-toggle-active" : ""}
                      type="button"
                      onClick={() => setWidgetLibraryView("list")}
                      title={labels.listView}
                    >
                      =
                    </button>
                    <button
                      className={widgetLibraryView === "grid" ? "datox-react-dashboard__view-toggle-active" : ""}
                      type="button"
                      onClick={() => setWidgetLibraryView("grid")}
                      title={labels.gridView}
                    >
                      ::
                    </button>
                  </div>
                </div>
                <div className="datox-react-dashboard__section-label">{labels.designer.elementsAdd || labels.widgetLibrary}</div>
                <div
                  className={
                    widgetLibraryView === "grid"
                      ? "datox-react-dashboard__palette datox-react-dashboard__palette--grid"
                      : "datox-react-dashboard__palette"
                  }
                >
                  {paletteItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <button key={item.type} className="datox-react-dashboard__palette-item" type="button" draggable={!locked && (Boolean(props.studio) || !(item.data && !selectedTable))} onDragStart={(event) => { setDraggingWidgetType(item.type); event.dataTransfer.effectAllowed = "copy"; event.dataTransfer.setData("application/x-datox-widget", item.type) }} onDragEnd={() => { clearDropPreview(); setDraggingWidgetType("") }} onClick={() => addWidget(item.type)} disabled={locked || (!props.studio && item.data && !selectedTable)}>
                        <Icon className="datox-react-dashboard__palette-icon" />
                        {widgetLabel(labels, item.type)}
                      </button>
                    )
                  })}
                </div>
              </>
            ) : panel === "data" ? (
              <div className="datox-react-dashboard__data-panel">
                {props.studio ? <button type="button" className="designer-secondary" onClick={() => props.studio.onPanel("uploads")}>{labels.designer.workflowFiles}</button> : null}
                <label>
                  <div className="datox-react-dashboard__field-label">{labels.outputTable}</div>
                  <select
                    className="datox-react-dashboard__select"
                    style={{ width: "100%", minWidth: 0 }}
                    value={props.selectedDatasetID || ""}
                    onChange={(event) => props.onSelectDataset?.(event.currentTarget.value)}
                  >
                    {(props.datasets || []).map((dataset) => (
                      <option key={dataset.id} value={dataset.id}>
                        {dataset.name}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="datox-react-dashboard__metadata-card" aria-label={labels.tableMetadata}>
                  <div className="datox-react-dashboard__metadata-tile">
                    <span>{labels.rows}</span>
                    <strong>{selectedDataset?.row_count ?? selectedTable?.rows?.length ?? 0}</strong>
                  </div>
                  <div className="datox-react-dashboard__metadata-tile">
                    <span>{labels.columns}</span>
                    <strong>{selectedDataset?.columns?.length ?? selectedTable?.headers?.length ?? 0}</strong>
                  </div>
                </div>
                <div className="datox-react-dashboard__section-label">{labels.columnMetadata}</div>
                <div className="datox-react-dashboard__column-metadata-list">
                  {(
                    selectedDataset?.columns ||
                    selectedTable?.headers?.map((name) => ({ name, type: "string" })) ||
                    []
                  ).map((column) => (
                    <div key={column.name} className="datox-react-dashboard__column-metadata-row">
                      <span>{column.name}</span>
                      <small>{column.type}</small>
                    </div>
                  ))}
                </div>
                <div className="datox-react-dashboard__preview-section">
                  <div className="datox-react-dashboard__section-label">{labels.tablePreview}</div>
                  <div className="datox-react-dashboard__preview-wrap">
                    <TableWidget table={selectedTable} limit={8} />
                  </div>
                </div>
              </div>
            ) : (
              <fieldset className="datox-react-dashboard__design-panel" disabled={locked}>
                {/* §75: the sheet's style, one look for every widget (V2 only; the flag-off app keeps his look). */}
                {v2 ? <SegmentedControl label={labels.sheetStyle} value={sheetStyle(config.design)} options={SHEET_STYLES.map((value) => ({ value, label: labels.sheetStyles?.[value] || value }))} onChange={(value) => updateConfig({ ...config, design: { ...(config.design || {}), sheetStyle: value } })} /> : null}
                <div><div className="datox-react-dashboard__field-label">{labels.logo}</div><input ref={logoInputRef} hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => { void uploadLogo(event.currentTarget.files?.[0]); event.currentTarget.value = "" }} /><button className="datox-react-dashboard__upload-box" type="button" onClick={() => logoInputRef.current?.click()}>{labels.uploadLogo}</button><small>{labels.designer.logoHint}</small>{config.design?.logoUrl ? <button type="button" onClick={() => setLogo(undefined)}>{labels.designer.removeLogo}</button> : null}</div>
                <ColorSwatches label={labels.accentColor} value={config.design?.accent || "#2f80ed"} onChange={(accent) => updateConfig({ ...config, design: { ...(config.design || {}), accent } })} colors={["#2f80ed", "#2f54eb", "#7048e8", "#40a9ff", "#339989", "#2f855a", "#d6334c", "#e8590c", "#1d4ed8", "#475467"]} />
                <ColorSwatches label={labels.headerColor} value={config.design?.headerColor || "#101828"} onChange={(headerColor) => updateConfig({ ...config, design: { ...(config.design || {}), headerColor } })} colors={["#101828", "#1f2937", "#1e3a8a", "#1d4ed8", "#475467", "#ffffff", "#111827"]} />
                <SegmentedControl label={labels.cornerRadius} value={config.design?.radius || "medium"} options={[{ value: "square", label: labels.radiusSquare }, { value: "medium", label: labels.radiusMedium }, { value: "large", label: labels.radiusLarge }]} onChange={(radius) => updateConfig({ ...config, design: { ...(config.design || {}), radius } })} />
                <SegmentedControl label={labels.fontSize} value={config.design?.fontSize || "normal"} options={[{ value: "compact", label: labels.fontCompact }, { value: "normal", label: labels.fontNormal }, { value: "large", label: labels.fontLarge }]} onChange={(fontSize) => updateConfig({ ...config, design: { ...(config.design || {}), fontSize } })} />
                <label><div className="datox-react-dashboard__field-label">{labels.headingFont}</div><select className="datox-react-dashboard__select" style={{ width: "100%", minWidth: 0 }} value={config.design?.headingFont || "System"} onChange={(event) => updateConfig({ ...config, design: { ...(config.design || {}), headingFont: event.currentTarget.value } })}>{["System", "Roboto", "Inter", "Serif", "Arial", "Georgia", "Verdana"].map((font) => <option key={font} value={font}>{font}</option>)}</select></label>
                <label><div className="datox-react-dashboard__field-label">{labels.bodyFont}</div><select className="datox-react-dashboard__select" style={{ width: "100%", minWidth: 0 }} value={config.design?.bodyFont || "System"} onChange={(event) => updateConfig({ ...config, design: { ...(config.design || {}), bodyFont: event.currentTarget.value } })}>{["System", "Roboto", "Inter", "Serif", "Arial", "Georgia", "Verdana"].map((font) => <option key={font} value={font}>{font}</option>)}</select></label>
                <ToggleRow label={labels.widgetShadows} checked={Boolean(config.design?.widgetShadows)} onChange={(widgetShadows) => updateConfig({ ...config, design: { ...(config.design || {}), widgetShadows } })} />
                <ToggleRow label={labels.lightGrayBackground} checked={Boolean(config.design?.lightGrayBackground)} onChange={(lightGrayBackground) => updateConfig({ ...config, design: { ...(config.design || {}), lightGrayBackground } })} />
              </fieldset>
            )}
          </aside> : null}
          <main ref={workspaceRef} className={`datox-react-dashboard__workspace ${draggingWidgetType ? "datox-react-dashboard__workspace--dragging" : ""}`} onDragOver={dragOverWorkspace} onDragLeave={dragLeaveWorkspace} onDrop={dropWidget}>
            {!props.studio && !leftPanelOpen ? <button className="datox-react-dashboard__panel-open" type="button" onClick={() => setLeftPanelOpen(true)} title={labels.openPanel}>☰</button> : null}
            {stacked ? (config.tabs || []).slice(0, activeIndex).map(paperPage) : null}
            <div className="datox-react-dashboard__page-wrap" data-page-id={activeTab.id} style={{ width: props.studio ? canvas.width * scale : canvas.width + 56, minWidth: props.studio ? canvas.width * scale : canvas.width + 56, height: props.studio ? (overflowing ? overflowBottom + grid.margin : canvas.height) * scale : undefined }}>
              <div
                ref={pageRef}
                className={`datox-react-dashboard__page ${gridMode ? "datox-react-dashboard__page--flow" : ""} ${config.design?.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""} ${draggingWidgetType ? "datox-react-dashboard__page--drop-target" : ""}`}
                data-sheet-style={sheetStyle(config.design)}
                onMouseDown={(event) => { if (event.target === event.currentTarget) { setActiveWidgetId(null); setEditorWidgetId(null) } }}
                onPointerDown={startMarquee}
                style={{
                  "--ds-accent": config.design?.accent || "#2f80ed",
                  "--ds-header-color": config.design?.headerColor || "#101828",
                  "--ds-heading-font": designFont(config.design?.headingFont),
                  "--ds-body-font": designFont(config.design?.bodyFont),
                  width: canvas.width,
                  minHeight: props.studio ? canvas.height : dashboardKpiPageHeight(config.canvas?.height, widgets),
                  transform: props.studio ? `scale(${scale})` : undefined,
                  transformOrigin: "top left",
                }}
              >
                {showGridGuides && !previewing ? (
                  // The 24-column grid as one faint layer over the whole page: continuous columns from
                  // margin to margin, top to bottom, under the widgets. (The old 12px-row cells read as
                  // a stray dashed band in the one free row under the content.)
                  <div
                    aria-hidden="true"
                    className="datox-react-dashboard__grid-guides"
                    style={{
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      zIndex: 0,
                      backgroundImage: `repeating-linear-gradient(to right, rgba(47, 128, 237, 0.045) 0 ${grid.colPitch - grid.gutter}px, transparent ${grid.colPitch - grid.gutter}px ${grid.colPitch}px)`,
                      backgroundPosition: `${grid.margin}px 0`,
                      backgroundSize: `${canvas.width - grid.margin * 2 + grid.gutter}px 100%`,
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                ) : null}
                {props.studio && gridMode ? (
                  // §36 The resize bound (see resizeBounds): the page at rest, taller while a widget is resized.
                  <div ref={setResizeBounds} aria-hidden="true" data-testid="resize-bounds" style={{ position: "absolute", left: 0, top: 0, width: canvas.width, height: canvas.height, visibility: "hidden", pointerEvents: "none" }} />
                ) : null}
                {overflowing ? (
                  // Off-page veil (summer): a translucent grey over everything below the page, over the widgets too, so
                  // what won't print reads as grey at once but stays in view and can be picked; the dashed page edge on it.
                  <>
                    <div aria-hidden="true" data-testid="page-veil" className="datox-react-dashboard__page-veil" style={{ top: canvas.height, height: overflowBottom - canvas.height + grid.margin }} />
                    <div aria-hidden="true" className="datox-react-dashboard__page-edge" style={{ position: "absolute", left: 0, right: 0, top: canvas.height, borderTop: "1px dashed #98a2b3", pointerEvents: "none", zIndex: 8 }}>
                      {v2 && labels.designer.pageEdge ? <span className="datox-react-dashboard__page-edge-label" style={{ fontSize: 10 / scale }}>{labels.designer.pageEdge}</span> : null}
                    </div>
                  </>
                ) : null}
                {flowPreview?.ghosts.map((ghost, index) => (
                  // The drop slot (summer drag ghost): dashed accent outline on the grid cells. A library element's slot
                  // names what lands there on an opaque chip (§31, §32).
                  <div
                    key={`ghost-${flowPreview.ids[index]}`}
                    aria-hidden="true"
                    data-testid={`drop-ghost-${flowPreview.ids[index]}`}
                    data-moves={flowPreview.drop ? String(flowPreview.drop.overflow.length) : undefined}
                    className={`datox-react-dashboard__drop-ghost${flowPreview.drop ? " datox-react-dashboard__drop-ghost--new" : ""}`}
                    style={{ transform: `translate(${ghost.x}px, ${ghost.y}px)`, width: ghost.w, height: ghost.h }}
                  >
                    {flowPreview.drop ? (
                      <span className="datox-react-dashboard__drop-ghost-label" style={{ fontSize: 13 / scale }}>
                        {widgetLabel(labels, flowPreview.drop.type)}
                      </span>
                    ) : null}
                  </div>
                ))}
                {resizeSize ? (
                  // The live size while resizing: the snapped footprint in grid cells and in page px.
                  <div
                    aria-live="polite"
                    data-testid="resize-size"
                    className="datox-react-dashboard__size-badge"
                    style={{ position: "absolute", left: resizeSize.x + resizeSize.w, top: resizeSize.y + resizeSize.h, transform: `translate(-100%, ${8 / scale}px) scale(${1 / scale})`, transformOrigin: "top right", zIndex: 30, pointerEvents: "none" }}
                  >
                    {(() => {
                      const size = { ...resizeSize, ...(resizeSize.saved || {}) }
                      const cells = gridCells(size, grid)
                      return (labels.designer.resizeSize || "{{cols}} × {{rows}} · {{width}} × {{height}}")
                        .replace("{{cols}}", String(cells.cols))
                        .replace("{{rows}}", String(cells.rows))
                        .replace("{{width}}", String(Math.round(size.w)))
                        .replace("{{height}}", String(Math.round(size.h)))
                    })()}
                  </div>
                ) : null}
                <div ref={marqueeRef} className="datox-react-dashboard__marquee" />
                <div ref={verticalGuideRef} className="datox-react-dashboard__guide datox-react-dashboard__guide--vertical" />
                <div ref={horizontalGuideRef} className="datox-react-dashboard__guide datox-react-dashboard__guide--horizontal" />
                {!locked && selectedWidgetIds.length > 1 ? (
                  <div className="datox-react-dashboard__selection-toolbar">
                    <span>{labels.widgetCount.replace("{count}", String(selectedWidgetIds.length))}</span>
                    <button type="button" title={labels.duplicateWidget} onClick={duplicateSelectedWidgets}>
                      {labels.duplicateWidget}
                    </button>
                    <button type="button" title={labels.deleteWidget} onClick={deleteSelectedWidgets}>
                      {labels.deleteWidget}
                    </button>
                  </div>
                ) : null}
                {widgets.length === 0 ? <div className="datox-react-dashboard__empty"><div><strong>{labels.emptyCanvasTitle}</strong><br />{labels.emptyCanvasDescription}</div></div> : null}
                <DashboardFitContext.Provider value={fitContext}>{(paper ? displayWidgets.filter((widget) => !offPage(widget)) : displayWidgets).map((widget) => (
                  <Rnd
                    key={widget.id}
                    ref={(instance) => {
                      if (instance) widgetRefs.current.set(widget.id, instance)
                      else widgetRefs.current.delete(widget.id)
                    }}
                    bounds={props.studio && gridMode && resizeBounds ? resizeBounds : "parent"}
                    scale={scale}
                    // On the grid the widget follows the pointer and a ghost shows its snapped slot (summer);
                    // resizing moves freely and snaps to whole cells on release.
                    dragGrid={gridMode ? [1, 1] : [snapEnabled ? 4 : 1, snapEnabled ? 4 : 1]}
                    resizeGrid={gridMode ? [1, 1] : [snapEnabled ? 4 : 1, snapEnabled ? 4 : 1]}
                    disableDragging={locked || !dragEnabled || inlineEditWidgetId === widget.id}
                    // HUNT R4: the page-top header is always shown as the header band (fitPageLayout), so it has no handles.
                    enableResizing={!locked && (props.studio ? activeWidgetId === widget.id && !(gridMode && isPageTopHeader(widget, canvas)) : true)}
                    // Named zones, a screen-constant hit area (the V2 editor draws the visible handles over them).
                    resizeHandleClasses={props.studio ? RESIZE_HANDLE_CLASSES : undefined}
                    resizeHandleStyles={props.studio ? resizeHandleStyles(scale) : undefined}
                    dragHandleClassName={props.studio ? undefined : "datox-react-dashboard__drag-handle"}
                    cancel={props.studio
                      ? 'input, textarea, select, button, a, [contenteditable="true"], .datox-react-dashboard__widget-dropdown, .datox-react-dashboard__table-container'
                      : '.datox-react-dashboard__widget-menu, .datox-react-dashboard__widget-dropdown, .datox-react-dashboard__chart-wrap, .datox-react-dashboard__table-container, input, textarea, select, button, a, table, [contenteditable="true"]'}
                    // The display fit may show a widget below its type minimum (an overflowing page); Rnd
                    // must not stretch it back, so the minimum never exceeds the shown size.
                    minWidth={gridMode ? Math.min(gridMinimumSize(widget.type, grid, widget).minWidth, widget.layout.w) : 120}
                    minHeight={gridMode ? Math.min(gridMinimumSize(widget.type, grid, widget).minHeight, widget.layout.h) : widget.type === "kpi" && (widget.style?.target || widget.kpi_display?.target) ? 180 : 56}
                    // While a grid drag or resize is previewed, the other widgets sit where they yield to.
                    position={{ x: yielded(widget).x, y: yielded(widget).y }}
                    size={dropShown(widget) ? { width: dropShown(widget).w, height: dropShown(widget).h } : { width: widget.layout.w, height: dashboardWidgetHeight(widget) }}
                    className={flowPreview && !flowPreview.ids.includes(widget.id) ? "datox-react-dashboard__rnd--yield" : undefined}
                    onDragStart={() => {
                      pageRef.current?.classList.add("datox-react-dashboard__page--interacting")
                      // HUNT M4 / M5: as on resize (§36), the bounds grow by a page, so a widget past the bottom is not
                      // pulled onto the page on its first move and a drag can take one past the bottom.
                      if (gridMode) growResizeBounds(true)
                      const ids = selectedWidgetIds.includes(widget.id) ? selectedWidgetIds : [widget.id]
                      const selected = displayWidgets.filter((item) => ids.includes(item.id))
                      dragStateRef.current = {
                        activeId: widget.id,
                        ids,
                        widgets: selected,
                        delta: { x: 0, y: 0 },
                        invalid: false,
                        cancelled: false,
                        key: "0,0",
                      }
                    }}
                    onDrag={(event, data) => previewGroupDrag(widget, data)}
                    onDragStop={finishGroupDrag}
                    onResizeStart={() => {
                      resizingRef.current = true
                      pageRef.current?.classList.add("datox-react-dashboard__page--interacting")
                      if (gridMode) growResizeBounds(true)
                    }}
                    onResize={(event, direction, ref, delta, position) => {
                      const raw = { x: position.x, y: position.y, w: ref.offsetWidth, h: ref.offsetHeight }
                      const layout = gridMode ? resizeLayoutFor(widget, raw, direction) : raw
                      if (gridMode) {
                        // Growing pushes the widgets below down, live; shrinking brings them back (§36).
                        let saved = flowPreview?.saved
                        if (sameRect(layout, widget.layout)) {
                          setFlowPreview(null)
                          saved = widgets.find((item) => item.id === widget.id)?.layout
                        } else if (!flowPreview || !sameRect(flowPreview.ghosts[0], layout)) saved = previewResize(widget, layout, direction)
                        // HUNT R3: the readout counts the rows and px the release saves (a slack-extended edge is no row).
                        const size = { ...layout, saved: saved ? { w: saved.w, h: saved.h } : undefined }
                        if (!sameRect(resizeSize, layout) || resizeSize?.saved?.w !== size.saved?.w || resizeSize?.saved?.h !== size.saved?.h) setResizeSize(size)
                        return
                      }
                      resizeInvalidRef.current = placementCollides(layout, displayWidgets, [widget.id])
                      setCollisionPreview([widget.id], resizeInvalidRef.current)
                    }}
                    onResizeStop={(event, direction, ref, delta, position) => {
                      resizingRef.current = false
                      pageRef.current?.classList.remove("datox-react-dashboard__page--interacting")
                      if (gridMode) {
                        growResizeBounds(false)
                        setFlowPreview(null)
                        setResizeSize(null)
                        // A press and release without a move changes nothing (the shown rect may sit off the whole rows).
                        const layout = delta?.width || delta?.height
                          ? resizeLayoutFor(widget, { x: position.x, y: position.y, w: ref.offsetWidth, h: ref.offsetHeight }, direction)
                          : widget.layout
                        settleRef.current = [widget.id]
                        if (sameRect(layout, widget.layout)) {
                          widgetRefs.current.get(widget.id)?.updatePosition(widget.layout)
                          widgetRefs.current.get(widget.id)?.updateSize({ width: widget.layout.w, height: dashboardWidgetHeight(widget) })
                          return
                        }
                        // One save, one undo step: exactly the page the drag showed (§36).
                        updateWidgets(resizeFlow(widget, layout, direction).next)
                        return
                      }
                      setCollisionPreview([widget.id], false)
                      if (resizeInvalidRef.current) {
                        widgetRefs.current.get(widget.id)?.updatePosition(widget.layout)
                        widgetRefs.current.get(widget.id)?.updateSize({
                          width: widget.layout.w,
                          height: dashboardWidgetHeight(widget),
                        })
                        resizeInvalidRef.current = false
                        return
                      }
                      const raw = { x: position.x, y: position.y, w: ref.offsetWidth, h: ref.offsetHeight }
                      const layout = fitLayout(gridMode ? raw : snapLayout(raw, snapEnabled ? 4 : 1), widget)
                      // The grid snap can shrink or move the widget onto a neighbour; keep the old layout then.
                      if (gridMode && placementCollides(layout, displayWidgets, [widget.id])) {
                        widgetRefs.current.get(widget.id)?.updatePosition(widget.layout)
                        widgetRefs.current.get(widget.id)?.updateSize({ width: widget.layout.w, height: dashboardWidgetHeight(widget) })
                        return
                      }
                      updateWidget(widget.id, { layout })
                    }}
                    onMouseDown={(event) => pickWidget(widget.id, event)}
                    style={{
                      zIndex: selectedWidgetIds.includes(widget.id) || activeWidgetId === widget.id ? 5 : 1,
                    }}
                  >
                    <WidgetFrame
                      preview={previewing}
                      focusable={!!props.studio}
                      offPage={offPage(widget)}
                      onMoveToNextPage={v2 && offPage(widget) && !locked && !flowPreview ? () => { setWidgetMenuId(null); moveToNextPage([widget.id]) } : undefined}
                      onFocus={() => focusWidget(widget)}
                      onSelect={(event) => selectWidget(widget, event)}
                      labels={labels}
                      filterWidgets={activeFilterWidgets}
                      widget={widget}
                      draggable={dragEnabled && !locked}
                      table={applyDashboardFilters(
                        widgetOutputTable(props.outputTables, widget),
                        widget,
                        activeFilterWidgets,
                      )}
                      accent={config.design?.accent || "#2f80ed"}
                      design={config.design || {}}
                      comparison={comparisonState.itemsByWidgetId[widget.id]}
                      selected={!previewing && (selectedWidgetIds.includes(widget.id) || activeWidgetId === widget.id)}
                      menuOpen={widgetMenuId === widget.id}
                      inlineEditing={inlineEditWidgetId === widget.id && !locked}
                      onInlineEdit={() => { if (!locked) setInlineEditWidgetId(widget.id) }}
                      onInlineEditClose={() => setInlineEditWidgetId(null)}
                      onInlineTextChange={(value) =>
                        updateWidget(widget.id, widget.type === "title" ? { title: value } : { text: value }, {
                          coalesce: `inline:${widget.id}`,
                        })
                      }
                      onSortTable={
                        locked
                          ? undefined
                          : (sort) => {
                              const { sort: _previous, ...rest } = widget.data || {}
                              updateWidget(widget.id, { data: sort ? { ...rest, sort } : rest })
                            }
                      }
                      onToggleMenu={() => setWidgetMenuId(widgetMenuId === widget.id ? null : widget.id)}
                      onRename={() => {
                        setWidgetMenuId(null)
                        openRenameWidget(widget)
                      }}
                      onEditData={() => openWidgetEditor(widget, "data")}
                      onEditDesign={() => openWidgetEditor(widget, "design")}
                      onExplainSource={
                        props.onExplainSource && widget.data?.dataset_id
                          ? () => {
                              const request = {
                                dashboardID: props.selectedDashboardID,
                                dashboardName: config.title || labels.dashboardTitle,
                                tabID: activeTab.id,
                                tabName: activeTab.name,
                                widgetID: widget.id,
                                widgetType: widget.type,
                                widgetTitle: widget.title,
                                widgetData: widget.data || {},
                              }
                              setWidgetMenuId(null)
                              void loadSourceExplanation(request)
                            }
                          : undefined
                      }
                      onExportTable={() => {
                        setWidgetMenuId(null)
                        exportWidgetTable(widget)
                      }}
                      onFullView={() => {
                        setWidgetMenuId(null)
                        setFullViewWidgetId(widget.id)
                      }}
                      onFilterValueToggle={(value) => toggleFilterValue(widget, value)}
                      onDelete={() => {
                        setWidgetMenuId(null)
                        deleteWidget(widget.id)
                      }}
                      onDuplicate={() => {
                        setWidgetMenuId(null)
                        duplicateWidget(widget.id)
                      }}
                    />
                  </Rnd>
                ))}</DashboardFitContext.Provider>
                {paper ? continuesLines(displayWidgets.filter((widget) => !offPage(widget))) : null}
              </div>
            </div>
            {paper && livePageNote ? (
              <p className="datox-react-dashboard__page-note" data-testid="page-outside-note" data-page-id={activeTab.id}>{livePageNote}</p>
            ) : null}
            {stacked ? (config.tabs || []).slice(activeIndex + 1).map(paperPage) : null}
          </main>
          {editorWidget && (!props.studio || (props.studio.panel === "properties" && !previewing)) ? (
            <aside className="datox-react-dashboard__editor-panel">
              <div className="datox-react-dashboard__editor-sticky">
                <div className="datox-react-dashboard__editor-header">
                  {/* The head names what is open: "Table · Top holdings" (UX research C13). */}
                  <h3>{props.studio ? [widgetLabel(labels, editorWidget.type), editorWidget.title].filter(Boolean).join(" · ") : labels.editWidget}</h3>
                  <button type="button" aria-label={labels.closePanel} onClick={closeWidgetEditor}>
                    ×
                  </button>
                </div>
                <div className="datox-react-dashboard__segmented">
                  <button
                    className={`datox-react-dashboard__segment ${widgetEditorPanel === "data" ? "datox-react-dashboard__segment--active" : ""}`}
                    type="button"
                    onClick={() => setWidgetEditorPanel("data")}
                  >
                    {labels.data}
                  </button>
                  <button
                    className={`datox-react-dashboard__segment ${widgetEditorPanel === "design" ? "datox-react-dashboard__segment--active" : ""}`}
                    type="button"
                    onClick={() => setWidgetEditorPanel("design")}
                  >
                    {labels.design}
                  </button>
              </div>
            </div>
            <div className="datox-react-dashboard__editor-scroll">
              <label>
                <div className="datox-react-dashboard__field-label">{labels.widgetType}</div>
                <select
                  className="datox-react-dashboard__select"
                  style={{ width: "100%", minWidth: 0 }}
                  value={editorWidget.type}
                  disabled={locked}
                  onChange={(event) => changeWidgetType(event.currentTarget.value)}
                >
                  {WIDGETS.map((widget) => (
                    <option key={widget.type} value={widget.type}>
                      {widgetLabel(labels, widget.type)}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="datox-react-dashboard__editor-fields" disabled={locked}>
              {widgetEditorPanel === "data" ? (
                <div className="datox-react-dashboard__editor-section">
                  <div className="datox-react-dashboard__field-label">{labels.data}</div>
                  {editorWidget.type === "text" || editorWidget.type === "title" ? (
                    <label><span>{editorWidget.type === "title" ? labels.titleContent : labels.textContent}</span><textarea className="datox-react-dashboard__textarea" value={editorWidget.type === "title" ? editorWidget.title || "" : editorWidget.text || ""} placeholder={editorWidget.type === "title" ? widgetLabel(labels, "title") : labels.textPlaceholder} onChange={(event) => updateWidget(editorWidget.id, editorWidget.type === "title" ? { title: event.currentTarget.value } : { text: event.currentTarget.value })} /></label>
                  ) : editorWidget.type === "header" ? (
                    <>
                      <label><span>{labels.designer.titleField || labels.selectedWidgetTitle}</span><input className="datox-react-dashboard__input" value={editorWidget.title || ""} onChange={(event) => updateWidget(editorWidget.id, { title: event.currentTarget.value })} /></label>
                      {/* Summer show_date: the as-of date under the title, the same on every page (V2 only). */}
                      {v2 ? <ToggleRow label={labels.headerShowDate || "Show the data date"} checked={editorWidget.style?.showDate !== false} onChange={(showDate) => updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), showDate } })} /> : null}
                      {v2 ? <p className="datox-react-dashboard__settings-help">{labels.headerSyncHelp || "The header is the same on every page."}</p> : null}
                    </>
                  ) : editorWidget.type === "freeTable" && editorWidget.data?.freeStyle ? null : (
                    <label><span>{labels.widgetDataset}</span><select className="datox-react-dashboard__select" value={editorWidget.data?.dataset_id || ""} onChange={(event) => updateEditorWidgetData({ dataset_id: event.currentTarget.value, metric: undefined, label: undefined, measures: undefined, columns: undefined, displayNames: undefined, filters: [], sort: undefined, targets: [], selectedValues: [] })}><option value="">{labels.notConnected}</option>{(props.datasets || []).map((dataset) => <option key={dataset.id} value={dataset.id}>{dataset.name}</option>)}</select></label>
                  )}
                  {!["text", "title", "header"].includes(editorWidget.type) ? (
                    <>
                      {editorWidget.type === "filter" ? (
                        <div className="datox-react-dashboard__filter-targets">
                          <label><span>{labels.column}</span><select value={editorWidget.data?.label || ""} onChange={(event) => updateEditorWidgetData({ label: event.currentTarget.value, selectedValues: [], ...(editorWidget.data?.field ? { field: event.currentTarget.value || undefined } : {}) })}><option value="">{labels.designer.chooseColumn}</option>{editorWidgetColumns.map((column) => <option key={column} value={column}>{column}</option>)}</select></label>
                          {/* Summer universal filter (backlog #21): bound to the field, it filters every widget whose data
                              has that column; the per-dataset targets below still apply on top. */}
                          <ToggleRow
                            label={labels.filterEveryWidget || "Filter every widget with this column"}
                            checked={!!editorWidget.data?.field}
                            disabled={!editorWidget.data?.label}
                            onChange={(on) => updateEditorWidgetData({ field: on ? editorWidget.data?.label : undefined })}
                          />
                          {editorWidget.data?.field ? (
                            <p className="datox-react-dashboard__settings-help" data-filter-field={editorWidget.data.field}>
                              {(labels.filterEveryWidgetHelp || "Every widget whose data has “{column}” follows this filter.").replace("{column}", editorWidget.data.field)}
                            </p>
                          ) : null}
                          <div className="datox-react-dashboard__field-label">{labels.filterTargets}</div>
                          {(props.datasets || []).map((dataset) => {
                            const target = (editorWidget.data?.targets || []).find(
                              (item) => item.dataset_id === dataset.id,
                            )
                            const columns = datasetColumns(dataset.id, props.outputTables, props.datasets)
                            return (
                              <div key={dataset.id} className="datox-react-dashboard__filter-target-row">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(target)}
                                    onChange={(event) =>
                                      event.currentTarget.checked
                                        ? updateFilterTarget(dataset.id, {})
                                        : removeFilterTarget(dataset.id)
                                    }
                                  />{" "}
                                  <span>{dataset.name}</span>
                                </label>
                                <select
                                  disabled={!target}
                                  value={target?.column || columns[0] || ""}
                                  onChange={(event) =>
                                    updateFilterTarget(dataset.id, { column: event.currentTarget.value })
                                  }
                                >
                                  {columns.map((column) => (
                                    <option key={column} value={column}>
                                      {column}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )
                          })}
                        </div>
                      ) : null}
                      {editorWidget.type === "freeTable" && editorWidget.data?.freeStyle ? (
                        <FreeStyleEditor
                          key={editorWidget.id}
                          value={editorWidget.data.freeStyle}
                          labels={labels}
                          onChange={(freeStyle) => updateEditorWidgetData({ freeStyle })}
                        />
                      ) : null}
                      {editorWidget.type === "freeTable" && !editorWidget.data?.freeStyle ? (
                        <>
                          <p className="datox-react-dashboard__settings-help">{labels.conversionHelp}</p>
                          <button
                            type="button"
                            className="datox-react-dashboard__button"
                            disabled={
                              !editorWidgetConversionTable ||
                              editorWidgetConversionTable.loadError ||
                              editorWidgetConversionTable.headers.length > 20 ||
                              editorWidgetConversionTable.rows.length > 50
                            }
                            onClick={() =>
                              updateEditorWidgetData({
                                freeStyle: freeStyleFromTable(editorWidgetConversionTable, labels),
                                dataset_id: undefined,
                              })
                            }
                          >
                            {labels.editFreeStyle}
                          </button>
                          {editorWidgetConversionTable &&
                          (editorWidgetConversionTable.headers.length > 20 ||
                            editorWidgetConversionTable.rows.length > 50) ? (
                            <div role="status">{labels.freeStyleLimit}</div>
                          ) : null}
                          {editorWidgetConversionTable?.loadError ? (
                            <div role="alert">{labels.datasetLoadError}</div>
                          ) : null}
                        </>
                      ) : null}
                      {isChartWidget(editorWidget.type) ? (
                        <div className="datox-react-dashboard__chart-data-editor">
                          <div className="datox-react-dashboard__field-label">{labels.chartData}</div>
                          <div className="datox-react-dashboard__muted-text">
                            {chartDataSummary(editorWidget, labels)}
                          </div>
                          {editorWidget.type === "scatter" ? (
                            <>
                              {[
                                ["xColumn", labels.scatterX],
                                ["yColumn", labels.scatterY],
                                ["sizeColumn", labels.scatterSize],
                              ].map(([key, label]) => (
                                <label key={key}>
                                  <span>{label}</span>
                                  <select
                                    className="datox-react-dashboard__select"
                                    aria-invalid={Boolean(
                                      editorWidgetTable &&
                                      !editorWidgetTable.loadError &&
                                      (((key !== "sizeColumn" || editorWidget.data?.[key]) &&
                                        !editorWidgetNumericColumns.includes(editorWidget.data?.[key])) ||
                                        (key === "yColumn" &&
                                          editorWidget.data?.xColumn &&
                                          editorWidget.data.xColumn === editorWidget.data?.yColumn)),
                                    )}
                                    value={editorWidget.data?.[key] || ""}
                                    onChange={(event) => updateEditorWidgetData({ [key]: event.currentTarget.value })}
                                  >
                                    <option value="">
                                      {key === "sizeColumn" ? labels.scatterSizeNone : labels.noBinding}
                                    </option>
                                    {editorWidget.data?.[key] &&
                                    !editorWidgetNumericColumns.includes(editorWidget.data[key]) ? (
                                      <option value={editorWidget.data[key]} disabled>
                                        {editorWidget.data[key]}
                                      </option>
                                    ) : null}
                                    {editorWidgetNumericColumns.map((column) => (
                                      <option key={column} value={column}>
                                        {column}
                                      </option>
                                    ))}
                                  </select>
                                  {editorWidgetTable &&
                                  !editorWidgetTable.loadError &&
                                  (key !== "sizeColumn" || editorWidget.data?.[key]) &&
                                  !editorWidgetNumericColumns.includes(editorWidget.data?.[key]) ? (
                                    <small className="datox-react-dashboard__field-error">
                                      {labels.numericBindingRequired}
                                    </small>
                                  ) : null}
                                  {key === "yColumn" &&
                                  editorWidget.data?.xColumn &&
                                  editorWidget.data.xColumn === editorWidget.data?.yColumn ? (
                                    <small className="datox-react-dashboard__field-error">
                                      {labels.scatterSameAxes}
                                    </small>
                                  ) : null}
                                </label>
                              ))}
                            </>
                          ) : (
                            <label>
                              <span>{labels.groupBy}</span>
                              <small>{labels.groupByHelp}</small>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.label || ""}
                                onChange={(event) => updateEditorWidgetData({ label: event.currentTarget.value })}
                              >
                                <option value="" disabled>
                                  {labels.noBinding}
                                </option>
                                {editorWidget.data?.label && !editorWidgetColumns.includes(editorWidget.data.label) ? (
                                  <option value={editorWidget.data.label} disabled>
                                    {editorWidget.data.label}
                                  </option>
                                ) : null}
                                {editorWidgetColumns.map((column) => (
                                  <option key={column} value={column}>
                                    {column}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                          {editorWidget.type !== "scatter" ? (
                            <label>
                              <span>{labels.aggregation}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.aggregation || "sum"}
                                onChange={(event) => updateEditorWidgetData({ aggregation: event.currentTarget.value })}
                              >
                                {["sum", "avg", "min", "max", "count"].map((operation) => (
                                  <option key={operation} value={operation}>
                                    {
                                      labels[
                                        `aggregation${operation === "avg" ? "Average" : operation[0].toUpperCase() + operation.slice(1)}`
                                      ]
                                    }
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                          {["pie", "treemap", "funnel", "heatmap"].includes(editorWidget.type) ? (
                            <label>
                              <span>{labels.metricColumn}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.metric || ""}
                                onChange={(event) => updateEditorWidgetData({ metric: event.currentTarget.value })}
                              >
                                <option value="" disabled>
                                  {labels.noBinding}
                                </option>
                                {editorWidget.data?.metric &&
                                !editorWidgetNumericColumns.includes(editorWidget.data.metric) ? (
                                  <option value={editorWidget.data.metric} disabled>
                                    {editorWidget.data.metric}
                                  </option>
                                ) : null}
                                {editorWidgetNumericColumns.map((column) => (
                                  <option key={column} value={column}>
                                    {column}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                          {["bar", "line", "multiSeries"].includes(editorWidget.type) ? (
                            <label>
                              <span>{labels.dataStreams}</span>
                              <small>{labels.dataStreamsHelp}</small>
                              <div className="datox-react-dashboard__visible-columns">
                                {editorWidgetNumericColumns.map((column) => (
                                  <button
                                    key={column}
                                    type="button"
                                    aria-pressed={(editorWidget.data?.measures || [editorWidget.data?.metric]).includes(
                                      column,
                                    )}
                                    className={
                                      (
                                        editorWidget.data?.measures || [editorWidget.data?.metric].filter(Boolean)
                                      ).includes(column)
                                        ? "datox-react-dashboard__column-chip datox-react-dashboard__column-chip--active"
                                        : "datox-react-dashboard__column-chip"
                                    }
                                    onClick={() => toggleMeasure(column)}
                                  >
                                    {column}
                                  </button>
                                ))}
                              </div>
                            </label>
                          ) : null}
                          {["bar", "line", "multiSeries"].includes(editorWidget.type) ? (
                            <EditorSection title={labels.dataStreamDisplayNames}>
                              {(editorWidget.data?.measures?.length
                                ? editorWidget.data.measures
                                : [editorWidget.data?.metric].filter(Boolean)
                              )
                                .filter(() => ["bar", "line", "multiSeries"].includes(editorWidget.type))
                                .map((measure) => (
                                  <label key={measure}>
                                    <span>{measure}</span>
                                    <input
                                      className="datox-react-dashboard__input"
                                      value={editorWidget.data?.displayNames?.[measure] || ""}
                                      placeholder={measure}
                                      onChange={(event) =>
                                        updateEditorWidgetData({
                                          displayNames: {
                                            ...(editorWidget.data?.displayNames || {}),
                                            [measure]: event.currentTarget.value,
                                          },
                                        })
                                      }
                                    />
                                  </label>
                                ))}
                            </EditorSection>
                          ) : null}
                          {editorWidget.type !== "scatter" ? (
                            <EditorSection title={labels.resultLimit}>
                              <p className="datox-react-dashboard__settings-help">{labels.resultLimitHelp}</p>
                              {/* Summer `limit_enabled`: the cap can be switched off. Part-of-whole charts
                                  group the rest into "Other"; ranked charts rank before limiting. */}
                              <ToggleRow
                                label={labels.limitCategories || "Limit categories"}
                                checked={editorWidget.data?.limitEnabled !== false}
                                onChange={(limitEnabled) => updateEditorWidgetData({ limitEnabled })}
                              />
                              {editorWidget.data?.limitEnabled !== false ? (
                                <p className="datox-react-dashboard__settings-help">
                                  {["pie", "treemap"].includes(editorWidget.type)
                                    ? labels.limitOtherHelp || "The rest is grouped into one Other slice, so the chart still adds up to 100%."
                                    : labels.limitRankedHelp || "Pick a column in By so the largest categories are kept."}
                                </p>
                              ) : null}
                              <div className="datox-react-dashboard__top-row">
                                <label>
                                  <span>{labels.showTop}</span>
                                  <input
                                    className="datox-react-dashboard__input"
                                    type="number"
                                    min="1"
                                    max="100"
                                    disabled={editorWidget.data?.limitEnabled === false}
                                    value={editorWidget.data?.showTop || 12}
                                    onChange={(event) =>
                                      updateEditorWidgetData({ showTop: Number(event.currentTarget.value) || 12 })
                                    }
                                  />
                                </label>
                                <label>
                                  <span>{labels.by}</span>
                                  <select
                                    className="datox-react-dashboard__select"
                                    value={editorWidget.data?.topBy || ""}
                                    onChange={(event) => updateEditorWidgetData({ topBy: event.currentTarget.value })}
                                  >
                                    <option value="">{labels.noSort}</option>
                                    {(editorWidgetNumericColumns.length
                                      ? editorWidgetNumericColumns
                                      : editorWidgetColumns
                                    ).map((column) => (
                                      <option key={column} value={column}>
                                        {column}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  <span>{labels.order}</span>
                                  <select
                                    className="datox-react-dashboard__select"
                                    disabled={!editorWidget.data?.topBy}
                                    value={editorWidget.data?.topOrder || "desc"}
                                    onChange={(event) =>
                                      updateEditorWidgetData({ topOrder: event.currentTarget.value })
                                    }
                                  >
                                    <option value="desc">{labels.sortDescending}</option>
                                    <option value="asc">{labels.sortAscending}</option>
                                  </select>
                                </label>
                              </div>
                            </EditorSection>
                          ) : null}
                        </div>
                      ) : null}
                      {editorWidget.type === "kpi" ? (
                        <KpiFormulaSettings
                          widget={editorWidget}
                          table={editorWidgetConversionTable}
                          labels={labels}
                          filterWidgets={activeFilterWidgets}
                          onChange={(value) => updateWidget(editorWidget.id, { kpi_calculation: value })}
                        >
                          <div className="datox-react-dashboard__chart-data-editor">
                            <div className="datox-react-dashboard__field-label">{labels.summarize}</div>
                            <label>
                              <span>{labels.metricColumn}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.metric || ""}
                                onChange={(event) => updateEditorWidgetData({ metric: event.currentTarget.value })}
                              >
                                {(editorWidgetNumericColumns.length
                                  ? editorWidgetNumericColumns
                                  : editorWidgetColumns
                                ).map((column) => (
                                  <option key={column} value={column}>
                                    {column}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>{labels.aggregation}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.aggregation || "sum"}
                                onChange={(event) => updateEditorWidgetData({ aggregation: event.currentTarget.value })}
                              >
                                <option value="sum">{labels.aggregationSum}</option>
                                <option value="avg">{labels.aggregationAverage}</option>
                                <option value="min">{labels.aggregationMin}</option>
                                <option value="max">{labels.aggregationMax}</option>
                                <option value="count">{labels.aggregationCount}</option>
                                <option value="first">{labels.aggregationFirst}</option>
                                <option value="last">{labels.aggregationLast}</option>
                              </select>
                            </label>
                          </div>
                        </KpiFormulaSettings>
                      ) : null}
                      {editorWidget.type !== "filter" &&
                      !(editorWidget.type === "freeTable" && editorWidget.data?.freeStyle) ? (
                        <>
                          <details className="datox-react-dashboard__editor-details">
                            <summary>{labels.sampleData}</summary>
                            <div className="datox-react-dashboard__sample-scroll">
                              <TableWidget table={editorWidgetTable} limit={4} />
                            </div>
                          </details>
                          <EditorSection
                            title={labels.filtersAndSorting}
                            badge={editorWidget.data?.filters?.length || undefined}
                          >
                            <div className="datox-react-dashboard__field-label">{labels.filter}</div>
                            <button
                              className="datox-react-dashboard__calculation-button"
                              type="button"
                              onClick={addEditorFilter}
                            >
                              + {labels.addFilter}
                            </button>
                            {(editorWidget.data?.filters || []).length ? (
                              <div className="datox-react-dashboard__filter-editor-list">
                                {(editorWidget.data?.filters || []).map((filter) => {
                                  // Kind-aware operators and value hints (backlog #12): text = ≠ contains; number and
                                  // date = ≠ > ≥ < ≤ (dates in words); a <datalist> of the column's values.
                                  const { kind, hints } = filterColumnInfo(filter.column)
                                  const operators = filterOperatorsFor(kind, filter.operator || "contains")
                                  const listId = `datox-filter-values-${editorWidget.id}-${filter.id}`
                                  return (
                                  <div key={filter.id} className="datox-react-dashboard__filter-editor-row" data-kind={kind}>
                                    <select
                                      value={filter.column || ""}
                                      onChange={(event) => {
                                        const column = event.currentTarget.value
                                        updateEditorFilter(filter.id, {
                                          column,
                                          operator: filterOperatorFor(filterColumnKind(editorWidgetTable?.rows, column), filter.operator || "contains"),
                                        })
                                      }}
                                    >
                                      {editorWidgetColumns.map((column) => (
                                        <option key={column} value={column}>
                                          {column}
                                        </option>
                                      ))}
                                    </select>
                                    <select
                                      value={filter.operator || "contains"}
                                      aria-label={labels.filterOperator || "Condition"}
                                      onChange={(event) =>
                                        updateEditorFilter(filter.id, { operator: event.currentTarget.value })
                                      }
                                    >
                                      {operators.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {labels[option.label] || option.text}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      value={filter.value || ""}
                                      placeholder={kind === "date" ? "YYYY-MM-DD" : labels.valueLabel}
                                      inputMode={kind === "number" ? "decimal" : undefined}
                                      list={hints.length && filter.operator !== "notEmpty" ? listId : undefined}
                                      disabled={filter.operator === "notEmpty"}
                                      onChange={(event) =>
                                        updateEditorFilter(filter.id, { value: event.currentTarget.value })
                                      }
                                    />
                                    {hints.length ? (
                                      <datalist id={listId}>
                                        {hints.map((value) => (
                                          <option key={value} value={value} />
                                        ))}
                                      </datalist>
                                    ) : null}
                                    <button type="button" onClick={() => removeEditorFilter(filter.id)}>
                                      ×
                                    </button>
                                    {filterIsApplied(filter) ? null : (
                                      <span className="datox-react-dashboard__filter-not-applied" role="status">
                                        {labels.filterNotApplied || "Not applied: fill in a value"}
                                      </span>
                                    )}
                                  </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="datox-react-dashboard__muted-text">{labels.noFiltersApplied}</div>
                            )}
                            <div className="datox-react-dashboard__field-label">{labels.sort}</div>
                            <div className="datox-react-dashboard__sort-row">
                              <select
                                value={editorWidget.data?.sort?.column || ""}
                                onChange={(event) =>
                                  updateEditorWidgetData({
                                    sort: { ...(editorWidget.data?.sort || {}), column: event.currentTarget.value },
                                  })
                                }
                              >
                                <option value="">{labels.noSort}</option>
                                {editorWidgetColumns.map((column) => (
                                  <option key={column} value={column}>
                                    {column}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={editorWidget.data?.sort?.direction || "asc"}
                                onChange={(event) =>
                                  updateEditorWidgetData({
                                    sort: { ...(editorWidget.data?.sort || {}), direction: event.currentTarget.value },
                                  })
                                }
                              >
                                <option value="asc">{labels.sortAscending}</option>
                                <option value="desc">{labels.sortDescending}</option>
                              </select>
                            </div>
                            <label>
                              <span>{labels.rowLimit}</span>
                              <input
                                className="datox-react-dashboard__input"
                                type="number"
                                min="1"
                                max="10000"
                                value={editorWidget.data?.rowLimit || ""}
                                placeholder={labels.noRowLimit}
                                onChange={(event) =>
                                  updateEditorWidgetData({
                                    rowLimit: event.currentTarget.value ? Number(event.currentTarget.value) : undefined,
                                  })
                                }
                              />
                            </label>
                          </EditorSection>
                          {["table", "freeTable"].includes(editorWidget.type) ? (
                            <>
                              <div className="datox-react-dashboard__field-label">{labels.columns}</div>
                              <div className="datox-react-dashboard__visible-columns">
                                {editorWidgetColumns.map((column) => (
                                  <button
                                    key={column}
                                    type="button"
                                    className={
                                      (editorWidget.data?.columns?.length
                                        ? editorWidget.data.columns
                                        : editorWidgetColumns
                                      ).includes(column)
                                        ? "datox-react-dashboard__column-chip datox-react-dashboard__column-chip--active"
                                        : "datox-react-dashboard__column-chip"
                                    }
                                    onClick={() => toggleEditorColumn(column)}
                                  >
                                    {column}
                                  </button>
                                ))}
                              </div>
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="datox-react-dashboard__editor-section">
                  <label>
                    <span>{labels.selectedWidgetTitle}</span>
                    <input
                      className="datox-react-dashboard__input"
                      value={editorWidget.title || ""}
                      onChange={(event) => updateWidget(editorWidget.id, { title: event.currentTarget.value })}
                    />
                  </label>
                  <SegmentedControl
                    label={labels.cornerRadius}
                    value={editorWidget.style?.radius || config.design?.radius || "medium"}
                    options={[
                      { value: "square", label: labels.radiusSquare },
                      { value: "medium", label: labels.radiusMedium },
                      { value: "large", label: labels.radiusLarge },
                    ]}
                    onChange={(radius) =>
                      updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), radius } })
                    }
                  />
                  <ColorSwatches
                    label={labels.accentColor}
                    value={editorWidget.style?.accent || config.design?.accent || "#2f80ed"}
                    onChange={(accent) =>
                      updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), accent } })
                    }
                    colors={["#2f80ed", "#2f54eb", "#7048e8", "#40a9ff", "#339989", "#2f855a", "#d6334c"]}
                  />
                  {editorWidget.type === "text" || editorWidget.type === "title" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <SegmentedControl
                        label={labels.textAlign}
                        value={editorWidget.style?.textAlign || "left"}
                        options={[
                          { value: "left", label: labels.alignLeft },
                          { value: "center", label: labels.alignCenter },
                          { value: "right", label: labels.alignRight },
                        ]}
                        onChange={(textAlign) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), textAlign } })
                        }
                      />
                      <SegmentedControl
                        label={labels.verticalAlign}
                        value={editorWidget.style?.verticalAlign || (editorWidget.type === "title" ? "center" : "top")}
                        options={[
                          { value: "top", label: labels.alignTop },
                          { value: "center", label: labels.alignMiddle },
                          { value: "bottom", label: labels.alignBottom },
                        ]}
                        onChange={(verticalAlign) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), verticalAlign } })
                        }
                      />
                      <label>
                        <span>{labels.fontSize}</span>
                        <input
                          className="datox-react-dashboard__input"
                          type="number"
                          min="8"
                          max="72"
                          value={editorWidget.style?.fontSize || (editorWidget.type === "title" ? 28 : 16)}
                          onChange={(event) =>
                            updateWidget(editorWidget.id, {
                              style: { ...(editorWidget.style || {}), fontSize: Number(event.currentTarget.value) },
                            })
                          }
                        />
                      </label>
                      <label>
                        <span>{labels.fontWeight}</span>
                        <select
                          className="datox-react-dashboard__select"
                          value={editorWidget.style?.fontWeight || (editorWidget.type === "title" ? "800" : "400")}
                          onChange={(event) =>
                            updateWidget(editorWidget.id, {
                              style: { ...(editorWidget.style || {}), fontWeight: event.currentTarget.value },
                            })
                          }
                        >
                          <option value="400">400</option>
                          <option value="500">500</option>
                          <option value="700">700</option>
                          <option value="800">800</option>
                          <option value="900">900</option>
                        </select>
                      </label>
                      <ColorSwatches
                        label={labels.textColor}
                        value={editorWidget.style?.textColor || "#101828"}
                        onChange={(textColor) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), textColor } })
                        }
                        colors={["#101828", "#344054", "#667085", "#2f80ed", "#2f54eb", "#d6334c", "#ffffff"]}
                      />
                    </div>
                  ) : null}
                  {editorWidget.type === "kpi" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <KpiDisplaySettings
                        labels={labels}
                        style={kpiDisplayStyle(editorWidget)}
                        onChange={(patch) => updateWidget(editorWidget.id, foldKpiDisplay(editorWidget, patch))}
                      />
                      {editorWidget.style?.valueFormat !== "currency" && editorWidget.style?.valueFormat !== "percent" ? (
                        <label>
                          <span>{labels.designer.prefix}</span>
                          <input
                            className="datox-react-dashboard__input"
                            maxLength={12}
                            value={editorWidget.kpi_prefix || ""}
                            onChange={(event) =>
                              updateWidget(editorWidget.id, { kpi_prefix: event.currentTarget.value || undefined })
                            }
                          />
                        </label>
                      ) : null}
                      <KpiComparisonSettings
                        labels={labels}
                        widget={editorWidget}
                        options={comparisonState.options}
                        onPreviousPeriod={locked ? undefined : comparisonState.setPreviousPeriod}
                        isStale={comparisonState.stale}
                        stateMessage={comparisonState.stale ? comparisonState.staleMessage : comparisonState.error}
                        onChange={(next) =>
                          updateWidget(editorWidget.id, {
                            kpi_comparison: next,
                          })
                        }
                        onReload={() => window.location.reload()}
                      />
                      <SegmentedControl
                        label={labels.textAlign}
                        value={editorWidget.style?.textAlign || "left"}
                        options={[
                          { value: "left", label: labels.alignLeft },
                          { value: "center", label: labels.alignCenter },
                          { value: "right", label: labels.alignRight },
                        ]}
                        onChange={(textAlign) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), textAlign } })
                        }
                      />
                    </div>
                  ) : null}
                  {editorWidget.type === "table" || editorWidget.type === "freeTable" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <EditorSection title={labels.tableAppearance}>
                        <SegmentedControl
                          label={labels.tableDensity}
                          value={editorWidget.style?.density || "normal"}
                          options={[
                            { value: "compact", label: labels.fontCompact },
                            { value: "normal", label: labels.fontNormal },
                            { value: "large", label: labels.fontLarge },
                          ]}
                          onChange={(density) =>
                            updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), density } })
                          }
                        />
                        <ToggleRow
                          label={labels.showTableHeader}
                          checked={editorWidget.style?.showHeader !== false}
                          onChange={(showHeader) =>
                            updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), showHeader } })
                          }
                        />
                        <ToggleRow
                          label={labels.stripedRows}
                          checked={Boolean(editorWidget.style?.stripedRows)}
                          onChange={(stripedRows) =>
                            updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), stripedRows } })
                          }
                        />
                        <ToggleRow
                          label={labels.headerFill}
                          checked={
                            editorWidget.style?.headerFill !== false &&
                            editorWidget.data?.freeStyle?.highlightHeaderRow !== false
                          }
                          onChange={(headerFill) =>
                            updateWidget(editorWidget.id, {
                              style: { ...(editorWidget.style || {}), headerFill },
                              ...(editorWidget.data?.freeStyle
                                ? {
                                    data: {
                                      ...editorWidget.data,
                                      freeStyle: { ...editorWidget.data.freeStyle, highlightHeaderRow: headerFill },
                                    },
                                  }
                                : {}),
                            })
                          }
                        />
                        {editorWidget.type === "table" && editorWidget.style?.headerFill !== false ? (
                          // Bobur's header colour (style.tableHeaderColor); the page accent clears it.
                          <ColorSwatches
                            label={labels.designer.tableHeaderColor}
                            value={editorWidget.style?.tableHeaderColor || editorWidget.style?.accent || config.design?.accent || "#2f80ed"}
                            onChange={(color) => {
                              const accent = editorWidget.style?.accent || config.design?.accent || "#2f80ed"
                              updateWidget(editorWidget.id, {
                                style: {
                                  ...(editorWidget.style || {}),
                                  tableHeaderColor: color.toLowerCase() === accent.toLowerCase() ? undefined : color,
                                },
                              })
                            }}
                            colors={[...new Set([(editorWidget.style?.accent || config.design?.accent || "#2f80ed").toLowerCase(), "#101828", "#344054", "#2f54eb", "#339989", "#2f855a", "#d6334c"])]}
                          />
                        ) : null}
                        {editorWidget.data?.freeStyle ? (
                          <ToggleRow
                            label={labels.showRowNumbers}
                            checked={Boolean(editorWidget.data.freeStyle.showRowNumbers)}
                            onChange={(showRowNumbers) =>
                              updateEditorWidgetData({ freeStyle: { ...editorWidget.data.freeStyle, showRowNumbers } })
                            }
                          />
                        ) : null}
                      </EditorSection>
                      {editorWidget.type === "table" ? (
                        <TableSettingsEditor
                          key={editorWidget.id}
                          data={editorWidget.data || {}}
                          headers={editorWidgetVisibleColumns}
                          table={editorWidgetTable}
                          labels={labels}
                          onChange={updateEditorWidgetData}
                        />
                      ) : null}
                      {!(editorWidget.type === "freeTable" && editorWidget.data?.freeStyle) ? (
                        <div className="datox-react-dashboard__cell-rules-header">
                          <div className="datox-react-dashboard__cell-rules-header-label">Conditional colors</div>
                          <button
                            className="datox-react-dashboard__button datox-react-dashboard__button--outline-primary"
                            type="button"
                            onClick={() =>
                              openEditorCellColorRule(createEditorCellColorRule(editorWidgetVisibleColumns[0] || ""))
                            }
                          >
                            <span className="datox-react-dashboard__cell-rules-add-button-content">
                              <TbPlus size={14} />
                              <span>Add rule</span>
                            </span>
                          </button>
                        </div>
                      ) : null}
                      {!(editorWidget.type === "freeTable" && editorWidget.data?.freeStyle) &&
                        (editorCellColorRules.length ? (
                          <div className="datox-react-dashboard__cell-rules-list">
                            {editorCellColorRules.map((rule, index) => {
                              const operatorLabel =
                                CELL_COLOR_OPERATOR_OPTIONS.find((option) => option.value === rule.operator)?.label ||
                                "="
                              return (
                                <div key={rule.id} className="datox-react-dashboard__cell-rule-card">
                                  <span
                                    aria-hidden
                                    className="datox-react-dashboard__cell-rule-swatch"
                                    style={{ background: rule.color || "#dbeafe" }}
                                  />
                                  <div className="datox-react-dashboard__cell-rule-body">
                                    <div className="datox-react-dashboard__cell-rule-text">
                                      {rule.column || "Column"} {operatorLabel} {String(rule.value ?? "") || "(blank)"}
                                    </div>
                                    <div className="datox-react-dashboard__cell-rule-subtitle datox-react-dashboard__muted-text">
                                      {rule.highlight_row === true ? "Entire row" : "Cell only"} ·{" "}
                                      {rule.color || "#dbeafe"}
                                    </div>
                                  </div>
                                  <div className="datox-react-dashboard__cell-rule-actions-group">
                                    {/* <div className="datox-react-dashboard__cell-rule-actions-row">
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Move rule up"
                                        title="Move rule up"
                                        disabled={index === 0}
                                        onClick={() => moveEditorCellColorRule(rule.id, "up")}
                                      >
                                        <TbArrowNarrowUp size={16} />
                                      </button>
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Move rule down"
                                        title="Move rule down"
                                        disabled={index === editorCellColorRules.length - 1}
                                        onClick={() => moveEditorCellColorRule(rule.id, "down")}
                                      >
                                        <TbArrowNarrowDown size={16} />
                                      </button>
                                    </div> */}
                                    <div className="datox-react-dashboard__cell-rule-actions-row">
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Edit condition"
                                        onClick={() => openEditorCellColorRule({ ...rule })}
                                      >
                                        <TbEdit size={16} />
                                      </button>
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Remove condition"
                                        onClick={() => removeEditorCellColorRule(rule.id)}
                                      >
                                        <TbTrash size={16} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="datox-react-dashboard__muted-text">No conditional colors configured.</div>
                        ))}
                    </div>
                  ) : null}
                  {editorWidget.type === "filter" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <SegmentedControl
                        label={labels.filterDisplay}
                        value={editorWidget.style?.display || "chips"}
                        options={[
                          { value: "chips", label: labels.filterDisplayChips },
                          { value: "list", label: labels.filterDisplayList },
                          { value: "dropdown", label: labels.filterDisplayDropdown },
                        ]}
                        onChange={(display) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), display } })
                        }
                      />
                      <ToggleRow
                        label={labels.allowMultipleValues}
                        checked={editorWidget.style?.multi !== false}
                        onChange={(multi) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), multi } })
                        }
                      />
                    </div>
                  ) : null}
                  {isChartWidget(editorWidget.type) ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <div className="datox-react-dashboard__field-label">{labels.chartType}</div>
                      <div className="datox-react-dashboard__chart-type-grid">
                        {[
                          ["bar", labels.widgets.bar],
                          ["line", labels.widgets.line],
                          ["scatter", labels.widgets.scatter],
                          ["pie", labels.widgets.pie],
                          ["treemap", labels.widgets.treemap],
                          ["funnel", labels.widgets.funnel],
                        ].map(([type, label]) => (
                          <button
                            key={type}
                            className={
                              editorWidget.type === type
                                ? "datox-react-dashboard__chart-type-card datox-react-dashboard__chart-type-card--active"
                                : "datox-react-dashboard__chart-type-card"
                            }
                            type="button"
                            onClick={() => changeWidgetType(type)}
                          >
                            <span>{chartTypeIcon(type)}</span>
                            <strong>{label}</strong>
                          </button>
                        ))}
                      </div>
                      {CHART_CONTROLS[editorWidget.type]?.includes("orientation") ||
                      editorWidget.type === "pie" ||
                      CHART_CONTROLS[editorWidget.type]?.includes("colors") ||
                      (CHART_CONTROLS[editorWidget.type]?.includes("stack") &&
                        editorWidget.data?.measures?.length > 1) ? (
                        <EditorSection title={labels.chartSection} open>
                          {CHART_CONTROLS[editorWidget.type]?.includes("orientation") ? (
                            <SegmentedControl
                              label={labels.chartOrientation}
                              value={editorWidget.style?.orientation || "vertical"}
                              options={[
                                { value: "vertical", label: labels.vertical },
                                { value: "horizontal", label: labels.horizontal },
                              ]}
                              onChange={(orientation) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), orientation } })
                              }
                            />
                          ) : null}
                          {editorWidget.type === "bar" ? (
                            <SegmentedControl
                              label={labels.barDisplay || "Display"}
                              value={editorWidget.style?.barDisplay || "bars"}
                              options={[
                                { value: "bars", label: labels.barDisplayBars || "Bars" },
                                { value: "contributors", label: labels.barDisplayContributors || "Contributors" },
                                { value: "detractors", label: labels.barDisplayDetractors || "Detractors" },
                              ]}
                              onChange={(barDisplay) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...(editorWidget.style || {}), barDisplay: barDisplay === "bars" ? undefined : barDisplay },
                                })
                              }
                            />
                          ) : null}
                          {editorWidget.type === "pie" ? (
                            <ToggleRow
                              label={labels.donutChart}
                              checked={editorWidget.style?.donut !== false}
                              onChange={(donut) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), donut } })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("colors") ? (
                            <ToggleRow
                              label={labels.distinctBarColors}
                              checked={Boolean(editorWidget.style?.useDistinctBarColors)}
                              onChange={(useDistinctBarColors) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...editorWidget.style, useDistinctBarColors },
                                })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("stack") &&
                          editorWidget.data?.measures?.length > 1 ? (
                            <SeriesSettings
                              labels={labels}
                              type={editorWidget.type}
                              measures={editorWidget.data.measures}
                              displayNames={editorWidget.data?.displayNames}
                              style={editorWidget.style || {}}
                              horizontal={editorWidget.style?.orientation === "horizontal"}
                              onChange={(patch) => updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), ...patch } })}
                            />
                          ) : null}
                        </EditorSection>
                      ) : null}
                      {CHART_CONTROLS[editorWidget.type]?.some((control) =>
                        ["legend", "values", "adjacent"].includes(control),
                      ) ? (
                        <EditorSection title={labels.labelsSection}>
                          {CHART_CONTROLS[editorWidget.type]?.includes("legend") ? (
                            <ToggleRow
                              label={labels.showLegend}
                              checked={editorWidget.style?.showLegend !== false}
                              onChange={(showLegend) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), showLegend } })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("values") ? (
                            <ToggleRow
                              label={labels.showValueLabels}
                              checked={
                                // On by default for a pie, and on Lesha's sheet (V2), which writes values unless told not to.
                                editorWidget.type === "pie" || (v2 && sheetStyle(config.design) === "lesha")
                                  ? editorWidget.style?.showValueLabels !== false
                                  : Boolean(editorWidget.style?.showValueLabels)
                              }
                              onChange={(showValueLabels) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...(editorWidget.style || {}), showValueLabels },
                                })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("adjacent") ? (
                            <ToggleRow
                              label={labels.showValuesNextToLabels}
                              checked={Boolean(editorWidget.style?.showValuesNextToLabels)}
                              onChange={(showValuesNextToLabels) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...(editorWidget.style || {}), showValuesNextToLabels },
                                })
                              }
                            />
                          ) : null}
                        </EditorSection>
                      ) : null}
                      {CHART_CONTROLS[editorWidget.type]?.includes("axes") ? (
                        <EditorSection title={labels.chartAxes}>
                          {CHART_CONTROLS[editorWidget.type]?.includes("axes") ? (
                            <ToggleRow
                              label={labels.showAxisLabels}
                              checked={editorWidget.style?.showValueAxisLabels !== false}
                              onChange={(showValueAxisLabels) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...(editorWidget.style || {}), showValueAxisLabels },
                                })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("grid") ? (
                            <ToggleRow
                              label={labels.showGridLines}
                              checked={editorWidget.style?.showGrid !== false}
                              onChange={(showGrid) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), showGrid } })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("titles") ? (
                            <label>
                              <span>{labels.xAxisLabel}</span>
                              <input
                                className="datox-react-dashboard__input"
                                value={editorWidget.style?.xAxisLabel || ""}
                                onChange={(event) =>
                                  updateWidget(editorWidget.id, {
                                    style: { ...(editorWidget.style || {}), xAxisLabel: event.currentTarget.value },
                                  })
                                }
                              />
                            </label>
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("titles") ? (
                            <label>
                              <span>{labels.yAxisLabel}</span>
                              <input
                                className="datox-react-dashboard__input"
                                value={editorWidget.style?.yAxisLabel || ""}
                                onChange={(event) =>
                                  updateWidget(editorWidget.id, {
                                    style: { ...(editorWidget.style || {}), yAxisLabel: event.currentTarget.value },
                                  })
                                }
                              />
                            </label>
                          ) : null}
                        </EditorSection>
                      ) : null}
                      {CHART_CONTROLS[editorWidget.type]?.includes("format") ? (
                        <EditorSection title={labels.numberFormatting}>
                          {CHART_CONTROLS[editorWidget.type]?.includes("format") ? (
                            <label>
                              <span>{labels.valueFormat}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.style?.valueFormat || "number"}
                                onChange={(event) =>
                                  updateWidget(editorWidget.id, {
                                    style: { ...(editorWidget.style || {}), valueFormat: event.currentTarget.value },
                                  })
                                }
                              >
                                <option value="number">{labels.numberFormat}</option>
                                <option value="integer">{labels.integerFormat}</option>
                                <option value="percent">{labels.percentFormat}</option>
                              </select>
                            </label>
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("format") ? (
                            <label>
                              <span>{labels.decimalPlaces}</span>
                              <EditorNumberInput
                                min={0}
                                max={6}
                                value={
                                  editorWidget.style?.valueFormat === "integer"
                                    ? 0
                                    : (editorWidget.style?.decimalPlaces ?? 2)
                                }
                                disabled={editorWidget.style?.valueFormat === "integer"}
                                onChange={(decimalPlaces) =>
                                  updateWidget(editorWidget.id, { style: { ...editorWidget.style, decimalPlaces } })
                                }
                              />
                            </label>
                          ) : null}
                          {editorWidget.style?.valueFormat === "percent" ? (
                            <p className="datox-react-dashboard__settings-help">{labels.percentFormatHelp}</p>
                          ) : null}
                        </EditorSection>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
              <details className="datox-react-dashboard__editor-details"><summary>{labels.designer.position}</summary><div className="designer-field-grid">{[["x", "positionX"], ["y", "positionY"], ["w", "width"], ["h", "height"]].map(([key, label]) => <label key={key}><span>{labels.designer[label]}</span><input className="datox-react-dashboard__input" type="number" value={Math.round(editorWidget.layout[key])} onChange={(event) => updateWidget(editorWidget.id, { layout: fitLayout({ ...editorWidget.layout, [key]: Number(event.currentTarget.value) }, editorWidget) })} /></label>)}</div></details>
              </fieldset>
            </div>
            {props.studio && editorWidget.data?.dataset_id ? (
              // Where the number comes from, in words (UX research E6): the table it reads, and the way to change it.
              <p className="designer-source-line">
                <span>{(labels.designer.sourceLine || "{{table}}").replace("{{table}}", readableTableName((props.datasets || []).find((dataset) => dataset.id === editorWidget.data.dataset_id)?.name || editorWidget.data.dataset_id))}</span>
                <button type="button" onClick={props.studio.onWorkflow}>{labels.designer.changeInWorkflow}</button>
              </p>
            ) : null}
            {props.studio ? <div className="designer-inspect-actions"><button type="button" onClick={() => props.studio.onAsk(selection())}>{labels.designer.askAbout}</button><button type="button" title={labels.designer.viewData} aria-label={labels.designer.viewData} disabled={!editorWidget.data?.dataset_id} onClick={() => { props.onSelectDataset?.(editorWidget.data.dataset_id); props.studio.onData(selection()) }}>{labels.designer.viewDataShort || labels.designer.viewData}</button><button type="button" title={labels.designer.viewCalculation} aria-label={labels.designer.viewCalculation} onClick={props.studio.onWorkflow}>{labels.designer.viewCalculationShort || `${labels.designer.viewCalculation} →`}</button></div> : null}
          </aside>
        ) : null}
      </div>
      {props.studio ? <ReportPageStrip labels={labels} editor={studioEditor} /> : <div className="datox-react-dashboard__statusbar">
        <span>{labels.widgetCount.replace("{count}", String(widgets.length))}</span>
        <span>{labels.tabCount.replace("{count}", String(config.tabs?.length || 1))}</span>
        <DashboardSaveStatus store={props.saveStatusStore} labels={labels} />
      </div>}
      {createOpen ? (
        <DashboardDialog title={labels.createDashboard} onClose={() => setCreateOpen(false)}>
          <form className="datox-react-dashboard__editor-section" onSubmit={submitCreate}>
            <label className="datox-react-dashboard__dialog-field">
              <span>{labels.dashboardName}</span>
              <input
                autoFocus
                placeholder={labels.dashboardNamePlaceholder}
                value={newName}
                onChange={(event) => setNewName(event.currentTarget.value)}
              />
            </label>
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={() => setCreateOpen(false)}>
                {labels.cancel}
              </button>
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="submit"
                disabled={!newName.trim()}
              >
                {labels.createDashboard}
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {renameState ? (
        <DashboardDialog title={renameState.title || labels.rename} onClose={() => setRenameState(null)}>
          <form onSubmit={submitRename}>
            <label className="datox-react-dashboard__dialog-field">
              <span>{labels.name}</span>
              <input
                autoFocus
                placeholder={labels.name}
                value={renameState.name}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  setRenameState((current) => (current ? { ...current, name: value } : current))
                }}
              />
            </label>
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={() => setRenameState(null)}>
                {labels.cancel}
              </button>
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="submit"
                disabled={!renameState.name.trim()}
              >
                {labels.rename}
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {fullViewWidget ? (
        <DashboardDialog
          title={fullViewWidget.title || widgetLabel(labels, fullViewWidget.type)}
          onClose={() => setFullViewWidgetId(null)}
          wide
        >
          <div className="datox-react-dashboard__full-view-widget">
            <WidgetContent
              labels={labels}
              widget={fullViewWidget}
              filterWidgets={activeFilterWidgets}
              table={applyWidgetTableSettings(
                applyDashboardFilters(
                  widgetOutputTable(props.outputTables, fullViewWidget),
                  fullViewWidget,
                  activeFilterWidgets,
                ),
                isChartWidget(fullViewWidget.type)
                  ? { ...fullViewWidget.data, columns: undefined }
                  : fullViewWidget.data,
              )}
              accent={fullViewWidget.style?.accent || config.design?.accent || "#2f80ed"}
              font={config.design?.bodyFont}
              onFilterValueToggle={(value) => toggleFilterValue(fullViewWidget, value)}
            />
          </div>
        </DashboardDialog>
      ) : null}
      {sourceWidget ? (
        <KpiSourcePanel
          widget={sourceWidget}
          filterWidgets={activeFilterWidgets}
          labels={labels}
          onClose={() => setSourceWidget(null)}
        />
      ) : null}
      {previewOpen ? (
        <DashboardDialog title={labels.preview} onClose={() => setPreviewOpen(false)} wide preview>
          <div className="datox-react-dashboard__dialog-actions" style={{ marginTop: 0, marginBottom: 16 }}>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--primary"
              type="button"
              onClick={() => {
                setPreviewOpen(false)
                openExport()
              }}
            >
              {labels.exportPdf}
            </button>
          </div>
          <div className="datox-react-dashboard__preview-shell">
            <div className="datox-react-dashboard__published-tabs datox-react-dashboard__preview-tabs">
              {(config.tabs || []).map((tab) => (
                <button
                  key={tab.id}
                  className={
                    tab.id === activeTab.id
                      ? "datox-react-dashboard__published-tab datox-react-dashboard__published-tab--active"
                      : "datox-react-dashboard__published-tab"
                  }
                  type="button"
                  onClick={() => setActiveTabId(tab.id)}
                >
                  {tab.name || labels.defaultTabName}
                </button>
              ))}
            </div>
            <div
              className="datox-react-dashboard__page-wrap"
              style={{ width: (config.canvas?.width || 1123) + 56, minWidth: (config.canvas?.width || 1123) + 56 }}
            >
              <div
                className={`datox-react-dashboard__page ${config.design?.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`}
                data-sheet-style={sheetStyle(config.design)}
                style={{
                  "--ds-accent": config.design?.accent || "#2f80ed",
                  "--ds-header-color": config.design?.headerColor || "#101828",
                  "--ds-heading-font": designFont(config.design?.headingFont),
                  "--ds-body-font": designFont(config.design?.bodyFont),
                  width: config.canvas?.width || 1123,
                  minHeight: dashboardKpiPageHeight(config.canvas?.height, widgets),
                }}
              >
                {widgets.map((widget) => (
                  <div
                    key={widget.id}
                    style={{
                      position: "absolute",
                      left: widget.layout.x,
                      top: widget.layout.y,
                      width: widget.layout.w,
                      height: dashboardWidgetHeight(widget),
                    }}
                  >
                    <WidgetFrame
                      preview
                      labels={labels}
                      filterWidgets={activeFilterWidgets}
                      widget={widget}
                      table={applyDashboardFilters(
                        widgetOutputTable(props.outputTables, widget),
                        widget,
                        activeFilterWidgets,
                      )}
                      accent={config.design?.accent || "#2f80ed"}
                      design={config.design || {}}
                      selected={false}
                      menuOpen={false}
                      onToggleMenu={() => {}}
                      onRename={() => {}}
                      onEditData={() => {}}
                      onEditDesign={() => {}}
                      onExportTable={() => {}}
                      onFullView={() => {}}
                      onFilterValueToggle={(value) => toggleFilterValue(widget, value)}
                      onDelete={() => {}}
                      onDuplicate={() => {}}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DashboardDialog>
      ) : null}
      {cellColorRulesOpen &&
      cellColorRuleDraft &&
      editorWidget &&
      (editorWidget.type === "table" || editorWidget.type === "freeTable") ? (
        <DashboardDialog
          title={
            editorCellColorRules.some((rule) => rule.id === cellColorRuleDraft.id)
              ? "Edit color rule"
              : "Add color rule"
          }
          onClose={closeEditorCellColorRule}
        >
          <form
            className="datox-react-dashboard__cell-rules-modal-form"
            onSubmit={(event) => {
              event.preventDefault()
              saveEditorCellColorRule()
            }}
          >
            <label className="datox-react-dashboard__dialog-field">
              <span>Column</span>
              <select
                className="datox-react-dashboard__select datox-react-dashboard__cell-rule-control"
                value={cellColorRuleDraft.column || ""}
                onChange={(event) => updateEditorCellColorRule({ column: event.currentTarget.value })}
              >
                <option value="">Select column</option>
                {editorWidgetVisibleColumns.map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </label>
            <label className="datox-react-dashboard__dialog-field">
              <span>Condition</span>
              <select
                className="datox-react-dashboard__select datox-react-dashboard__cell-rule-control"
                value={cellColorRuleDraft.operator || "equals"}
                onChange={(event) => updateEditorCellColorRule({ operator: event.currentTarget.value })}
              >
                {CELL_COLOR_OPERATOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="datox-react-dashboard__dialog-field">
              <span>Value</span>
              <input
                className="datox-react-dashboard__input datox-react-dashboard__cell-rule-control"
                value={cellColorRuleDraft.value ?? ""}
                onChange={(event) => updateEditorCellColorRule({ value: event.currentTarget.value })}
              />
            </label>
            <label className="datox-react-dashboard__toggle-row datox-react-dashboard__cell-rules-toggle-row">
              <input
                type="checkbox"
                checked={cellColorRuleDraft.highlight_row === true}
                onChange={(event) => updateEditorCellColorRule({ highlight_row: event.currentTarget.checked })}
              />
              <span>Highlight entire row</span>
            </label>
            <div className="datox-react-dashboard__cell-rules-helper datox-react-dashboard__muted-text">
              Off colors only the selected column. First matching rule wins for each cell.
            </div>
            <label className="datox-react-dashboard__dialog-field">
              <span>{cellColorRuleDraft.highlight_row === true ? "Row color" : "Cell color"}</span>
              <input
                value={cellColorRuleDraft.color || "#dbeafe"}
                type="color"
                onChange={(event) => updateEditorCellColorRule({ color: event.currentTarget.value })}
                aria-label="Rule color"
                className="datox-react-dashboard__cell-rule-color-input"
              />
            </label>
            <div className="datox-react-dashboard__dialog-actions datox-react-dashboard__cell-rules-modal-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={closeEditorCellColorRule}>
                Cancel
              </button>
              <button className="datox-react-dashboard__button datox-react-dashboard__button--primary" type="submit">
                Save rule
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {deleteDashboardOpen ? (
        <DashboardDialog title={labels.deleteDashboard} onClose={() => setDeleteDashboardOpen(false)}>
          <p className="datox-react-dashboard__dialog-copy">{labels.deleteDashboardConfirm}</p>
          <div className="datox-react-dashboard__dialog-actions">
            <button
              className="datox-react-dashboard__button"
              type="button"
              onClick={() => setDeleteDashboardOpen(false)}
            >
              {labels.cancel}
            </button>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--danger-solid"
              type="button"
              onClick={() => {
                setDeleteDashboardOpen(false)
                props.onDeleteDashboard?.()
              }}
            >
              {labels.deleteDashboard}
            </button>
          </div>
        </DashboardDialog>
      ) : null}
      {publishedLink ? (
        <DashboardDialog
          title={labels.publishDashboard}
          onClose={() => {
            setPublishedLink("")
            setPublishedLinkCopied(false)
          }}
        >
          <p className="datox-react-dashboard__dialog-copy">{labels.publishDashboardCopied}</p>
          <div className="datox-react-dashboard__copy-link-row">
            <input className="datox-react-dashboard__input" value={publishedLink} readOnly />
            <button
              className={
                publishedLinkCopied
                  ? "datox-react-dashboard__toolbar-button datox-react-dashboard__toolbar-button--success"
                  : "datox-react-dashboard__toolbar-button"
              }
              type="button"
              title={publishedLinkCopied ? labels.copied : labels.copyLink}
              onClick={copyPublishedLink}
            >
              {publishedLinkCopied ? "✓" : "⧉"}
            </button>
          </div>
          <div className="datox-react-dashboard__dialog-actions">
            <button
              className="datox-react-dashboard__button"
              type="button"
              onClick={() => window.open(publishedLink, "_blank", "noopener,noreferrer")}
            >
              {labels.openLink}
            </button>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--primary"
              type="button"
              onClick={() => {
                setPublishedLink("")
                setPublishedLinkCopied(false)
              }}
            >
              {labels.done}
            </button>
          </div>
        </DashboardDialog>
      ) : null}
      {shareAccessOpen ? (
        <DashboardDialog title={labels.shareAccess} onClose={() => setShareAccessOpen(false)} reviewer>
          <form onSubmit={submitShareAccess} className="datox-react-dashboard__share-form">
            <div className="datox-react-dashboard__reviewer-table">
              <div className="datox-react-dashboard__reviewer-row datox-react-dashboard__reviewer-row--head">
                <span />
                <strong>{labels.reviewerName}</strong>
                <strong>{labels.reviewerEmail}</strong>
                <strong>{labels.client}</strong>
                <strong>{labels.reports}</strong>
                <strong>{labels.status}</strong>
                <span />
              </div>
              {shareReviewers.map((reviewer) => (
                <div key={reviewer.id} className="datox-react-dashboard__reviewer-row">
                  <input
                    type="checkbox"
                    checked={reviewer.selected !== false}
                    onChange={(event) => updateShareReviewer(reviewer.id, { selected: event.currentTarget.checked })}
                  />
                  <input
                    aria-label={labels.reviewerName}
                    value={reviewer.name}
                    onChange={(event) => updateShareReviewer(reviewer.id, { name: event.currentTarget.value })}
                  />
                  <input
                    aria-label={labels.reviewerEmail}
                    value={reviewer.email}
                    placeholder={labels.reviewersPlaceholder}
                    onChange={(event) => updateShareReviewerEmail(reviewer.id, event.currentTarget.value)}
                  />
                  <select
                    aria-label={labels.client}
                    className="datox-react-dashboard__select"
                    value={reviewer.clientValue || ""}
                    disabled={!clientColumn || clientValues.length === 0}
                    onChange={(event) => updateShareReviewer(reviewer.id, { clientValue: event.currentTarget.value })}
                  >
                    <option value="">{labels.allClients}</option>
                    {clientValues.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>{config.title || labels.dashboardTitle}</span>
                  <span>{reviewer.status || labels.notSent}</span>
                  <button type="button" onClick={() => removeShareReviewer(reviewer)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--outline-primary"
              type="button"
              onClick={addShareReviewer}
            >
              + {labels.addAnotherReviewer}
            </button>
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={() => setShareAccessOpen(false)}>
                {labels.cancel}
              </button>
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="submit"
                disabled={
                  shareAccessSending ||
                  hasDuplicateReviewerEmails(shareReviewers) ||
                  !shareReviewers.some((reviewer) => reviewer.selected !== false && reviewer.email.trim())
                }
              >
                {shareAccessSending ? labels.sendingShareAccess : labels.sendShareAccess}
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {sourceExplanation ? (
        <DashboardDialog title={labels.source} onClose={closeSourceExplanation} wide>
          <div className="datox-react-dashboard__source-explanation">
            {sourceExplanation.loading ? (
              <div className="datox-react-dashboard__source-loading" role="status" aria-live="polite">
                <span className="datox-react-dashboard__source-spinner" aria-hidden="true" />
                <strong>{labels.loading}</strong>
                <div className="datox-react-dashboard__source-loading-lines" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="datox-react-dashboard__source-loading-card" aria-hidden="true">
                  <span />
                  <span />
                </div>
              </div>
            ) : null}

            {sourceExplanation.data ? (
              <>
                <h3>{sourceExplanation.data.title}</h3>
                <DashboardMarkdown
                  className="datox-react-dashboard__source-summary"
                  text={sourceExplanation.data.summary_markdown}
                />
                <dl className="datox-react-dashboard__source-metadata">
                  <dt>{labels.outputTable}</dt>
                  <dd>{sourceExplanation.data.dataset_name}</dd>
                </dl>
                {sourceExplanation.data.formula_markdown ? (
                  <DashboardMarkdown
                    className="datox-react-dashboard__source-formula"
                    text={sourceExplanation.data.formula_markdown}
                  />
                ) : null}
                <SourceFilterChips
                  labels={labels}
                  filters={sourceExplanation.data.filters}
                  pageFilters={(() => {
                    const explained = widgets.find((widget) => widget.id === sourceExplanation.request?.widgetID)
                    return explained ? dashboardFiltersFor(explained, widgetOutputTable(props.outputTables, explained), activeFilterWidgets) : []
                  })()}
                />
                {sourceExplanation.data.warnings?.length ? (
                  <ul className="datox-react-dashboard__source-warnings" role="note">
                    {sourceExplanation.data.warnings.map((warning, index) => (
                      <li key={index}>{String(warning)}</li>
                    ))}
                  </ul>
                ) : null}
                {sourceExplanation.data.sources.map((source, index) => {
                  const columns = sourceRowColumns(source.sample_rows)
                  return (
                    <article
                      className="datox-react-dashboard__source-card"
                      key={`${source.source_file_id || "unknown"}:${source.source_extracted_table || index}`}
                    >
                      <strong>{source.source_file_name || source.source_extracted_table || labels.notAssessed}</strong>
                      {source.source_file_name && source.source_extracted_table ? (
                        <div>{source.source_extracted_table}</div>
                      ) : null}
                      {source.source_file_id && props.onOpenEvidence ? (
                        <button
                          className="datox-react-dashboard__button datox-react-dashboard__source-open"
                          type="button"
                          onClick={() =>
                            props.onOpenEvidence({
                              fileID: source.source_file_id,
                              fileName: source.source_file_name,
                              extractedTable: source.source_extracted_table,
                              evidence: source.evidence || [],
                            })
                          }
                        >
                          {labels.openFile}
                        </button>
                      ) : null}
                      {columns.length ? (
                        <div className="datox-react-dashboard__source-table-wrap">
                          <table
                            className="datox-react-dashboard__source-table"
                            style={{ minWidth: `${Math.max(720, columns.length * 90)}px` }}
                          >
                            <thead>
                              <tr>
                                {columns.map((column) => (
                                  <th key={column}>{column}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {source.sample_rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  {columns.map((column) => (
                                    <td key={column}>{formatSourceValue(row[column])}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </article>
                  )
                })}
              </>
            ) : null}
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={closeSourceExplanation}>
                {labels.done}
              </button>
            </div>
          </div>
        </DashboardDialog>
      ) : null}
        {exportSelection ? (
          <DashboardDialog title={labels.exportPdf} variant="export" closeLabel={labels.done} closeDisabled={exportingPdf} onClose={closeExport}>
            <div className="report-export-body">
              <div className="report-export-document">
                <span className="report-export-document-icon" aria-hidden="true"><TbFileExport /></span>
                <div><strong>{config.title || labels.dashboardTitle}</strong><p>{labels.exportIntro}</p></div>
              </div>
              <div className="report-export-selection-header">
                <div><h3>{labels.exportPages}</h3><span role="status">{labels.exportSelectionSummary(exportSelection.length, config.tabs.length)}</span></div>
                <div className="report-export-selection-actions">
                  <button type="button" disabled={exportingPdf || exportSelection.length === config.tabs.length} onClick={() => setExportSelection(config.tabs.map((tab) => tab.id))}>{labels.exportSelectAll}</button>
                  <button type="button" disabled={exportingPdf || !exportSelection.length} onClick={() => setExportSelection([])}>{labels.exportClearSelection}</button>
                </div>
              </div>
              <fieldset className="report-export-pages" disabled={exportingPdf} aria-label={labels.exportPages}>
                {config.tabs.map((tab, index) => (
                  <label key={tab.id} className="report-export-page" data-selected={exportSelection.includes(tab.id)}>
                    <input type="checkbox" checked={exportSelection.includes(tab.id)} onChange={(event) => {
                      const checked = event.currentTarget.checked
                      setExportSelection((selected) => config.tabs.filter((page) => page.id === tab.id ? checked : selected.includes(page.id)).map((page) => page.id))
                    }} />
                    {v2 ? pageThumbs?.get(tab.id) : <span className="report-export-page-number" aria-hidden="true">{index + 1}</span>}
                    <span className="report-export-page-info"><strong>{tab.name || labels.defaultTabName}</strong><small>{labels.exportPageNumber(index + 1)}</small></span>
                  </label>
                ))}
              </fieldset>
              {serverPdf && pdfSettings ? <fieldset className="report-export-pdf-controls" disabled={exportingPdf}>
                <label>{labels.pdfOrientation}<select value={pdfSettings.orientation} onChange={(event) => setPdfSettings((value) => ({ ...value, orientation: event.target.value }))}><option value="landscape">{labels.landscape}</option><option value="portrait">{labels.portrait}</option></select></label>
                <label>{labels.pdfMargin}<select value={pdfSettings.margin} onChange={(event) => setPdfSettings((value) => ({ ...value, margin: Number(event.target.value) }))}>{[0, 5, 10, 15, 20].map((value) => <option key={value} value={value}>{value} mm</option>)}</select></label>
                <label>{labels.pdfTextSize}<select value={pdfSettings.fontSize} onChange={(event) => setPdfSettings((value) => ({ ...value, fontSize: event.target.value }))}><option value="compact">{labels.pdfCompact}</option><option value="medium">{labels.pdfNormal}</option><option value="large">{labels.pdfLarge}</option></select></label>
                <label>{String(labels.pdfScale || "").replace("{value}", pdfSettings.scale)}<input type="range" min="50" max="100" step="5" value={pdfSettings.scale} onChange={(event) => setPdfSettings((value) => ({ ...value, scale: Number(event.target.value) }))} /></label>
                <label>{labels.pdfAccent}<input type="color" value={pdfSettings.accent} onChange={(event) => setPdfSettings((value) => ({ ...value, accent: event.target.value }))} /></label>
                <label className="report-export-pdf-check"><input type="checkbox" checked={pdfSettings.gray} onChange={(event) => setPdfSettings((value) => ({ ...value, gray: event.target.checked }))} />{labels.pdfGray}</label>
                <label className="report-export-pdf-check"><input type="checkbox" checked={pdfSettings.allRows} onChange={(event) => setPdfSettings((value) => ({ ...value, allRows: event.target.checked }))} />{labels.pdfAllRows}</label>
                {pdfSettings.allRows ? <label>{labels.pdfTableDensity}<select value={pdfSettings.density} onChange={(event) => setPdfSettings((value) => ({ ...value, density: event.target.value }))}><option value="original">{labels.pdfOriginal}</option><option value="compact">{labels.pdfCompact}</option><option value="normal">{labels.pdfNormal}</option><option value="large">{labels.pdfLarge}</option></select></label> : null}
                {pdfWidget ? <details className="report-export-pdf-widget-controls"><summary>{labels.pdfWidgetAdjustments}</summary>
                  <p>{labels.pdfWidgetHelp}</p>
                  <label>{labels.pdfWidgetSelect}<select value={pdfWidget.key} onChange={(event) => setPdfWidgetID(event.target.value)}>{pdfWidgets.map(({ key, tab, widget }) => <option key={key} value={key}>{tab.name}: {widget.title || labels.pdfWidgetSelect}</option>)}</select></label>
                  <label>{String(labels.pdfWidgetTitleSize || "").replace("{value}", pdfWidgetSettings.titleSize ?? 16)}<input type="range" min="8" max="64" value={pdfWidgetSettings.titleSize ?? 16} onChange={(event) => setPdfWidgetSettings(pdfWidget.tab.id, pdfWidget.widget.id, { titleSize: Number(event.target.value) })} /></label>
                  {!["header", "title", "kpi"].includes(pdfWidget.widget.type) ? <label>{String(labels.pdfWidgetContentSize || "").replace("{value}", pdfWidgetSettings.contentSize ?? 13)}<input type="range" min="8" max="32" value={pdfWidgetSettings.contentSize ?? 13} onChange={(event) => setPdfWidgetSettings(pdfWidget.tab.id, pdfWidget.widget.id, { contentSize: Number(event.target.value) })} /></label> : null}
                  {pdfWidget.widget.type === "kpi" ? <label>{String(labels.pdfKpiScale || "").replace("{value}", pdfWidgetSettings.valueScale ?? 100)}<input type="range" min="25" max="100" step="5" value={pdfWidgetSettings.valueScale ?? 100} onChange={(event) => setPdfWidgetSettings(pdfWidget.tab.id, pdfWidget.widget.id, { valueScale: Number(event.target.value) })} /></label> : null}
                  <button type="button" onClick={() => setPdfWidgetSettings(pdfWidget.tab.id, pdfWidget.widget.id, null)}>{labels.pdfWidgetReset}</button>
                </details> : null}
              </fieldset> : null}
              {serverPdf && pdfHtml ? <iframe ref={pdfPreviewRef} className="report-export-pdf-preview" title={labels.pdfPreview} sandbox="allow-scripts allow-same-origin" srcDoc={pdfHtml} onLoad={bindPdfPreview} /> : null}
              {!exportSelection.length ? <p className="report-export-empty" role="status">{labels.exportEmptySelection}</p> : null}
              <details className="report-export-details"><summary>{labels.exportDetails}</summary><p>{labels.exportSnapshotHint}</p></details>
              {exportError ? <p className="report-export-error" role="alert">{labels.exportFailed}</p> : null}
              {exportingPdf ? <div className="report-export-progress" role="status"><TbLoader2 className="report-export-spinner" aria-hidden="true" /><div><strong>{labels.exportPreparing}</strong><p>{labels.exportPreparingHint}</p></div></div> : null}
            </div>
            <div className="datox-react-dashboard__dialog-actions report-export-footer">
              <button className="datox-react-dashboard__button" type="button" disabled={exportingPdf} onClick={closeExport}>{labels.cancel}</button>
              <button className="datox-react-dashboard__button datox-react-dashboard__button--primary" type="button" disabled={props.readOnly || props.saving || exportingPdf || !exportSelection.length || (serverPdf && !pdfHtml)} onClick={() => exportPdf(exportSelection)}>
                {exportingPdf ? <TbLoader2 className="report-export-spinner" aria-hidden="true" /> : <TbFileExport aria-hidden="true" />}
                {exportingPdf ? labels.exportPreparing : labels.exportPdf}
              </button>
            </div>
          </DashboardDialog>
        ) : null}
        {exportSnapshot ? (
          <div ref={exportRef} className="datox-react-dashboard__export-root" aria-hidden="true">
            {(exportSnapshot.config.tabs || []).filter((tab) => exportSnapshot.pages.includes(tab.id)).flatMap((tab) => {
              const filters = tab.widgets.filter((widget) => widget.type === "filter" && widget.data?.selectedValues?.length)
              const tables = Object.fromEntries(tab.widgets.map((widget) => {
                const dataset = exportSnapshot.datasets.find((dataset) => dataset.id === widget.data?.dataset_id)
                const table = dataset ? { datasetID: dataset.id, headers: dataset.columns.map((column) => typeof column === "string" ? column : column.name), rows: dataset.rows } : undefined
                return [widget.id, applyWidgetTableSettings(applyDashboardFilters(table, widget, filters), widget.data)]
              }))
              const fitted = gridMode
                ? {
                    ...tab,
                    // HUNT R8: the PDF lays the page out as Preview does (printPageLayout, the same needs and row stops).
                    widgets: printPageLayout(tab.widgets, exportSnapshot.config.canvas, { needs: contentNeeds, stops: contentStops }),
                  }
                : tab
              return paginateReportTab(fitted, tables, exportSnapshot.config.canvas, !!props.studio, gridMode ? contentStops : undefined)
            }).map((tab) => {
              const design = exportSnapshot.config.design || {}
              return <div key={tab.id} className={`datox-react-dashboard__page datox-react-dashboard__export-page ${design.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`} data-sheet-style={sheetStyle(design)} style={{ "--ds-accent": design.accent || "#2f80ed", "--ds-header-color": design.headerColor || "#101828", "--ds-heading-font": designFont(design.headingFont), "--ds-body-font": designFont(design.bodyFont), width: exportSnapshot.config.canvas?.width || 1123, minHeight: exportSnapshot.config.canvas?.height || 794 }}>
                {tab.widgets.map((widget) => <div key={widget.id} style={{ position: "absolute", left: widget.layout.x, top: widget.layout.y, width: widget.layout.w, height: dashboardWidgetHeight(widget) }}><WidgetFrame preview exporting labels={labels} widget={widget} table={tab.tables[widget.id]} accent={design.accent || "#2f80ed"} design={design} selected={false} menuOpen={false} onToggleMenu={() => {}} onRename={() => {}} onEditData={() => {}} onEditDesign={() => {}} onExportTable={() => {}} onFullView={() => {}} onFilterValueToggle={() => {}} onDelete={() => {}} onDuplicate={() => {}} /></div>)}
                <footer style={{ position: "absolute", bottom: 8, left: 32, fontSize: 10 }}>{exportSnapshot.draft ? `${labels.exportDraft} · ` : ""}{exportSnapshot.scopeLabel || ""}</footer>
              </div>
            })}
          </div>
        ) : null}
    </div>
  )
}

function DashboardDialog(props) {
  const root = useRef(null)
  useEffect(() => {
    const previous = document.activeElement
    root.current?.querySelector("input, select, textarea, button")?.focus()
    return () => { if (previous instanceof HTMLElement && previous.isConnected) previous.focus() }
  }, [])
  return (
    <div ref={root} data-variant={props.variant} className={props.preview ? "datox-react-dashboard__dialog-backdrop datox-react-dashboard__dialog-backdrop--preview" : "datox-react-dashboard__dialog-backdrop"} role="presentation" onKeyDown={(event) => { trapReportDialog(event); if (event.key === "Escape") { event.stopPropagation(); if (!props.closeDisabled) props.onClose() } }}>
      <div data-variant={props.variant} className={props.preview ? "datox-react-dashboard__dialog datox-react-dashboard__dialog--wide datox-react-dashboard__dialog--preview" : props.reviewer ? "datox-react-dashboard__dialog datox-react-dashboard__dialog--reviewer" : props.wide ? "datox-react-dashboard__dialog datox-react-dashboard__dialog--wide" : "datox-react-dashboard__dialog"} style={props.style} role="dialog" aria-modal="true" aria-label={props.title}>
        <div className="datox-react-dashboard__dialog-header"><h2>{props.title}</h2><button type="button" aria-label={props.closeLabel} disabled={props.closeDisabled} onClick={props.onClose}>×</button></div>
        {props.children}
      </div>
    </div>
  )
}

function ColorSwatches(props) {
  return (
    <div role="group" aria-label={props.label}>
      <div className="datox-react-dashboard__field-label">{props.label}</div>
      <div className="datox-react-dashboard__swatches">
        {/* The current colour is marked whatever its case ("#2F80ED" is "#2f80ed"; UX research E25). */}
        {props.colors.map((color) => { const current = String(props.value || "").toLowerCase() === color.toLowerCase(); return <button key={color} aria-label={color} title={color} aria-pressed={current} className={current ? "datox-react-dashboard__swatch datox-react-dashboard__swatch--active" : "datox-react-dashboard__swatch"} style={{ background: color }} type="button" onClick={() => props.onChange(color)} /> })}
        <input type="color" aria-label={props.label} value={props.value} onChange={(event) => props.onChange(event.currentTarget.value)} />
      </div>
    </div>
  )
}

function SegmentedControl(props) {
  return (
    <div>
      <div className="datox-react-dashboard__field-label">{props.label}</div>
      <div className="datox-react-dashboard__mini-segmented">
        {props.options.map((option) => (
          <button
            key={option.value}
            className={
              props.value === option.value
                ? "datox-react-dashboard__mini-segment datox-react-dashboard__mini-segment--active"
                : "datox-react-dashboard__mini-segment"
            }
            type="button"
            onClick={() => props.onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// Multi-series settings (summer Modal :84–107, backlog #22): side by side / stacked / 100%, and per series how it draws
// (a bar chart's series as bars or a line; a line chart's as a line or an area) and whether it reads on the right axis.
// Only the style fields of series that differ from the default are written.
export function SeriesSettings(props) {
  const labels = props.labels
  const style = props.style || {}
  const mode = chartStackMode(style)
  const list = (key) => (Array.isArray(style[key]) ? style[key] : [])
  const toggle = (key, measure, on) => {
    const next = list(key).filter((item) => item !== measure && props.measures.includes(item))
    return { [key]: on ? [...next, measure] : next.length ? next : undefined }
  }
  const drawKey = props.type === "bar" ? "lineMeasures" : "areaMeasures"
  const drawOptions =
    props.type === "bar"
      ? [
          { value: "", label: labels.seriesBars || "Bars" },
          { value: "line", label: labels.seriesLine || "Line" },
        ]
      : [
          { value: "", label: labels.seriesLine || "Line" },
          { value: "area", label: labels.seriesArea || "Area" },
        ]
  return (
    <div className="datox-react-dashboard__series-settings" data-stack-mode={mode}>
      <SegmentedControl
        label={labels.stackSeries}
        value={mode}
        options={[
          { value: "grouped", label: labels.stackGrouped || "Side by side" },
          { value: "stacked", label: labels.stackStacked || "Stacked" },
          { value: "percent", label: labels.stackPercent || "100%" },
        ]}
        onChange={(next) => props.onChange({ stackSeries: next !== "grouped", stackMode: next === "percent" ? "percent" : undefined })}
      />
      <div className="datox-react-dashboard__field-label">{labels.seriesSettings || "Series"}</div>
      {props.measures.map((measure) => (
        <div key={measure} className="datox-react-dashboard__series-row" data-measure={measure}>
          <span className="datox-react-dashboard__series-name" title={measure}>
            {props.displayNames?.[measure] || humanizeColumnName(measure) || measure}
          </span>
          <select
            aria-label={`${labels.seriesDraw || "Draw as"}: ${measure}`}
            value={list(drawKey).includes(measure) ? drawOptions[1].value : ""}
            onChange={(event) => props.onChange(toggle(drawKey, measure, !!event.currentTarget.value))}
          >
            {drawOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="datox-react-dashboard__series-axis" title={props.horizontal ? labels.seriesRightAxisVertical || "Only on a vertical chart" : undefined}>
            <input
              type="checkbox"
              disabled={props.horizontal || undefined}
              checked={list("secondaryMeasures").includes(measure)}
              onChange={(event) => props.onChange(toggle("secondaryMeasures", measure, event.currentTarget.checked))}
            />
            <span>{labels.seriesRightAxis || "Right axis"}</span>
          </label>
        </div>
      ))}
      {mode === "percent" ? (
        <p className="datox-react-dashboard__settings-help">{labels.stackPercentHelp || "Each bar shows every series as its share of the whole. Lines keep their own values on the right axis."}</p>
      ) : null}
    </div>
  )
}

// Explain (summer DashboardWidgetExplainModal, backlog #19): the filters behind the number as plain chips, the widget's
// own first ("Weight > 2"), then the page's filter widgets that apply to it ("Sector is one of Banks, Energy").
export function SourceFilterChips(props) {
  const options = { labels: props.labels, formatDate: formatDay }
  const own = explainFilterChips(props.filters, options)
  const page = explainFilterChips(props.pageFilters, options).filter((chip) => !own.includes(chip))
  if (!own.length && !page.length) return null
  return (
    <section className="datox-react-dashboard__source-filters">
      <h4>{props.labels.explainFilters || props.labels.filter}</h4>
      <ul className="datox-react-dashboard__source-chips">
        {own.map((chip) => (
          <li key={`own:${chip}`} className="datox-react-dashboard__source-chip">{chip}</li>
        ))}
        {page.map((chip) => (
          <li key={`page:${chip}`} className="datox-react-dashboard__source-chip" data-from="page" title={props.labels.explainPageFilter || "From a filter on this page"}>
            {chip}
          </li>
        ))}
      </ul>
    </section>
  )
}

function ToggleRow(props) {
  return (
    <label className="datox-react-dashboard__toggle-row">
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled || undefined}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
      />
      <span>{props.label}</span>
    </label>
  )
}

// §75, review #13: a page's thumbnail in Export PDF. Its page (every widget, charts and their observers) mounts only
// once the thumbnail nears the list's view, then stays; until then the thumbnail is the blank sheet. A long report's
// pages below the fold cost nothing until scrolled to. Without IntersectionObserver the page mounts at once.
function PageThumb({ width, height, children }) {
  const ref = useRef(null)
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined")
  useEffect(() => {
    if (seen || !ref.current) return
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      setSeen(true)
    }, { root: ref.current.closest(".report-export-pages"), rootMargin: "200px 0px" })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [seen])
  return <span ref={ref} className="report-export-page-thumb" aria-hidden="true" inert="" style={{ width, height }}>{seen ? children : null}</span>
}

export function WidgetFrame(props) {
  // Bobur's `sourceDisabled` (while his agent applies a change) greys out Explain source.
  const analytics = useContext(DashboardAnalyticsContext)
  const explainSource = props.onExplainSource
  const header = props.widget.type === "header"
  const textOnly = props.widget.type === "text" || props.widget.type === "title"
  const kpiOnly = props.widget.type === "kpi"
  const pdfStyle = props.printing ? props.pdfStyle : undefined
  const effectiveAccent = props.widget.style?.accent || props.accent
  // Summer WidgetCard: radius, hairline, optional shadow and fonts come from the design tokens.
  const radius = designRadius(props.design, props.widget.style)
  const fontScale = designFontScale(props.design)
  const frameHeight = dashboardWidgetHeight(props.widget)
  // Smart title (display only): a short title line plus a muted subtitle; the saved title is untouched.
  const smartTitle = splitWidgetTitle(props.widget.title)
  const titleSize = pdfStyle?.titleSize
    ? undefined
    : kpiOnly
      ? kpiLabelSize(frameHeight, fontScale)
      : widgetTitleSize(props.widget.layout?.w, frameHeight, fontScale)
  const displayTable = applyWidgetTableSettings(
    props.table,
    isChartWidget(props.widget.type) ? { ...props.widget.data, columns: undefined } : props.widget.data,
  )
  // A ranked chart that shows only its top categories says so in the subtitle: "Top 12 of 15".
  const capCaption = useMemo(
    () =>
      isChartWidget(props.widget.type) && props.widget.type !== "scatter" && displayTable?.rows && !displayTable.loadError
        ? chartCapCaption(chartItems(displayTable.rows, chartWidgetData(props.widget), props.widget.type, props.labels), props.labels)
        : "",
    [displayTable, props.widget.type, props.widget.data, props.labels],
  )
  const titleBlock = <WidgetTitle smart={smartTitle} size={titleSize} subtitle={!kpiOnly} caption={capCaption} />
  // Summer kept the card chrome the same at every size; only the Compact font setting tightens it.
  // (Text blocks under 150px keep their smaller body text.)
  const compact = props.design?.fontSize === "compact" || (textOnly && props.widget.layout?.h < 150)
  const large = props.design?.fontSize === "large"
  // Content-fit heights (fitPageLayout): tables and text report the frame height their content
  // wants. Debounced, and only a change of more than 2px is stored, so it settles in one pass.
  const frameRef = useRef(null)
  const fit = useContext(DashboardFitContext)
  const measuresContent = !!fit.reportNeed && !props.printing && CONTENT_FIT_TYPES.includes(props.widget.type)
  useEffect(() => {
    const frame = frameRef.current
    if (!measuresContent || !frame || typeof ResizeObserver === "undefined") return undefined
    let timer
    const measure = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        const { need, stops } = widgetContentFit(frame)
        if (need > 0) fit.reportNeed(props.widget.id, need, stops)
      }, 120)
    }
    const observer = new ResizeObserver(measure)
    observer.observe(frame)
    const content = frame.querySelector("table, .datox-react-dashboard__editable-text > span")
    if (content) observer.observe(content)
    measure()
    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
  }, [measuresContent, props.widget.id, props.table?.rows?.length, props.table?.headers?.join("|"), props.widget.text, props.widget.data, props.design?.fontSize])
  // Empty states offer "Connect data" (this widget's Data settings) only while editing.
  const connect = !props.preview && !props.printing ? props.onEditData || null : null
  const empty = useMemo(() => ({ connect }), [connect])
  return (
    <WidgetEmptyContext.Provider value={empty}>
    <div
      ref={frameRef}
      data-widget-type={props.widget.type}
      data-widget-id={props.widget.id}
      data-sheet-style={sheetStyle(props.design)}
      data-header-tone={headerTone(props.design)}
      data-off-page={props.offPage || undefined}
      tabIndex={props.focusable ? 0 : undefined}
      aria-label={props.focusable ? props.widget.title : undefined}
      onFocus={props.onFocus}
      onClick={props.onSelect}
      className={`datox-react-dashboard__widget ${header ? "datox-react-dashboard__widget--header" : ""} ${compact ? "datox-react-dashboard__widget--compact" : ""} ${large ? "datox-react-dashboard__widget--large" : ""} ${props.design?.widgetShadows ? "datox-react-dashboard__widget--shadow" : ""} ${textOnly ? "datox-react-dashboard__widget--transparent" : ""} ${kpiOnly ? "datox-react-dashboard__widget--kpi" : ""} ${props.preview ? "datox-react-dashboard__widget--static" : ""} ${props.selected ? "datox-react-dashboard__widget--selected" : ""} ${pdfStyle?.wrapTitle ? "datox-react-dashboard__pdf-wrap-text" : ""} ${pdfStyle?.wrapTitle && !header && !textOnly ? "datox-react-dashboard__widget--pdf-wrap" : ""} ${pdfStyle?.contentSize ? "datox-react-dashboard__pdf-content-size" : ""} ${pdfStyle?.titleSize ? "datox-react-dashboard__pdf-title-size" : ""} ${pdfStyle?.wrapCells ? "datox-react-dashboard__pdf-wrap-cells" : ""}`}
      style={{
        ...designToCssVars(props.design, { ...props.widget.style, accent: effectiveAccent }),
        "--ds-pdf-content-size": pdfStyle?.contentSize ? `${pdfStyle.contentSize}px` : undefined,
        "--ds-pdf-title-size": pdfStyle?.titleSize ? `${pdfStyle.titleSize}px` : undefined,
        borderRadius: header ? 0 : radius,
      }}
    >
      {!props.preview && (header || textOnly) ? (
        <>
          <div
            className="datox-react-dashboard__text-drag-strip datox-react-dashboard__drag-handle"
            aria-hidden="true"
          />
          <div className="datox-react-dashboard__widget-actions datox-react-dashboard__widget-actions--header">
            <WidgetMenu
              labels={props.labels}
              open={props.menuOpen}
              onToggle={props.onToggleMenu}
              onRename={props.onRename}
              onEditData={props.onEditData}
              onEditDesign={props.onEditDesign}
              onExplainSource={explainSource}
              sourceDisabled={!!analytics?.sourceDisabled}
              onExportTable={props.onExportTable}
              onDuplicate={props.onDuplicate}
              onFullView={props.onFullView}
              onExplain={kpiOnly ? props.onExplain : null}
              onMoveToNextPage={props.onMoveToNextPage}
              onDelete={props.onDelete}
            />
          </div>
        </>
      ) : !props.preview ? (
        <div className="datox-react-dashboard__widget-head datox-react-dashboard__drag-handle">
          {titleBlock}
          <WidgetMenu
            labels={props.labels}
            open={props.menuOpen}
            onToggle={props.onToggleMenu}
            onRename={props.onRename}
            onEditData={props.onEditData}
            onEditDesign={props.onEditDesign}
            onExplainSource={explainSource}
            sourceDisabled={!!analytics?.sourceDisabled}
            onExportTable={props.onExportTable}
            onDuplicate={props.onDuplicate}
            onFullView={props.onFullView}
            onExplain={kpiOnly ? props.onExplain : null}
            onMoveToNextPage={props.onMoveToNextPage}
            onDelete={props.onDelete}
          />
        </div>
      ) : !header && !textOnly ? (
        <div className="datox-react-dashboard__widget-head">
          {titleBlock}
          {kpiOnly && props.onExplain ? (
            <button type="button" onClick={props.onExplain}>
              {props.labels.kpi.explain}
            </button>
          ) : null}
        </div>
      ) : null}
      <div
        className={
          header
            ? "datox-react-dashboard__header-content"
            : textOnly
              ? "datox-react-dashboard__widget-body datox-react-dashboard__widget-body--full"
              : kpiOnly
                ? "datox-react-dashboard__widget-body datox-react-dashboard__widget-body--kpi"
                : "datox-react-dashboard__widget-body"
        }
      >
        <WidgetContent
          labels={props.labels}
          exporting={props.exporting}
          printing={props.printing}
          pdfStyle={pdfStyle}
          widget={props.widget}
          table={displayTable}
          accent={effectiveAccent}
          paletteAccent={props.widget.style?.accent || props.design?.accent}
          sheet={sheetStyle(props.design)}
          sublabel={kpiOnly ? smartTitle.subtitle : undefined}
          headerColor={props.design?.headerColor}
          fontScale={fontScale}
          comparison={props.comparison}
          font={props.design?.bodyFont}
          inlineEditing={props.inlineEditing}
          onInlineEdit={props.preview ? undefined : props.onInlineEdit}
          onInlineEditClose={props.onInlineEditClose}
          onInlineTextChange={props.onInlineTextChange}
          onFilterValueToggle={props.onFilterValueToggle}
          onSortTable={props.preview ? undefined : props.onSortTable}
          sortableView={props.preview && !props.exporting}
          filterWidgets={props.filterWidgets}
        />
      </div>
    </div>
    </WidgetEmptyContext.Provider>
  )
}

// Summer WidgetHeader: the title wraps to two lines before the ellipsis and its size follows the
// widget; the full saved title stays available as a tooltip.
function WidgetTitle(props) {
  const { title, full } = props.smart
  const subtitle = [props.smart.subtitle, props.caption].filter(Boolean).join(" · ")
  return (
    <div className="datox-react-dashboard__widget-titles" title={full || undefined}>
      <div className="datox-react-dashboard__widget-title" style={props.size ? { fontSize: props.size } : undefined}>
        {title}
      </div>
      {props.subtitle && subtitle ? <div className="datox-react-dashboard__widget-subtitle">{subtitle}</div> : null}
    </div>
  )
}

function WidgetMenu(props) {
  return (
    <div className="datox-react-dashboard__widget-actions">
      <button
        className="datox-react-dashboard__widget-menu"
        type="button"
        title={props.labels.more}
        onClick={(event) => {
          event.stopPropagation()
          props.onToggle()
        }}
      >
        ...
      </button>
      {props.open ? (
        <div className="datox-react-dashboard__widget-dropdown">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onRename()
            }}
          >
            {props.labels.renameWidget}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onEditData()
            }}
          >
            {props.labels.editData}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onEditDesign()
            }}
          >
            {props.labels.editDesign}
          </button>
          {props.onExplainSource ? (
            <button
              type="button"
              disabled={!!props.sourceDisabled}
              onClick={(event) => {
                event.stopPropagation()
                props.onExplainSource()
              }}
            >
              {props.labels.source}
            </button>
          ) : null}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onExportTable()
            }}
          >
            {props.labels.exportTable}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onDuplicate()
            }}
          >
            {props.labels.duplicateWidget}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onFullView()
            }}
          >
            {props.labels.fullView}
          </button>
          {props.onExplain ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                props.onExplain()
              }}
            >
              {props.labels.kpi.explain}
            </button>
          ) : null}
          {props.onMoveToNextPage ? (
            // §33: a widget below the page edge; the V2 bar presses this.
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                props.onMoveToNextPage()
              }}
            >
              {props.labels.designer?.moveToNextPage}
            </button>
          ) : null}
          <button
            type="button"
            className="datox-react-dashboard__widget-dropdown-danger"
            onClick={(event) => {
              event.stopPropagation()
              props.onDelete()
            }}
          >
            {props.labels.deleteWidget}
          </button>
        </div>
      ) : null}
    </div>
  )
}

// The logo's mean ink luminance, read once per image from a small offscreen copy. A cross-origin
// image that cannot be read stays null (no plate).
const logoLuminanceCache = new Map()
function useLogoLuminance(src) {
  const [value, setValue] = useState(() => logoLuminanceCache.get(src) ?? null)
  useEffect(() => {
    if (!src || typeof Image === "undefined" || typeof document === "undefined") return undefined
    if (logoLuminanceCache.has(src)) {
      setValue(logoLuminanceCache.get(src))
      return undefined
    }
    let cancelled = false
    const image = new Image()
    if (!/^(data|blob):/.test(src)) image.crossOrigin = "anonymous"
    image.onload = () => {
      let luminance = null
      try {
        const scale = Math.min(1, 96 / Math.max(1, image.naturalWidth, image.naturalHeight))
        const canvas = document.createElement("canvas")
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
        const context = canvas.getContext("2d")
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        luminance = imageLuminance(context.getImageData(0, 0, canvas.width, canvas.height).data)
      } catch {
        luminance = null
      }
      logoLuminanceCache.set(src, luminance)
      if (!cancelled) setValue(luminance)
    }
    image.onerror = () => logoLuminanceCache.set(src, null)
    image.src = src
    return () => {
      cancelled = true
    }
  }, [src])
  return value
}

// The dashboard's as-of date: the real data date of a finished factsheet run (datox/v2/readiness dataDate), never
// the upload day.
export function dashboardDataDate(dashboards, dashboardID) {
  return dataDate((dashboards || []).find((dashboard) => dashboard.id === dashboardID))
}

// "Data to 29 Apr 2026" (dates.ts formatDay, the format of the top bar and the reports list).
export function headerDateText(date, labels) {
  const day = date ? formatDay(date) : ""
  return day ? (labels?.headerDataTo || "Data to {date}").replace("{date}", day) : ""
}

function HeaderTitle(props) {
  const band = props.widget.layout?.h || 84
  const dateText = props.widget.style?.showDate !== false && band >= HEADER_DATE_MIN_HEIGHT ? headerDateText(props.date, props.labels) : ""
  // The title fits the band less the date line (18px) when there is one.
  const size = Math.max(13, Math.min(Number(props.widget.style?.fontSize) || 28, band - 18 - (dateText ? 18 : 0)))
  return (
    <span className="datox-react-dashboard__header-titles">
      <span style={{ fontSize: size }}>{props.widget.title}</span>
      {dateText ? (
        <span className="datox-react-dashboard__header-date" data-header-date="">
          {dateText}
        </span>
      ) : null}
    </span>
  )
}

function HeaderLogo(props) {
  const plate = logoNeedsPlate(props.headerColor, useLogoLuminance(props.src))
  return (
    <span
      className={`datox-react-dashboard__header-logo ${plate ? "datox-react-dashboard__header-logo--plate" : ""}`}
      data-logo-plate={plate ? "" : undefined}
      style={{ height: props.height }}
    >
      <img src={props.src} alt={props.alt} />
    </span>
  )
}

function WidgetContent(props) {
  const kpi = useContext(DashboardKpiContext)
  if (props.widget.type === "header")
    return (
      <>
        {/* Summer header: the title fits the band (28px, never taller than the band less 18px) and the
            logo sits in the band with even padding; both are centred vertically. */}
        <HeaderTitle widget={props.widget} labels={props.labels} date={kpi?.dataDate} />
        {props.widget.style?.logoUrl ? (
          <HeaderLogo
            src={props.widget.style.logoUrl}
            alt={props.labels.logoAlt || ""}
            headerColor={props.headerColor}
            height={`calc(100% - ${Math.min(32, Math.round((props.widget.layout?.h || 84) * 0.3))}px)`}
          />
        ) : null}
      </>
    )
  if (props.widget.type === "text")
    return (
      <EditableTextContent
        multiline
        value={props.widget.text || ""}
        style={props.widget.style || {}}
        labels={props.labels}
        editing={props.inlineEditing}
        onEdit={props.onInlineEdit}
        onClose={props.onInlineEditClose}
        onChange={props.onInlineTextChange}
      />
    )
  if (props.widget.type === "title")
    return (
      <EditableTextContent
        value={props.widget.title || ""}
        style={{
          ...(props.widget.style || {}),
          // Summer title levels (h1-h4 = 40/34/28/22, weight 800) picked by the widget height.
          fontSize:
            props.widget.style?.fontSize ||
            (props.pdfStyle?.titleSize ? undefined : titleWidgetSize(props.widget.layout?.h, props.fontScale)),
          fontWeight: props.widget.style?.fontWeight || TITLE_WEIGHT,
        }}
        labels={props.labels}
        editing={props.inlineEditing}
        onEdit={props.onInlineEdit}
        onClose={props.onInlineEditClose}
        onChange={props.onInlineTextChange}
      />
    )
  if (props.widget.type === "kpi")
    return (
      <KpiWidget
        labels={props.labels}
        valueScale={props.pdfStyle?.valueScale}
        locale={kpi.locale}
        status={
          kpi.datasets[props.widget.data?.dataset_id]?.status ||
          (props.widget.data?.dataset_id ? (props.table ? "ready" : "error") : "missing")
        }
        table={props.table}
        data={props.widget.data || {}}
        style={kpiDisplayStyle(props.widget)}
        layout={{ ...props.widget.layout, h: dashboardWidgetHeight(props.widget) }}
        comparison={props.comparison}
        accent={props.widget.style?.accent || props.accent}
        sublabel={props.sublabel}
        printing={props.printing}
        fontScale={props.fontScale}
        font={props.font}
        widget={props.widget}
        filterWidgets={props.filterWidgets}
        sourceKey={kpi.sourceKeys?.[props.widget.data?.dataset_id] ?? kpi.sourceKey}
        onEvaluateKpi={kpi.onEvaluateKpi}
      />
    )
  if (props.widget.type === "freeTable" && props.widget.data?.freeStyle)
    return <FreeStyleWidget value={props.widget.data.freeStyle} style={props.widget.style || {}} fontScale={props.fontScale} />
  if (props.widget.type === "table" || props.widget.type === "freeTable")
    return (
      <TableWidget
        key={JSON.stringify([
          props.widget.id,
          props.widget.data?.dataset_id,
          props.widget.data?.filters,
          props.widget.data?.sort,
          props.widget.data?.rowLimit,
          props.widget.data?.pageSize,
          dashboardFiltersFor(props.widget, props.table, props.filterWidgets),
        ])}
        table={props.table}
        style={props.widget.style || {}}
        data={props.widget.data || {}}
        labels={props.labels}
        fontScale={props.fontScale}
        onSort={props.exporting ? undefined : props.onSortTable}
        viewSortable={!props.exporting && !props.onSortTable && !!props.sortableView}
        limit={props.exporting ? Infinity : undefined}
        missingLabel={props.widget.id?.startsWith("risk-table-") ? props.labels?.notAssessed : "—"}
      />
    )
  if (props.widget.type === "filter")
    return (
      <FilterWidget
        table={props.table}
        label={props.widget.data?.label}
        selectedValues={props.widget.data?.selectedValues || []}
        style={props.widget.style || {}}
        labels={props.labels}
        onToggle={props.onFilterValueToggle}
      />
    )
  return (
    <ChartWidget
      labels={props.labels}
      widget={props.widget}
      table={props.table}
      accent={props.accent}
      paletteAccent={props.paletteAccent}
      sheet={props.sheet}
      headerColor={props.headerColor}
      font={props.font}
      printing={props.printing}
      exporting={props.exporting}
      textSize={props.pdfStyle?.contentSize}
      fontScale={props.fontScale}
    />
  )
}

function EditableTextContent(props) {
  if (props.editing)
    return (
      <div className="datox-react-dashboard__inline-edit">
        <textarea
          autoFocus
          className="datox-react-dashboard__inline-edit-input"
          value={props.value}
          onChange={(event) => props.onChange(event.currentTarget.value)}
          onBlur={props.onClose}
          onKeyDown={(event) => {
            if (event.key === "Escape") props.onClose()
          }}
        />
      </div>
    )
  return (
    <div
      className={
        props.multiline
          ? "datox-react-dashboard__editable-text datox-react-dashboard__editable-text--body"
          : "datox-react-dashboard__editable-text datox-react-dashboard__editable-text--title"
      }
      onDoubleClick={props.onEdit}
      style={{
        alignItems: verticalAlignValue(props.style.verticalAlign),
        justifyContent: verticalAlignValue(props.style.verticalAlign),
        textAlign: props.style.textAlign || "left",
        color: props.style.textColor || "#101828",
        fontSize: props.style.fontSize ? `${props.style.fontSize}px` : undefined,
        fontWeight: props.style.fontWeight || undefined,
      }}
    >
      <span>{props.value}</span>
      {props.onEdit ? (
        <button
          type="button"
          title={props.labels.editText}
          aria-label={props.labels.editText}
          onClick={(event) => {
            event.stopPropagation()
            props.onEdit()
          }}
        >
          ✎
        </button>
      ) : null}
    </div>
  )
}

export function aggregateKpiValue(table, data) {
  if (!table) return 0
  const metric = data?.metric
  const aggregation = data?.aggregation || "sum"
  const values = metric ? numericValues(table, metric) : []
  if (aggregation === "count") return table.rows.length
  if (!values.length) return 0
  if (aggregation === "avg") return values.reduce((sum, value) => sum + value, 0) / values.length
  if (aggregation === "min") return Math.min(...values)
  if (aggregation === "max") return Math.max(...values)
  if (aggregation === "first") return values[0]
  if (aggregation === "last") return values[values.length - 1]
  return values.reduce((sum, value) => sum + value, 0)
}

function FreeStyleWidget(props) {
  const value = props.value
  const kpi = useContext(DashboardKpiContext)
  const locale = props.locale || kpi?.locale
  // Typed number / percent columns are explicit. A "text" column (the default) keeps strings
  // exactly as typed, but real JSON numbers sent by the agent get the inferred default format.
  const columns = useMemo(
    () =>
      value.headers.map((header, col) => {
        const type = value.columnTypes?.[col] || "text"
        if (type !== "text") return explicitColumnFormat({ format: type, decimals: 2 })
        const numbers = value.rows.map((row) => row[col]).filter((cell) => typeof cell === "number")
        return numbers.length ? inferColumnFormat(header, numbers) : null
      }),
    [value.headers, value.rows, value.columnTypes],
  )
  return (
    <div className="datox-react-dashboard__table-container">
      <table
        style={tableTokenStyle(props.fontScale)}
        className={`datox-react-dashboard__table ${props.style?.density === "compact" ? "datox-react-dashboard__table--compact" : props.style?.density === "large" ? "datox-react-dashboard__table--large" : ""} ${props.style?.stripedRows ? "datox-react-dashboard__table--striped" : ""} ${props.style?.headerFill === false || value.highlightHeaderRow === false ? "datox-react-dashboard__table--plain-header" : ""}`}
      >
        <colgroup>
          {value.showRowNumbers ? <col style={{ width: 40 }} /> : null}
          {value.headers.map((_, index) => (
            <col key={index} style={{ width: value.columnWidths?.[index] || 168 }} />
          ))}
        </colgroup>
        {props.style?.showHeader === false ? null : (
          <thead>
            <tr>
              {value.showRowNumbers ? <th /> : null}
              {value.headers.map((header, index) => (
                <th key={index} style={{ minWidth: value.columnWidths?.[index] || 168 }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {value.rows.map((row, index) => (
            <tr
              key={index}
              className={value.rowTypes?.[index] === "section" ? "datox-react-dashboard__section-row" : ""}
            >
              {value.showRowNumbers ? <td>{index + 1}</td> : null}
              {value.headers.map((header, col) => {
                const format = typeof row[col] === "number" || value.columnTypes?.[col] !== "text" ? columns[col] : null
                const numeric = isNumericFormat(format)
                const text = formatTableCell(row[col], format, locale)
                return (
                  <td
                    key={col}
                    className={numeric ? "datox-react-dashboard__cell--num" : undefined}
                    style={{ textAlign: value.cellAlign?.[`${index}:${col}`] || (numeric ? "right" : "left") }}
                  >
                    {text != null ? (
                      text
                    ) : numeric && value.rowTypes?.[index] !== "section" ? (
                      <span className="datox-react-dashboard__cell-empty">{TABLE_TOKENS.emptyMark}</span>
                    ) : null}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// useLayoutEffect on the client, useEffect in static (server) renders, which have no layout.
const useClientLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect

function TableWidget(props) {
  const [page, setPage] = useState(1)
  const viewport = useRef(null)
  const headers = props.table?.headers || []
  // Header-click sort ▲▼ (summer DynamicReportTable, backlog #16): in the editor the click saves `data.sort` (onSort);
  // in Preview and the published view it only re-orders what this reader sees (viewSort), nothing is saved.
  const [viewSort, setViewSort] = useState(null)
  // V2 only (FLAG-AUDIT.md): in his classic render a header click stays a plain click.
  const sortable = !!useContext(DashboardKpiContext).v2 && (!!props.onSort || !!props.viewSortable)
  const activeSort = props.onSort ? props.data?.sort : viewSort || props.data?.sort
  const baseRows = props.table?.rows || []
  const rows = useMemo(() => (props.onSort || !viewSort ? baseRows : sortRows(baseRows, viewSort)), [baseRows, viewSort, props.onSort])
  const pagination = !props.limit && props.data?.pagination !== false
  const size = Math.max(5, Math.min(1000, Number(props.data?.pageSize) || 20))
  const pages = Math.max(1, Math.ceil(rows.length / size))
  const current = Math.min(page, pages)
  const selectPage = (next) => {
    setPage(Math.max(1, Math.min(pages, next)))
    if (viewport.current) viewport.current.scrollTop = 0
  }
  const displayed = props.limit
    ? rows.slice(0, props.limit)
    : pagination
      ? rows.slice((current - 1) * size, current * size)
      : rows
  const summary = useMemo(
    () => (props.data?.showSummary ? tableSummary(rows, headers, props.data.summaryColumns) : null),
    [rows, headers, props.data?.showSummary, props.data?.summaryColumns],
  )
  const kpi = useContext(DashboardKpiContext)
  const locale = props.locale || kpi?.locale
  // Human headers and number formats by default; explicit columnLabels / columnFormats win.
  const columns = useMemo(
    () =>
      tableColumnModels(headers, rows, props.data || {}, {
        sampleRows: TABLE_TOKENS.inferenceSampleRows,
        longText: TABLE_TOKENS.longText,
      }),
    [headers, rows, props.data?.columnLabels, props.data?.columnFormats],
  )
  // Clamped text would hide words in a PDF, so exports (limit Infinity) print it in full.
  const clampLongText = props.limit !== Infinity
  // Column widths from the content (tableColumnWidths): short headers stay on one line, text wraps to
  // at most 2-3 lines. Needs the real width and fonts, so it starts after the first layout.
  const tableRef = useRef(null)
  const [fitBox, setFitBox] = useState(null)
  useEffect(() => {
    const container = viewport.current
    const table = tableRef.current
    if (!container || !table || typeof ResizeObserver === "undefined") return undefined
    const read = () => {
      const head = table.querySelector("th")
      const body = table.querySelector("td")
      const font = (element, fallback) => {
        if (!element) return fallback
        const style = getComputedStyle(element)
        return `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
      }
      // The token padding (both sides), not the computed one: a tight table overrides it below.
      const padding =
        2 * (container.closest(".datox-react-dashboard__widget--compact") ? TABLE_TOKENS.compactCellPaddingXPx : TABLE_TOKENS.cellPaddingXPx)
      // §75: a sheet style that keeps its card inset on a tight table's outer cells says how much (--dxt-tight-inset).
      const inset = parseFloat(getComputedStyle(container).getPropertyValue("--dxt-tight-inset")) || 0
      // §75: a sheet style that frames the table (Lesha) says how much wider than its columns the frame makes it
      // (--dxt-frame): the columns leave it that room, or the viewport cut off the frame's right edge.
      const frame = parseFloat(getComputedStyle(container).getPropertyValue("--dxt-frame")) || 0
      const next = { width: container.clientWidth - frame, head: font(head, "600 12px sans-serif"), body: font(body, "400 13px sans-serif"), padding, inset }
      setFitBox((current) =>
        current && current.width === next.width && current.head === next.head && current.body === next.body && current.padding === next.padding && current.inset === next.inset ? current : next,
      )
    }
    const observer = new ResizeObserver(read)
    observer.observe(container)
    read()
    // A web font that lands after the first measure (a sheet style's own face) changes every width: measure again.
    const fonts = typeof document !== "undefined" ? document.fonts : undefined
    fonts?.addEventListener?.("loadingdone", read)
    return () => {
      observer.disconnect()
      fonts?.removeEventListener?.("loadingdone", read)
    }
  }, [props.table])
  // Whole rows (round 6): a scrolling viewport ends on a row boundary, so no row is cut mid-height
  // and the scroll cue at the bottom edge only sits on the last row's bottom padding.
  useClientLayoutEffect(() => {
    const box = viewport.current
    const table = tableRef.current
    const container = box?.parentElement
    if (!box || !table || !container || typeof ResizeObserver === "undefined") return undefined
    let frame = 0
    const measure = () => {
      const bodyRows = [...(table.tBodies[0]?.rows || [])]
      const scrolls = box.scrollHeight > box.clientHeight + 1
      return {
        scrolls,
        height: wholeRowsHeight(
          bodyRows.map((row) => row.offsetTop + row.offsetHeight),
          box.clientHeight,
          table.tHead?.offsetHeight || 0,
        ),
      }
    }
    const snap = () => {
      frame = 0
      box.style.maxHeight = ""
      container.removeAttribute("data-squeeze")
      let { scrolls, height } = measure()
      // Not even one whole row fits (a dense page at the fit floor): single-line rows with compact
      // padding (the full text stays in title=) rather than a row cut mid-height.
      if (scrolls && height === undefined && table.tBodies[0]?.rows.length) {
        container.setAttribute("data-squeeze", "")
        ;({ height } = measure())
      }
      if (height !== undefined && height < box.clientHeight) box.style.maxHeight = `${height}px`
      box.toggleAttribute("data-whole-rows", height !== undefined)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(snap)
    }
    const observer = new ResizeObserver(schedule)
    observer.observe(container)
    observer.observe(table)
    snap()
    return () => {
      if (frame) cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [props.table, displayed])
  const columnWidths = useMemo(() => {
    if (!fitBox?.width || !columns.length) return undefined
    const context = typeof document !== "undefined" ? document.createElement("canvas").getContext?.("2d") : null
    if (!context) return undefined
    const measure = (text, kind) => {
      context.font = kind === "head" ? fitBox.head : fitBox.body
      return context.measureText(String(text)).width
    }
    const texts = columns.map((column) => displayed.map((row) => formatTableCell(row[column.key], column.format, locale)))
    return tableColumnWidths(columns, texts, fitBox.width, measure, {
      padding: fitBox.padding,
      tightPadding: 2 * TABLE_TOKENS.tightCellPaddingXPx,
      tightInset: fitBox.inset,
      longMinPx: TABLE_TOKENS.longText.minWidthPx,
      longMaxPx: TABLE_TOKENS.longText.maxWidthPx,
    })
  }, [fitBox, columns, displayed, locale])
  const cellClass = (column) =>
    [
      column.numeric ? "datox-react-dashboard__cell--num" : "",
      column.nowrap ? "datox-react-dashboard__cell--nowrap" : "",
      column.long ? "datox-react-dashboard__cell--long" : "",
    ]
      .filter(Boolean)
      .join(" ") || undefined
  const emptyCell = <span className="datox-react-dashboard__cell-empty">{props.missingLabel || TABLE_TOKENS.emptyMark}</span>
  const densityClass =
    props.style?.density === "compact"
      ? "datox-react-dashboard__table--compact"
      : props.style?.density === "large"
        ? "datox-react-dashboard__table--large"
        : ""
  // Ours live in `style.cellColorRules`, Bobur's in `data.cellColorRules` (his inspector and agent): read both.
  const cellColorRules = [
    ...(Array.isArray(props.style?.cellColorRules) ? props.style.cellColorRules : []),
    ...(Array.isArray(props.data?.cellColorRules) ? props.data.cellColorRules : []),
  ]
  // Bobur's header colour override (style.tableHeaderColor → --ds-table-header, read by the stylesheet).
  const headerColorStyle = /^#[0-9a-f]{6}$/i.test(props.style?.tableHeaderColor || "")
    ? { "--ds-table-header": props.style.tableHeaderColor, "--ds-table-header-text": readableTextOn(props.style.tableHeaderColor), "--ds-table-header-tint": tintHex(props.style.tableHeaderColor, HEADER_TINT) }
    : {}
  const visibleColumnKeys = useMemo(() => new Set(headers), [headers])

  const getCellConditionalStyle = (row, columnKey) => {
    const matchingRule = cellColorRules.find((rule) => {
      if (!rule?.column || !visibleColumnKeys.has(rule.column)) return false
      if (rule.highlight_row !== true && rule.column !== columnKey) return false
      const cellValue = row[rule.column]
      const operator = rule.operator || "equals"
      const expected = rule.value ?? ""
      const cellNumber = parseComparableNumber(cellValue)
      const expectedNumber = parseComparableNumber(expected)

      if (["gt", "gte", "lt", "lte"].includes(operator)) {
        if (cellNumber == null || expectedNumber == null) return false
        if (operator === "gt") return cellNumber > expectedNumber
        if (operator === "gte") return cellNumber >= expectedNumber
        if (operator === "lt") return cellNumber < expectedNumber
        return cellNumber <= expectedNumber
      }

      const left = String(cellValue ?? "")
        .trim()
        .toLowerCase()
      const right = String(expected ?? "")
        .trim()
        .toLowerCase()
      if (operator === "contains") return right ? left.includes(right) : false
      if (operator === "not_equals") return left !== right
      return left === right
    })

    // A dark rule colour gets light text (summer WCAG pick), a light one keeps the dark cell text.
    return matchingRule?.color
      ? { background: matchingRule.color, color: readableTextOn(matchingRule.color, { dark: "#101828" }) }
      : null
  }

  if (!props.table || props.table.loadError)
    return props.labels ? <WidgetDatasetState table={props.table} labels={props.labels} datasetID={props.data?.dataset_id} /> : null
  return (
    <div
      className="datox-react-dashboard__table-container datox-react-dashboard__table-container--viewport"
      data-paged-table={pagination ? "" : undefined}
    >
      <div className="datox-react-dashboard__table-viewport" ref={viewport}>
        <table
          ref={tableRef}
          style={
            columnWidths
              ? {
                  ...tableTokenStyle(props.fontScale),
                  ...(columnWidths.padding < fitBox.padding
                    ? {
                        "--dxt-cell-padding": `9px ${TABLE_TOKENS.tightCellPaddingXPx}px`,
                        "--dxt-compact-cell-padding": `5px ${TABLE_TOKENS.tightCellPaddingXPx}px`,
                        "--dxt-header-padding": `8px ${TABLE_TOKENS.tightCellPaddingXPx}px`,
                      }
                    : {}),
                  tableLayout: "fixed",
                  width: columnWidths.reduce((sum, width) => sum + width, 0),
                  ...headerColorStyle,
                }
              : { ...tableTokenStyle(props.fontScale), ...headerColorStyle }
          }
          data-tight={columnWidths && fitBox && columnWidths.padding < fitBox.padding ? "" : undefined}
          className={`datox-react-dashboard__table ${columnWidths ? "datox-react-dashboard__table--fitted" : ""} ${densityClass} ${props.style?.stripedRows ? "datox-react-dashboard__table--striped" : ""} ${props.style?.headerFill === false ? "datox-react-dashboard__table--plain-header" : ""}`}
        >
          {columnWidths ? (
            <colgroup>
              {columnWidths.map((width, index) => (
                <col key={columns[index]?.key ?? index} style={{ width }} />
              ))}
            </colgroup>
          ) : null}
          {props.style?.showHeader === false ? null : (
            <thead>
              <tr>
                {columns.map((column) => {
                  const sorted = activeSort?.column === column.key ? activeSort.direction === "desc" ? "descending" : "ascending" : undefined
                  const sortBy = () => {
                    const next = nextTableSort(activeSort, column.key, column.numeric)
                    setPage(1)
                    if (props.onSort) props.onSort(next)
                    else setViewSort(next || { column: "", direction: "asc" })
                  }
                  return (
                  <th
                    key={column.key}
                    className={[column.numeric ? "datox-react-dashboard__cell--num" : "", sortable ? "datox-react-dashboard__th--sortable" : ""].filter(Boolean).join(" ") || undefined}
                    title={column.label}
                    aria-sort={sortable ? sorted || "none" : undefined}
                    tabIndex={sortable ? 0 : undefined}
                    onClick={sortable ? sortBy : undefined}
                    onKeyDown={sortable ? (event) => {
                      if (event.key !== "Enter" && event.key !== " ") return
                      event.preventDefault()
                      event.stopPropagation()
                      sortBy()
                    } : undefined}
                  >
                    {sortable ? (
                      <span className="datox-react-dashboard__th-inner">
                        <span className="datox-react-dashboard__th-label">{column.label}</span>
                        {sorted ? (
                          <span className="datox-react-dashboard__th-sort" aria-hidden="true">{sorted === "descending" ? "▼" : "▲"}</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="datox-react-dashboard__th-label">{column.label}</span>
                    )}
                  </th>
                  )
                })}
              </tr>
            </thead>
          )}
          <tbody>
            {displayed.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => {
                  const text = formatTableCell(row[column.key], column.format, locale)
                  const conditional = getCellConditionalStyle(row, column.key)
                  const signed = column.signed && text != null ? signedPercentCell(row[column.key], column.format, locale) : null
                  return (
                    <td
                      key={column.key}
                      className={cellClass(column)}
                      style={conditional || undefined}
                    >
                      {text == null ? (
                        emptyCell
                      ) : signed ? (
                        <span
                          className="datox-react-dashboard__cell-signed"
                          data-direction={signed.direction || undefined}
                          // A colour rule on the cell wins over the green / red.
                          style={signed.direction && !conditional?.color ? { color: SIGNED_COLORS[signed.direction] } : undefined}
                          title={text}
                        >
                          {signed.text}
                        </span>
                      ) : column.long && clampLongText ? (
                        <div className="datox-react-dashboard__cell-clamp" title={text}>
                          {text}
                        </div>
                      ) : (
                        text
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
          {summary ? (
            <tfoot>
              <tr>
                {columns.map((column, index) => (
                  <td key={column.key} className={cellClass({ ...column, long: false })}>
                    {summary[column.key] === "" && index === 0
                      ? props.labels?.summaryLabel
                      : formatTableCell(
                          summary[column.key],
                          summaryFormat(column, props.data?.summaryColumns?.[column.key]),
                          locale,
                        ) ?? ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
        {!rows.length && props.labels ? (
          <div className="datox-react-dashboard__widget-state datox-react-dashboard__table-empty" role="status">
            {props.data?.filters?.length ? props.labels.noMatchingRows : props.labels.noValues || props.labels.noMatchingRows}
          </div>
        ) : null}
      </div>
      {summary ? (
        <details className="datox-react-dashboard__table-scope">
          <summary>{props.labels?.summaryScopeShort || props.labels?.summaryScope}</summary>
          <p>{props.labels?.summaryScope}</p>
        </details>
      ) : null}
      {/* One page needs no pager: on a dense factsheet page its 30px are table rows. */}
      {pagination && pages > 1 ? (
        <div className="datox-react-dashboard__table-pagination">
          <span role="status" className="datox-react-dashboard__row-range">
            {props.labels?.rowRange
              ?.replace("{start}", String(rows.length ? (current - 1) * size + 1 : 0))
              .replace("{end}", String(Math.min(current * size, rows.length)))
              .replace("{total}", String(rows.length))}
          </span>
          <div className="datox-react-dashboard__page-navigation">
            <button
              type="button"
              className="datox-react-dashboard__page-button"
              aria-label={props.labels?.previousPage}
              title={props.labels?.previousPage}
              disabled={current <= 1}
              onClick={(event) => {
                event.stopPropagation()
                selectPage(current - 1)
              }}
            >
              <TbChevronLeft size={16} aria-hidden="true" />
            </button>
            <span className="datox-react-dashboard__page-status">
              {props.labels?.pageStatus?.replace("{page}", String(current)).replace("{pages}", String(pages))}
            </span>
            <button
              type="button"
              className="datox-react-dashboard__page-button"
              aria-label={props.labels?.nextPage}
              title={props.labels?.nextPage}
              disabled={current >= pages}
              onClick={(event) => {
                event.stopPropagation()
                selectPage(current + 1)
              }}
            >
              <TbChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
          {pages > 2 ? (
            <label className="datox-react-dashboard__page-jump">
              <span>{props.labels?.goToPage}</span>
              <EditorNumberInput min={1} max={pages} value={current} onChange={selectPage} />
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

// A count is always an integer; an average of whole numbers keeps 2 decimals; a summary
// on a text or date column (count aside) is a plain number.
function summaryFormat(column, operation) {
  if (operation === "count") return explicitColumnFormat({ format: "integer" })
  if (column.format.kind === "integer" && !column.format.explicit && operation === "avg") return { kind: "number", decimals: 2 }
  if (column.format.kind === "text" || column.format.kind === "date") return { kind: "number", decimals: 2 }
  return column.format
}

function parseComparableNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (value == null) return null
  const cleaned = String(value).replace(/[\s,]/g, "")
  if (!cleaned) return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

const filterColumnInfoCache = new WeakMap()
function filterColumnInfoFor(rows, column) {
  if (!rows) return { kind: filterColumnKind(rows, column), hints: filterValueHints(rows, column) }
  let byColumn = filterColumnInfoCache.get(rows)
  if (!byColumn) filterColumnInfoCache.set(rows, (byColumn = new Map()))
  const key = column || ""
  if (!byColumn.has(key)) byColumn.set(key, { kind: filterColumnKind(rows, column), hints: filterValueHints(rows, column) })
  return byColumn.get(key)
}

export function applyWidgetTableSettings(table, data) {
  if (!table) return table
  const filters = data?.filters || []
  const columns = data?.columns?.length
    ? data.columns.filter((column) => table.headers.includes(column))
    : table.headers
  // Each column is filtered in the scale it is shown in ("Weight > 5" means 5% on a percent column).
  const filteredRows = filterTableRows(table.rows, filters, data?.columnFormats)
  const sortedRows = sortRows(filteredRows, data?.sort)
  const rows = data?.rowLimit ? sortedRows.slice(0, Number(data.rowLimit)) : sortedRows
  return { ...table, headers: columns, rows }
}

// Keystrokes of one inline text edit closer than this share one undo step (summer throttleMs 600).
export const UNDO_COALESCE_MS = 600

export function applyDashboardFilters(table, widget, filterWidgets) {
  if (!table || widget.type === "filter") return table
  // Targets per dataset (as before) or a filter bound to a field, on every widget whose data has that column
  // (dashboardFilterColumn, backlog #21). Nothing selected ("All") filters nothing.
  const matchingFilters = dashboardFiltersFor(widget, table, filterWidgets || [])
  if (!matchingFilters.length) return table
  return {
    ...table,
    rows: table.rows.filter((row) => matchingFilters.every((filter) => rowMatchesDashboardFilter(row, filter))),
  }
}

function nextFilterSelectedValues(widget, value, selectedValues) {
  if (!value) return []
  if (widget.style?.multi === false) return selectedValues.includes(value) ? [] : [value]
  return selectedValues.includes(value) ? selectedValues.filter((item) => item !== value) : [...selectedValues, value]
}

// The next sort after a click on a column's header: a new column starts largest first when it holds numbers
// ("largest holding") and A→Z otherwise; a second click flips it; a third clears it (the saved order). Null = none.
export function nextTableSort(current, column, numeric = false) {
  if (!column) return null
  if (current?.column !== column) return { column, direction: numeric ? "desc" : "asc" }
  const first = numeric ? "desc" : "asc"
  const direction = current.direction === "desc" ? "desc" : "asc"
  if (direction === first) return { column, direction: first === "desc" ? "asc" : "desc" }
  return null
}

function sortRows(rows, sort) {
  if (!sort?.column) return rows
  return [...rows].sort((first, second) => {
    const result = compareDashboardCells(first[sort.column], second[sort.column])
    return sort.direction === "desc" ? -result : result
  })
}

function csvCell(value) {
  const text = String(value ?? "")
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

// A chart binding that this data does not have: his message, then which column ("“weight” is not in this data.").
function BindingAlert(props) {
  // §75: a reader (Preview, the PDF) never gets editing copy (WidgetEmpty's rule): the V2 sheet says "No data" there;
  // the editor keeps the alert with its column.
  const reader = !useContext(WidgetEmptyContext).connect
  const v2 = !!useContext(DashboardKpiContext).v2
  if (v2 && reader) return <WidgetEmpty labels={props.labels} reason="binding" />
  const issue = props.issue
  const detail = !issue
    ? ""
    : (issue.kind === "missing"
        ? props.labels.bindingMissingColumn || "“{column}” is not in this data."
        : props.labels.bindingNotNumeric || "“{column}” has no numbers."
      ).replace("{column}", issue.column)
  return (
    <div role="alert" className="datox-react-dashboard__binding-alert" data-issue={issue?.kind}>
      <span>{props.message}</span>
      {detail ? <small>{detail}</small> : null}
    </div>
  )
}

function FilterWidget(props) {
  // Every distinct value of the column, sorted (the old code took the first 12 rows instead).
  const values = useMemo(() => distinctFilterValues(props.table?.rows, props.label), [props.table?.rows, props.label])
  const [search, setSearch] = useState("")
  const [expanded, setExpanded] = useState(false)
  // No column yet (summer "Set up this filter in the editor"): a prompt in the editor, nothing for a reader.
  if (!props.label)
    return (
      <WidgetEmpty
        quiet
        labels={props.labels}
        reason="filterNotSet"
        message={props.labels?.filterNotSet || "Pick the column this filter lists."}
        action={props.labels?.filterSetUp || "Set up this filter"}
      />
    )
  if (props.style?.display === "dropdown")
    return (
      <select
        className="datox-react-dashboard__filter-select"
        value={props.selectedValues[0] || ""}
        onChange={(event) => props.onToggle?.(event.currentTarget.value)}
      >
        <option value="">All</option>
        {values.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
    )
  const visible = visibleFilterValues(values, props.selectedValues, search, expanded ? Infinity : undefined)
  const searchable = values.length > 12
  return (
    <div className="datox-react-dashboard__filters">
      {searchable ? (
        <input
          className="datox-react-dashboard__filter-search"
          type="search"
          value={search}
          placeholder={props.labels?.filterSearch || "Search values"}
          aria-label={props.labels?.filterSearch || "Search values"}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
      ) : null}
      {visible.shown.map((value) => (
        <button
          key={value}
          type="button"
          className={
            props.selectedValues.includes(value)
              ? "datox-react-dashboard__filter-chip datox-react-dashboard__filter-chip--active"
              : "datox-react-dashboard__filter-chip"
          }
          onClick={() => props.onToggle?.(value)}
        >
          {value}
        </button>
      ))}
      {visible.hidden > 0 ? (
        <button
          type="button"
          className="datox-react-dashboard__filter-chip datox-react-dashboard__filter-more"
          onClick={(event) => {
            event.stopPropagation()
            setExpanded(true)
          }}
        >
          {(props.labels?.filterMore || "+{count} more").replace("{count}", String(visible.hidden))}
        </button>
      ) : null}
    </div>
  )
}

function WidgetDatasetState(props) {
  const context = useContext(DashboardTableContext)
  const [retrying, setRetrying] = useState(false)
  const busy = context.loading || retrying
  // Nothing to load: the empty state (not connected, or bound to data this client doesn't have).
  if (!busy && !props.table?.loadError) return <WidgetEmpty labels={props.labels} reason={props.datasetID ? "noValues" : "notConnected"} />
  return (
    <div
      className="datox-react-dashboard__widget-state"
      role={props.table?.loadError && !busy ? "alert" : "status"}
      aria-busy={Boolean(busy)}
    >
      <p>
        {busy
          ? props.labels.tableLoading
          : props.table?.loadError
            ? props.labels.datasetLoadError
            : props.labels.selectDatasetHelp}
      </p>
      {props.table?.loadError && context.onRetry ? (
        <button
          className="datox-react-dashboard__button"
          type="button"
          disabled={Boolean(busy)}
          onClick={() => {
            setRetrying(true)
            Promise.resolve(context.onRetry())
              .catch(() => {})
              .finally(() => setRetrying(false))
          }}
        >
          {props.labels.retryDataset}
        </button>
      ) : null}
    </div>
  )
}

function ChartWidget(props) {
  // §75: on a Swiss sheet (V2) the plot and its legend start on the content's left edge, as the titles and tables do.
  const v2 = !!useContext(DashboardKpiContext).v2
  const flush = v2 && props.sheet === "swiss"
  if (!props.table || props.table.loadError) return <WidgetDatasetState table={props.table} labels={props.labels} datasetID={props.widget.data?.dataset_id} />
  const rows = props.table?.rows || []
  if (
    props.widget.type !== "scatter" &&
    props.widget.data?.aggregation &&
    !["sum", "avg", "min", "max", "count"].includes(props.widget.data.aggregation)
  )
    return <BindingAlert labels={props.labels} message={props.labels.invalidChartBindings} issue={chartBindingIssue(props.widget, props.table)} />
  if (
    (props.widget.data?.filters || []).some(
      (filter) => filter.column && !props.table?.headers?.includes(filter.column),
    ) ||
    (props.widget.data?.sort?.column && !props.table?.headers?.includes(props.widget.data.sort.column))
  )
    return <BindingAlert labels={props.labels} message={props.labels.invalidChartBindings} issue={chartBindingIssue(props.widget, props.table)} />
  const metric = props.widget.data?.metric
  const measures =
    props.widget.data?.measures?.length && ["bar", "line", "multiSeries"].includes(props.widget.type)
      ? props.widget.data.measures
      : [metric || (props.widget.data?.aggregation === "count" ? "__count__" : "")].filter(Boolean)
  if (
    props.widget.type === "scatter" &&
    (!props.table?.headers?.includes(props.widget.data?.xColumn) ||
      !props.table?.headers?.includes(props.widget.data?.yColumn) ||
      props.widget.data.xColumn === props.widget.data.yColumn ||
      (props.widget.data.sizeColumn && !props.table.headers.includes(props.widget.data.sizeColumn)) ||
      (props.widget.data.sizeColumn &&
        rows.length > 0 &&
        !rows.some((row) => numericCell(row[props.widget.data.sizeColumn]) !== null)) ||
      (rows.length > 0 &&
        !rows.some(
          (row) =>
            numericCell(row[props.widget.data.xColumn]) !== null &&
            numericCell(row[props.widget.data.yColumn]) !== null,
        )))
  )
    return <BindingAlert labels={props.labels} message={props.labels.invalidScatterBindings} issue={chartBindingIssue(props.widget, props.table)} />
  if (
    props.widget.type !== "scatter" &&
    (!props.table?.headers?.includes(props.widget.data?.label) ||
      !measures.length ||
      measures.some(
        (measure) =>
          measure !== "__count__" &&
          (!props.table?.headers?.includes(measure) ||
            (props.widget.data?.aggregation !== "count" &&
              rows.length > 0 &&
              !rows.some((row) => numericCell(row[measure]) !== null))),
      ) ||
      (props.widget.data?.topBy &&
        (!props.table?.headers?.includes(props.widget.data.topBy) ||
          (rows.length > 0 && !rows.some((row) => numericCell(row[props.widget.data.topBy]) !== null)))))
  )
    return <BindingAlert labels={props.labels} message={props.labels.invalidChartBindings} issue={chartBindingIssue(props.widget, props.table)} />
  const barDisplay = props.widget.type === "bar" ? props.widget.style?.barDisplay : undefined
  if (barDisplay === "contributors" || barDisplay === "detractors") {
    // Ranked over every category (no cap): the top 5 are picked from all of them.
    const all = chartItems(rows, { ...chartWidgetData(props.widget), limitEnabled: false }, "bar", props.labels)
    if (!all.length) return <WidgetEmpty labels={props.labels} reason={props.widget.data?.dataset_id ? "noValues" : "notConnected"} />
    return <ContributionRows items={all} mode={barDisplay} accent={props.accent} scale={contributionScale(rows, measures[0])} />
  }
  const items = chartItems(rows, chartWidgetData(props.widget), props.widget.type, props.labels)
  // Summer WidgetEmpty: a muted, centred "No data" instead of a bare line of text.
  if (!items.length) return <WidgetEmpty labels={props.labels} reason={props.widget.data?.dataset_id ? "noValues" : "notConnected"} />
  // A pie cannot draw a negative slice (summer DashboardWorkspaceWidget): say so instead of a wrong-looking pie.
  if (props.widget.type === "pie" && chartHasNegative(items))
    return (
      <WidgetEmpty
        labels={props.labels}
        reason="pieNegative"
        message={props.labels.pieNegative || "A pie needs values of zero or more. A bar chart shows gains and losses."}
        action={props.labels.designer?.editChart || "Change the chart"}
      />
    )
  const styled = withChartStyleExtras(props.widget.type, items, props.widget.style || {}, chartOption(
    props.widget.type,
    items,
    props.accent,
    props.labels,
    measures,
    { __count__: props.labels.aggregationCount, ...(props.widget.data?.displayNames || {}) },
    {
      ...(props.widget.style || {}),
      width: props.widget.layout?.w,
      height: props.widget.layout?.h,
      palette: chartPalette(props.paletteAccent),
      ranked: !!props.widget.data?.topBy,
      fontScale: props.fontScale,
      flush,
      sheet: v2 ? props.sheet : undefined,
    },
  ))
  // §75: the sheet style's axes, grid lines, labels and legend (V2 only; tokens/sheet-chart.js).
  const option = v2 ? withSheetChrome(styled, props.sheet, { headerColor: props.headerColor, bodyFont: props.font }) : styled
  return <ResponsiveChart exporting={props.exporting} option={props.exporting ? { ...option, animation: false } : option} />
}

// Contribution values print as percents: a percent column keeps its own scale (fractions ×100), and a column of
// plain fractions (every |v| ≤ 1) is read the same way.
export function contributionScale(rows, metric) {
  const values = (rows || []).map((row) => row?.[metric])
  const format = inferColumnFormat(metric, values)
  if (format.kind === "percent") return format.scale || 1
  const numbers = values.map(numericCell).filter((value) => value != null)
  return numbers.length && numbers.every((value) => Math.abs(value) <= 1) ? 100 : 1
}

// Summer factsheet contribution rows: label | proportional bar (min 16%) | "x.xx%", top 5; detractors mirrored.
export function ContributionRows(props) {
  const kpi = useContext(DashboardKpiContext)
  const rows = contributionRows(props.items, props.mode, { scale: props.scale, locale: kpi?.locale })
  const colors = contributionColors(props.mode, props.accent || "#2f5ea8")
  const detractors = props.mode === "detractors"
  return (
    <div
      className={`datox-react-dashboard__contribution datox-react-dashboard__contribution--${props.mode}`}
      data-contribution={props.mode}
    >
      {rows.map((row) => {
        const label = (
          <span className="datox-react-dashboard__contribution-label" title={row.label}>
            {row.label}
          </span>
        )
        const value = <span className="datox-react-dashboard__contribution-value">{row.text}</span>
        return (
          <div key={`${row.label}-${row.rank}`} className="datox-react-dashboard__contribution-row">
            {detractors ? value : label}
            <span className="datox-react-dashboard__contribution-track">
              <span
                className="datox-react-dashboard__contribution-bar"
                style={{ width: `${row.width}%`, background: colors[Math.min(row.rank, colors.length - 1)] }}
              />
            </span>
            {detractors ? label : value}
          </div>
        )
      })}
    </div>
  )
}

function ResponsiveChart(props) {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect || container.getBoundingClientRect()
      const width = Math.round(rect.width || 0)
      const height = Math.round(rect.height || 0)
      if (width > 0 && height > 0)
        setSize((current) => (current.width === width && current.height === height ? current : { width, height }))
      chartRef.current?.getEchartsInstance?.().resize()
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const resizeFrame = requestAnimationFrame(() => chartRef.current?.getEchartsInstance?.().resize())
    return () => cancelAnimationFrame(resizeFrame)
  }, [props.option, size.width, size.height])

  return <div ref={containerRef} data-report-chart={props.exporting ? "pending" : undefined} className="datox-react-dashboard__chart-wrap"><ReactECharts key={`${size.width}x${size.height}`} ref={chartRef} option={props.option} notMerge lazyUpdate={!props.exporting} onEvents={props.exporting ? { finished: () => { if (containerRef.current) containerRef.current.dataset.reportChart = "ready" } } : undefined} style={{ width: size.width || "100%", height: size.height || "100%" }} opts={{ renderer: "canvas" }} /></div>
}

// Bobur's chart style keys (leshabank 37f2a2b ChartSettings: bar corner radius and style, bar width and gap, a
// primary bar colour, a highlighted category, line width/pattern/colour/smoothing/area/markers, axis rotation,
// value axis from zero, heatmap and treemap options) on top of our option. Only the keys a widget sets change
// anything, so a chart without them draws exactly as before.
export function withChartStyleExtras(type, items, style, option) {
  if (!option || typeof option !== "object") return option
  const has = (key) => style[key] !== undefined && style[key] !== null && style[key] !== ""
  const axes = (value) => (Array.isArray(value) ? value : value ? [value] : [])
  const horizontal = type === "bar" && style.orientation === "horizontal"
  if (type === "heatmap") {
    if (has("axisLabelRotation")) for (const axis of axes(option.xAxis)) axis.axisLabel = { ...axis.axisLabel, rotate: style.axisLabelRotation }
    if (style.showGrid === false && option.series?.[0]) option.series[0].itemStyle = { ...option.series[0].itemStyle, borderWidth: 0 }
    return option
  }
  if (type === "treemap") {
    if (style.showValueLabels === false && option.series?.[0]) option.series[0].label = { ...option.series[0].label, show: false }
    return option
  }
  if (!["bar", "line", "multiSeries"].includes(type)) return option
  const valueAxes = axes(horizontal ? option.xAxis : option.yAxis).filter((axis) => axis.type === "value")
  const categoryAxes = axes(horizontal ? option.yAxis : option.xAxis).filter((axis) => axis.type === "category")
  if (style.startFromZero === false) for (const axis of valueAxes) axis.scale = true
  if (has("axisLabelRotation")) for (const axis of categoryAxes) axis.axisLabel = { ...axis.axisLabel, rotate: style.axisLabelRotation }
  const radius = style.barStyle === "rounded" ? 16 : has("barCornerRadius") ? Number(style.barCornerRadius) : null
  option.series = (option.series || []).map((series, index) => {
    const next = { ...series }
    if (series.type === "bar") {
      if (radius !== null) next.itemStyle = { ...next.itemStyle, borderRadius: horizontal ? [0, radius, radius, 0] : [radius, radius, 0, 0] }
      if (has("barWidth")) next.barMaxWidth = Number(style.barWidth)
      if (has("barCategoryGap")) next.barCategoryGap = `${style.barCategoryGap}%`
      const color = index === 0 && !style.useDistinctBarColors && /^#[0-9a-f]{6}$/i.test(style.barPrimaryColor || "") ? style.barPrimaryColor : null
      if (color) next.itemStyle = { ...next.itemStyle, color }
      const cell = (item) => (item !== null && typeof item === "object" ? item : { value: item })
      if (has("highlightCategory") && items.some((item) => item.name === style.highlightCategory))
        next.data = (next.data || []).map((item, itemIndex) => ({ ...cell(item), itemStyle: { ...cell(item).itemStyle, opacity: items[itemIndex]?.name === style.highlightCategory ? 1 : 0.35 } }))
      if (style.barStyle === "gradient")
        next.data = (next.data || []).map((item) => {
          const base = cell(item).itemStyle?.color || next.itemStyle?.color
          return base && typeof base === "string"
            ? { ...cell(item), itemStyle: { ...cell(item).itemStyle, color: { type: "linear", x: 0, y: 0, x2: horizontal ? 1 : 0, y2: horizontal ? 0 : 1, colorStops: [{ offset: 0, color: base }, { offset: 1, color: "#ffffff" }] } } }
            : item
        })
    }
    if (series.type === "line") {
      const color = type === "line" && /^#[0-9a-f]{6}$/i.test(style.lineColor || "") ? style.lineColor : null
      if (has("lineWidth") || has("lineStyle") || color)
        next.lineStyle = { ...next.lineStyle, ...(has("lineWidth") ? { width: Number(style.lineWidth) } : {}), ...(has("lineStyle") ? { type: style.lineStyle } : {}), ...(color ? { color } : {}) }
      if (color) next.itemStyle = { ...next.itemStyle, color }
      if (style.smoothLines === false) next.smooth = false
      if (type === "line" && style.areaFill && !next.areaStyle) next.areaStyle = { opacity: 0.2 }
      if (has("showMarkers")) next.showSymbol = !!style.showMarkers
      if (has("markerSize")) next.symbolSize = Number(style.markerSize)
    }
    return next
  })
  return option
}

export function chartOption(type, items, accent, labels, measures = [], displayNames = {}, style = {}) {
  const width = Number(style.width || 0)
  const height = Number(style.height || 0)
  // Summer chart modes (dashboardTokens widget.chart): compact below 340x220, tiny below 260x160.
  const { compact, tiny } = chartSizeMode(width, height)
  // Readable chart text (Artyom: axis labels were ~10px): 11px axis labels on compact charts, 12px
  // otherwise, and a 12px legend, all at 100% zoom.
  // The Design font size scales both (backlog #18; summer --ds-text-* times the font scale).
  const chartText = chartTextSizes(compact, style.fontScale)
  const axisFont = chartText.axis
  // A line chart keeps its value axis unless it is really tiny (a 152px-tall card still has room).
  const lineValueAxis = style.showValueAxisLabels !== false && (!tiny || height >= CHART_TEXT.lineAxisMinHeightPx)
  const formatValue = (value) => {
    return formatStyledNumber(value, style)
  }
  const escapeText = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char],
    )
  // Summer chartPalette: the brand ramp, or one derived from the design accent when it sets one.
  // The first series keeps the accent; benchmark series and mixed-sign single bars use the semantic colours.
  const palette = style.palette || chartPalette(accent)
  const seriesColor = (measure, index) =>
    isBenchmarkSeries(displayNames[measure] || measure) && measures.length > 1
      ? CHART_SEMANTIC.benchmark
      : index === 0
        ? accent
        : palette[index % palette.length]
  const signed =
    measures.length === 1 &&
    items.some((item) => (item.values?.[measures[0]] || 0) < 0) &&
    items.some((item) => (item.values?.[measures[0]] || 0) > 0)
  const lineWidth = tiny ? CHART_SIZE.line.widthMin : compact ? 2 : CHART_SIZE.line.widthMax
  // Category axes: ISO dates read "12 Apr" / "Apr 2026" / "2026" by span and sort by date (a
  // ranked chart keeps its rank order); ALL-CAPS sector names read in sentence case. Display only.
  const categoryChart = ["bar", "line", "multiSeries"].includes(type)
  const dateAxis = categoryChart ? dateCategoryAxis(items, { keepOrder: !!style.ranked }) : null
  if (dateAxis) items = dateAxis.items
  // Multi-series (backlog #22): 100% stacks, a second value axis, areas and lines in a bar chart. The plan is per
  // measure; with none of the new style fields every series draws exactly as before.
  const stackPlan = categoryChart ? multiSeriesPlan(type, measures, style, { horizontal: style.orientation === "horizontal" }) : null
  const percentStack = !!stackPlan?.percent
  const stackedMeasures = stackPlan ? stackPlan.plan.filter((entry) => entry.stack).map((entry) => entry.measure) : []
  const rightMeasures = stackPlan ? stackPlan.plan.filter((entry) => entry.axis === 1).map((entry) => entry.measure) : []
  const leftMeasures = measures.filter((measure) => !rightMeasures.includes(measure))
  if (percentStack) items = percentStackItems(items, stackedMeasures)
  const percentText = (value) => `${formatStyledNumber(value, { valueFormat: "number", decimalPlaces: 1 })}%`
  const categoryNames = dateAxis ? dateAxis.full : sentenceCaseLabels(items.map((item) => item.name))
  const plotWidthGuess = Math.max(60, (width || 400) - 64)
  const dateTicks = dateAxis
    ? dateAxisTickIndexes(
        dateAxis,
        plotWidthGuess,
        Math.max(...dateAxis.ticks.map((tick) => labelTextPx(tick, axisFont))),
      )
    : null
  const dateAxisLabel = dateAxis
    ? {
        formatter: (_value, index) => dateAxis.ticks[index] ?? "",
        interval: (index) => dateTicks.has(index),
      }
    : {}
  // Value axes: compact K / M / B ticks (1.2M) once the axis reaches 10,000; tooltips keep the full value.
  const axisNumber = (values) => {
    const unit = style.valueFormat === "percent" ? null : compactAxisUnit(Math.max(0, ...values.map((value) => Math.abs(Number(value) || 0))))
    return unit ? (value) => formatCompactAxisValue(value, unit) ?? formatValue(value) : formatValue
  }
  const seriesValues = items.flatMap((item) => leftMeasures.map((measure) => item.values?.[measure]))
  const valueAxisFormatter = percentStack
    ? (value) => `${Math.round(Number(value) || 0)}%`
    : axisNumber(
        style.stackSeries
          ? items.map((item) => leftMeasures.reduce((sum, measure) => sum + Math.abs(Number(item.values?.[measure]) || 0), 0))
          : seriesValues,
      )
  const rightValues = items.flatMap((item) => rightMeasures.map((measure) => item.values?.[measure]))
  // The right axis: its own ticks (compact K/M/B on its own range), no grid lines of its own.
  const rightAxis = (show, scale) =>
    rightMeasures.length
      ? {
          type: "value",
          position: "right",
          scale,
          name: tiny ? undefined : style.secondaryAxisLabel || undefined,
          axisLabel: { show, formatter: axisNumber(rightValues), fontSize: axisFont },
          splitLine: { show: false },
        }
      : null
  // Vertical bars: one-line names when they fit the band, two wrapped lines next, then horizontal
  // bars for more than 8 long names (unless the author chose an orientation), rotation last.
  const barCategoryAxis = type !== "line" && type !== "multiSeries" && style.orientation !== "horizontal"
  const barLayout = barCategoryAxis
    ? dateAxis
      ? "date"
      : categoryAxisLayout(categoryNames, plotWidthGuess, axisFont, { allowHorizontal: !style.orientation })
    : null
  const horizontal = style.orientation === "horizontal" || barLayout === "horizontal"
  // A short but wide chart (518x152 on a dense page) is "tiny" by height only: it still
  // names its categories, otherwise the bars carry no meaning. Value ticks stay hidden.
  const categoryLabels = style.showValueAxisLabels !== false && (!tiny || width >= CHART_SIZE.tiny.widthPx)
  // Summer chart grid: containLabel already reserves the tick labels, so the explicit
  // margins stay small instead of leaving dead space beside and under the plot.
  // A rotated category label hangs left of the first tick and containLabel does not count it:
  // reserve the overhang (label length, capped at the 84px truncation, minus half a band),
  // less the value-tick column that containLabel already keeps on that side.
  const rotatedCategories = barLayout === "rotate"
  // Rotated category labels may be as long as the plot height allows (sin 24° ≈ 0.41 of the label
  // length hangs below the axis, up to ~22% of the chart), then abbreviate instead of a hard cut.
  const rotatedLabelPx = Math.round(Math.max(84, Math.min(150, (height * 0.22) / Math.sin((24 * Math.PI) / 180))))
  // Capitals run ~20% wider than mixed case.
  const charPx = (text) => axisFont * (/[a-z]/.test(String(text)) ? 0.6 : 0.72)
  const labelChars = Math.max(6, Math.floor(rotatedLabelPx / charPx(categoryNames.join(""))))
  const bandPx = Math.max(1, (width || 400) - 48) / Math.max(1, items.length)
  const valueTicksPx = style.showValueAxisLabels !== false && !tiny ? 20 : 0
  // Each rotated label hangs left of its tick by its length × cos 24°; reserve the largest overhang
  // past the plot's left edge over the first few labels (not only the first one).
  const overhangPx = categoryNames.slice(0, 4).reduce((value, name, index) => {
    const text = abbreviateLabel(String(name ?? ""), labelChars)
    const length = Math.min(rotatedLabelPx, text.length * charPx(text))
    return Math.max(value, length * Math.cos((24 * Math.PI) / 180) - (index + 0.5) * bandPx)
  }, 0)
  // A flush plot (Swiss, §75) has no 8px inset of its own: its labels start where the title does.
  const plotEdge = style.flush ? 0 : 8
  const plotLeft =
    rotatedCategories && categoryLabels ? Math.max(plotEdge, Math.round(overhangPx - valueTicksPx + 6 - (8 - plotEdge))) : plotEdge
  const plotRight = style.flush ? 0 : 12
  const topLegend = () => (style.flush ? { ...scrollLegend("top", chartText.legend), left: 0, padding: [2, 28, 2, 0] } : scrollLegend("top", chartText.legend))
  const plotBottom = style.xAxisLabel && !tiny ? 28 : 6
  // §75 Lesha: the bank's factsheet labels its values on a short series (every bar, every point), in the chart's own
  // number format; a long or daily series keeps none.
  // §75: Lesha writes values by default (the bank's charts do), up to 30 marks; his toggle, once set, decides (an explicit
  // false turns them off: code review, they could not be turned off on the default style).
  const valueLabels = style.showValueLabels ?? (style.sheet === "lesha" && !tiny && items.length * Math.max(1, measures.length) <= 30)
  const seriesForMeasures = (chartSeriesType) =>
    measures.map((measure, index) => {
      const planned = stackPlan?.plan[index]
      // A line measure in a bar chart draws as a line (summer line_measures); everything else keeps the chart's type.
      const seriesType = planned && chartSeriesType === "bar" ? planned.type : chartSeriesType
      const asPercent = percentStack && !!planned?.stack
      const show = asPercent ? percentText : formatValue
      return {
      // Agent series keys (market_value_index) read as "Market value index" unless named.
      name: displayNames[measure] || humanizeColumnName(measure) || measure,
      type: seriesType,
      // Light, monotone smoothing: a 5-point monthly series must not bow past its own values.
      smooth: seriesType === "line" ? 0.3 : false,
      smoothMonotone: seriesType === "line" ? "x" : undefined,
      // Summer: a missing point (non-trading day) is bridged, never drawn as a drop to 0.
      connectNulls: seriesType === "line" ? true : undefined,
      stack: planned ? planned.stack : style.stackSeries ? "total" : undefined,
      yAxisIndex: planned?.axis ? 1 : undefined,
      areaStyle: planned?.area ? { opacity: 0.16, color: seriesColor(measure, index) } : undefined,
      tooltip: asPercent || planned?.axis ? { valueFormatter: show } : undefined,
      label: valueLabels
        ? {
            show: true,
            position: horizontal ? "right" : "top",
            formatter: (params) => show(params.value),
          }
        : { show: false },
      itemStyle: {
        color: style.useDistinctBarColors ? undefined : seriesColor(measure, index),
      },
      lineStyle: { color: seriesColor(measure, index), width: lineWidth },
      data: items.map((item, itemIndex) =>
        style.useDistinctBarColors && seriesType === "bar"
          ? { value: item.values[measure] || 0, itemStyle: { color: palette[itemIndex % palette.length] } }
          : signed && seriesType === "bar"
            ? {
                value: item.values[measure] || 0,
                itemStyle: {
                  color: (item.values[measure] || 0) < 0 ? CHART_SEMANTIC.negative : CHART_SEMANTIC.positive,
                },
              }
            : seriesType === "line"
              ? (item.values[measure] ?? null)
              : item.values[measure] || 0,
      ),
      }
    })
  // Pie: a scroll legend at the bottom, the pie re-centred above it (summer, backlog #17).
  const pieLegend = style.showLegend !== false && !tiny
  if (type === "pie")
    return {
      color: palette,
      tooltip: { valueFormatter: formatValue },
      legend: pieLegend ? scrollLegend("bottom", chartText.legend) : { show: false },
      series: [
        {
          type: "pie",
          center: pieLegend ? PIE_LEGEND_CENTER : ["50%", "50%"],
          radius: style.donut === false ? CHART_SIZE.donut.pie : CHART_SIZE.donut.donut,
          label: {
            show: style.showValueLabels !== false && !tiny,
            formatter: (params) =>
              style.showValuesNextToLabels ? `${params.name} ${formatValue(params.value)}` : params.name,
            color: "#344054",
            fontSize: compact ? axisFont : chartText.legend,
          },
          labelLine: { show: style.showValueLabels !== false && !tiny },
          data: items,
        },
      ],
    }
  if (type === "treemap")
    return { color: palette, tooltip: {}, series: [{ type: "treemap", roam: false, label: { show: !tiny }, data: items }] }
  if (type === "funnel")
    return {
      color: palette,
      tooltip: {},
      legend: style.showLegend !== false && !tiny ? scrollLegend("top", chartText.legend) : { show: false },
      series: [{ type: "funnel", label: { show: style.showValueLabels && !tiny }, data: items }],
    }
  if (type === "scatter")
    return {
      tooltip: {
        formatter: (params) =>
          `${params.name ? `${escapeText(params.name)}<br/>` : ""}${escapeText(style.xAxisLabel || labels.scatterX)}: ${formatValue(params.value[0])}<br/>${escapeText(style.yAxisLabel || labels.scatterY)}: ${formatValue(params.value[1])}${params.value[2] == null ? "" : `<br/>${escapeText(labels.scatterSize)}: ${formatValue(params.value[2])}`}`,
      },
      grid: { left: compact ? 24 : 36, right: 12, top: 18, bottom: compact ? 20 : 32, containLabel: true },
      xAxis: {
        type: "value",
        name: tiny ? undefined : style.xAxisLabel || undefined,
        axisLabel: { show: style.showValueAxisLabels !== false && !tiny, formatter: axisNumber(items.map((item) => item.x)) },
        splitLine: { show: style.showGrid !== false && !tiny },
      },
      yAxis: {
        type: "value",
        name: tiny ? undefined : style.yAxisLabel || undefined,
        axisLabel: { show: style.showValueAxisLabels !== false && !tiny, formatter: axisNumber(items.map((item) => item.value)) },
        splitLine: { show: style.showGrid !== false && !tiny },
      },
      series: [
        {
          type: "scatter",
          symbolSize: (value) =>
            value[2] == null
              ? tiny
                ? 7
                : compact
                  ? 9
                  : 12
              : Math.max(6, Math.min(36, Math.sqrt(Math.max(0, value[2])) * 4)),
          itemStyle: { color: accent },
          data: items.map((item) => ({ name: item.name, value: [item.x, item.value, item.size] })),
        },
      ],
    }
  if (type === "heatmap")
    return {
      tooltip: {},
      grid: { left: compact ? 20 : 36, right: 12, top: 18, bottom: compact ? 20 : 32, containLabel: true },
      xAxis: { type: "category", data: items.map((item) => item.name), axisLabel: { show: !tiny } },
      yAxis: { type: "category", data: [labels.valueLabel], axisLabel: { show: !tiny } },
      visualMap: {
        show: false,
        min: 0,
        max: Math.max(1, ...items.map((item) => item.value)),
        inRange: { color: ["#e7f0ff", accent] },
      },
      series: [
        {
          type: "heatmap",
          label: { show: style.showValueLabels && !tiny },
          data: items.map((item, index) => [index, 0, item.value]),
        },
      ],
    }
  if (type === "line" || type === "multiSeries")
    return {
      tooltip: { valueFormatter: formatValue },
      legend:
        style.showLegend !== false && measures.length > 1 && !tiny
          ? topLegend()
          : { show: false },
      grid: {
        left: plotLeft,
        right: plotRight,
        top: style.showLegend !== false && measures.length > 1 && !tiny ? 36 : 14,
        bottom: plotBottom,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        name: tiny ? undefined : style.xAxisLabel || undefined,
        nameLocation: "middle",
        nameGap: 28,
        axisLabel: {
          show: categoryLabels,
          fontSize: axisFont,
          hideOverlap: true,
          // The first and last dates sit inside the plot instead of being cut at the card edge.
          alignMinLabel: "left",
          alignMaxLabel: "right",
          ...dateAxisLabel,
        },
        data: categoryNames,
      },
      yAxis: [
        {
          type: "value",
          // Summer line axis: fit the data range, so an index near 100 is not a flat line over 0–120. A 100% stack
          // runs 0–100; a stack of lines starts at 0 so the bands read as parts of the total.
          scale: !percentStack && !stackedMeasures.length,
          // 3–5 round ticks over the data range.
          ...(percentStack
            ? { min: 0, max: 100, interval: 25 }
            : stackedMeasures.length
              ? {}
              : niceValueAxis(items.flatMap((item) => leftMeasures.map((measure) => item.values?.[measure]))) || {}),
          name: tiny ? undefined : style.yAxisLabel || undefined,
          axisLabel: {
            show: lineValueAxis,
            formatter: valueAxisFormatter,
            fontSize: axisFont,
          },
          splitLine: { show: style.showGrid !== false && lineValueAxis },
        },
        rightAxis(lineValueAxis, true),
      ].filter(Boolean).reduce(oneOrTwoAxes, undefined),
      series: seriesForMeasures("line").map((series, index) => {
        const markers = lineShowsMarkers(items.length, measures.length, tiny)
        const symbolSize = compact ? CHART_SIZE.line.markerSizeCompact : CHART_SIZE.line.markerSize
        // A dense or daily series has no comb of dots: only its last point is marked.
        const last = series.data.reduce((found, value, at) => (value == null ? found : at), -1)
        return {
          ...series,
          symbolSize,
          showSymbol: markers,
          markPoint:
            !markers && !tiny && last >= 0
              ? {
                  silent: true,
                  symbol: "circle",
                  symbolSize: symbolSize + 2,
                  label: { show: false },
                  itemStyle: { color: seriesColor(measures[index], index) },
                  data: [{ coord: [last, series.data[last]] }],
                }
              : undefined,
        }
      }),
    }
  const horizontalLabelPx = Math.round(
    Math.min((width || 400) * 0.4, Math.max(48, ...categoryNames.map((name) => labelTextPx(name, axisFont) + 6))),
  )
  const barSeries = seriesForMeasures("bar").map((series, index) =>
    series.type === "line"
      ? {
          // A line in a bar chart: the line chart's markers rule, drawn over the bars.
          ...series,
          z: 3,
          symbolSize: compact ? CHART_SIZE.line.markerSizeCompact : CHART_SIZE.line.markerSize,
          showSymbol: lineShowsMarkers(items.length, measures.length, tiny),
          itemStyle: { color: seriesColor(measures[index], index) },
        }
      : {
          ...series,
          // A stacked segment rounds only where the stack ends, never between two segments.
          itemStyle: { ...series.itemStyle, borderRadius: series.stack ? 0 : horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0] },
        },
  )
  return {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: formatValue },
    legend:
      style.showLegend !== false && measures.length > 1 && !tiny
        ? topLegend()
        : { show: false },
    grid: {
      left: plotLeft,
      right: valueLabels && !tiny ? 44 : plotRight,
      top: style.showLegend !== false && measures.length > 1 && !tiny ? 36 : valueLabels && !tiny ? 24 : 14,
      bottom: plotBottom,
      containLabel: true,
    },
    xAxis: horizontal
      ? {
          type: "value",
          name: tiny ? undefined : style.yAxisLabel || undefined,
          ...(percentStack ? { min: 0, max: 100, interval: 25 } : {}),
          axisLabel: {
            show: style.showValueAxisLabels !== false && !tiny,
            formatter: valueAxisFormatter,
            fontSize: axisFont,
          },
          splitLine: { show: style.showGrid !== false && !tiny },
        }
      : {
          type: "category",
          name: tiny ? undefined : style.xAxisLabel || undefined,
          nameLocation: "middle",
          nameGap: 28,
          data: categoryNames,
          // Flat or two wrapped lines when the names fit their band; rotated (abbreviated, the full
          // name in the tooltip) only as the last resort.
          axisLabel:
            barLayout === "date"
              ? { show: categoryLabels, hideOverlap: true, fontSize: axisFont, ...dateAxisLabel }
              : barLayout === "rotate"
                ? {
                    show: categoryLabels,
                    rotate: 24,
                    width: rotatedLabelPx,
                    overflow: "truncate",
                    formatter: (value) => abbreviateLabel(value, labelChars),
                    hideOverlap: true,
                    interval: compact ? "auto" : 0,
                    fontSize: axisFont,
                  }
                : {
                    show: categoryLabels,
                    width: Math.max(24, Math.floor(plotWidthGuess / Math.max(1, items.length)) - 4),
                    overflow: barLayout === "wrap" ? "break" : "truncate",
                    lineHeight: Math.round(axisFont * 1.2),
                    interval: 0,
                    fontSize: axisFont,
                  },
        },
    yAxis: horizontal
      ? {
          type: "category",
          name: tiny ? undefined : style.xAxisLabel || undefined,
          data: categoryNames,
          inverse: true,
          axisLabel: {
            show: categoryLabels,
            width: horizontalLabelPx,
            overflow: "truncate",
            fontSize: axisFont,
          },
        }
      : [
          {
            type: "value",
            name: tiny ? undefined : style.yAxisLabel || undefined,
            ...(percentStack ? { min: 0, max: 100, interval: 25 } : {}),
            axisLabel: {
              show: style.showValueAxisLabels !== false && !tiny,
              formatter: valueAxisFormatter,
              fontSize: axisFont,
            },
            splitLine: { show: style.showGrid !== false && !tiny },
          },
          rightAxis(style.showValueAxisLabels !== false && !tiny, false),
        ].filter(Boolean).reduce(oneOrTwoAxes, undefined),
    series: barSeries.map((series) => (series.type === "line" ? series : { ...series, barMaxWidth: compact ? CHART_SIZE.bar.compactMaxWidthPx : measures.length > 1 ? CHART_SIZE.bar.comboMaxWidthPx : CHART_SIZE.bar.maxWidthPx })),
  }
}

// One value axis stays an object (as before); a second one makes the pair an array.
function oneOrTwoAxes(result, axis) {
  return result === undefined ? axis : [].concat(result, axis)
}

function chartTypeIcon(type) {
  if (type === "bar") return "▥"
  if (type === "line") return "⌁"
  if (type === "scatter") return "○"
  if (type === "pie") return "◔"
  if (type === "treemap") return "▦"
  if (type === "funnel") return "▽"
  return "▥"
}

// Bobur (6563385): agent-built widgets (output_role) show every category unless a top-N is set.
function chartWidgetData(widget) {
  return widget.data?.output_role && !widget.data?.showTop ? { ...widget.data, showTop: Infinity } : widget.data
}

function isChartWidget(type) {
  return ["bar", "line", "scatter", "pie", "heatmap", "multiSeries", "treemap", "funnel"].includes(type)
}

function chartDataSummary(widget, labels) {
  if (widget.type === "scatter")
    return labels.scatterBindingSummary
      .replace("{x}", widget.data?.xColumn || labels.noBinding)
      .replace("{y}", widget.data?.yColumn || labels.noBinding)
  const measures = widget.data?.measures?.length
    ? widget.data.measures.join(", ")
    : widget.data?.metric || labels.metricFallback
  return `${measures} ${labels.by} ${widget.data?.label || labels.labelColumn}`
}

// Bobur's updateWidget cleanup (leshabank 37f2a2b), as a patch: his draft validator rejects `kpi_*` on a non-KPI
// widget, table settings on a non-table and a stale `freeStyle`; series settings follow the measures left; the
// values captured at publish (`kpi_value`, …) never survive an edit. Removed keys are set to undefined, which
// the merge in withSmartTitle and the JSON save both drop.
export function withoutStaleSettings(widget, patch) {
  const next = { ...patch }
  const type = patch.type ?? widget.type
  if (patch.type && patch.type !== widget.type) {
    if (type !== "kpi") for (const key of ["kpi_calculation", "kpi_display", "kpi_comparison", "kpi_prefix", "kpi_display_scale"]) if (key in widget || key in patch) next[key] = undefined
    const data = { ...(widget.data || {}), ...(patch.data || {}) }
    let changed = false
    if (type !== "freeTable" && data.freeStyle) { delete data.freeStyle; changed = true }
    if (type !== "table") {
      for (const key of ["pagination", "pageSize", "showSummary", "summaryScope", "summaryColumns", "columnLabels", "columnFormats", "cellColorRules"]) if (key in data) { delete data[key]; changed = true }
      if (type !== "freeTable" && "columns" in data) { delete data.columns; changed = true }
    }
    if (changed) next.data = data
  }
  if (patch.data) {
    const data = next.data || { ...(widget.data || {}), ...patch.data }
    const measures = data.measures?.length ? data.measures : [data.metric].filter(Boolean)
    const style = { ...(widget.style || {}), ...(patch.style || {}) }
    let changed = false
    if (style.secondaryAxisMeasures) { style.secondaryAxisMeasures = style.secondaryAxisMeasures.filter((measure) => measures.includes(measure)); changed = true }
    for (const field of ["seriesColors", "seriesTypes"]) if (style[field]) { style[field] = Object.fromEntries(Object.entries(style[field]).filter(([measure]) => measures.includes(measure))); changed = true }
    if (changed) next.style = style
  }
  for (const key of ["kpi_value", "kpi_raw_value", "kpi_evidence", "kpi_comparison_result"]) if (key in widget) next[key] = undefined
  return next
}

function createWidget(type, index, table, labels, position) {
  const widgetLabels = Object.fromEntries(WIDGETS.map((item) => [item.type, widgetLabel(labels, item.type)]))
  const metric = firstNumericColumn(table) || table?.headers?.[0]
  const label = table?.headers?.[0]
  const x = position?.x ?? 16 + (index % 2) * 540
  const y = position?.y ?? 16 + Math.floor(index / 2) * 260
  if (type === "header")
    return { id: widgetIdValue(), type, title: widgetLabels[type], layout: { x: 0, y, w: 1123, h: 96 } }
  if (type === "title") return { id: widgetIdValue(), type, title: widgetLabels[type], layout: { x, y, w: 520, h: 68 } }
  if (type === "text")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      text: labels.textPlaceholder,
      layout: { x, y, w: 520, h: 152 },
    }
  if (type === "kpi")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      layout: { x, y, w: 348, h: 152 },
      data: { dataset_id: table?.datasetID, metric },
    }
  if (type === "table" || type === "freeTable")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      layout: { x: 16, y, w: 1091, h: 320 },
      data:
        type === "freeTable"
          ? { freeStyle: freeStyleFromTable(undefined, labels) }
          : { dataset_id: table?.datasetID, columns: [] },
      style: { cellColorRules: [] },
    }
  if (type === "filter")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      layout: { x, y, w: 320, h: 120 },
      data: {
        dataset_id: table?.datasetID,
        label,
        selectedValues: [],
        // A new filter is bound to its column on every widget that has it (backlog #21); its own dataset's target stays.
        field: label || undefined,
        targets: table?.datasetID ? [{ dataset_id: table.datasetID, column: label }] : [],
      },
    }
  return {
    id: widgetIdValue(),
    type,
    title: widgetLabels[type],
    layout: { x, y, w: 520, h: 320 },
    data: {
      dataset_id: table?.datasetID,
      metric,
      label,
      ...(type === "scatter"
        ? {
            xColumn: table?.headers?.find((header) => header !== metric && numericValues(table, header).length) || "",
            yColumn: metric,
          }
        : {}),
    },
  }
}

// "Hamad_70714 — top_holdings" → "Top holdings": the client prefix goes, snake case reads as words.
function readableTableName(name) {
  const table = String(name || "").split(" — ").at(-1).trim()
  const words = table.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim()
  return words ? words[0].toUpperCase() + words.slice(1) : table
}

function widgetLabel(labels, type) {
  return labels.widgets[type]
}

function defaultConfig(title, tabName) {
  return {
    title,
    canvas: { orientation: "landscape", width: 1123, height: 794 },
    tabs: [{ id: "tab-1", name: tabName, widgets: [] }],
    design: { theme: "light", accent: "#2f80ed" },
  }
}

// The font stacks live in tokens/design.js (designToCssVars); kept as an export for existing callers.
export function designFont(value) {
  return designFontStack(value)
}

function verticalAlignValue(value) {
  if (value === "bottom") return "flex-end"
  if (value === "center") return "center"
  return "flex-start"
}

function formatStyledNumber(value, style = {}) {
  const number = numericCell(value)
  if (number == null) return "--"
  if (style.decimalPlaces == null) {
    const formatted = formatNumber(style.valueFormat === "integer" ? Math.round(number) : number)
    return style.valueFormat === "percent" ? `${formatted}%` : formatted
  }
  return formattedCell(number, {
    format: style.valueFormat === "integer" ? "integer" : style.valueFormat === "percent" ? "percent" : "number",
    decimals: style.decimalPlaces,
  })
}

function cloneDashboardConfig(value) {
  return JSON.parse(JSON.stringify(value))
}

async function waitForDashboardPaint() {
  await document.fonts?.ready
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
  await new Promise((resolve) => window.setTimeout(resolve, 120))
}

function datasetColumns(datasetID, outputTables, datasets) {
  const table = outputTables.find((item) => item.datasetID === datasetID)
  if (table) return table.headers
  return datasets.find((item) => item.id === datasetID)?.columns?.map((column) => column.name) || []
}

function uniqueColumnValues(table, column) {
  if (!table || !column) return []
  return [...new Set(table.rows.map((row) => String(row[column] ?? "")).filter(Boolean))].sort((first, second) =>
    first.localeCompare(second),
  )
}

function firstNumericColumn(table) {
  return table?.headers?.find((header) => numericValues(table, header).length > 0)
}

export function numericValues(table, metric) {
  if (!table || !metric) return []
  return table.rows.map((row) => reportNumber(row[metric])).filter((value) => value !== null)
}

function formatNumber(value) {
  // en-US like table cells and KPI values; the Intl default follows the OS region ("2 100").
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value)
}

function sourceRowColumns(rows) {
  return [...new Set((rows || []).flatMap((row) => Object.keys(row || {})))]
}

function formatSourceValue(value) {
  if (value == null) return ""
  return typeof value === "object" ? JSON.stringify(value) : String(value)
}

function DashboardMarkdown({ text, className }) {
  const [html, setHtml] = useState("")
  useEffect(() => {
    let active = true
    setHtml("")
    dashboardMarkdownHtml(text)
      .then((value) => {
        if (active) setHtml(value)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [text])
  if (!html) return <div className={className}>{text}</div>
  return <div className={className} data-component="markdown" dangerouslySetInnerHTML={{ __html: html }} />
}

function widgetIdValue() {
  return `widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function uniqueReviewers(reviewers) {
  const seen = new Set()
  return reviewers.flatMap((reviewer) => {
    const email = reviewer.email.trim().toLowerCase()
    if (!email || seen.has(email)) return []
    seen.add(email)
    return [{ name: reviewer.name?.trim() || "", email, clientValue: reviewer.clientValue || "" }]
  })
}

function hasDuplicateReviewerEmails(reviewers) {
  const emails = reviewers.map((reviewer) => reviewer.email.trim().toLowerCase()).filter(Boolean)
  return new Set(emails).size !== emails.length
}

function layoutBounds(rects) {
  const left = Math.min(...rects.map((rect) => rect.x))
  const top = Math.min(...rects.map((rect) => rect.y))
  return {
    x: left,
    y: top,
    w: Math.max(...rects.map((rect) => rect.x + rect.w)) - left,
    h: Math.max(...rects.map((rect) => rect.y + rect.h)) - top,
  }
}

function sameRect(first, second) {
  return ["x", "y", "w", "h"].every((key) => Number(first?.[key]) === Number(second?.[key]))
}

// react-rnd's eight resize zones, named so the editor can draw handles over them (Batch 3).
// The id the slot of a library element being dragged over a report page goes by in the push (never saved).
const DROP_GHOST_ID = "__drop__"

const RESIZE_HANDLE_CLASSES = Object.fromEntries(
  ["top", "right", "bottom", "left", "topRight", "bottomRight", "bottomLeft", "topLeft"].map((direction) => [
    direction,
    `datox-react-dashboard__resize-handle datox-react-dashboard__resize-handle--${direction}`,
  ]),
)
// The zones in screen px at any zoom: 12px along the edges and 18px squares on the corners, centred on the frame,
// so they stay wider than the 8px handles drawn over them. The cursor names the axis.
function resizeHandleStyles(scale) {
  const edge = 12 / scale
  const corner = 18 / scale
  // Corners sit over the edges they meet (the zones overlap there).
  const corners = (cursor, x, y) => ({ width: corner, height: corner, [x]: -corner / 2, [y]: -corner / 2, cursor, zIndex: 2 })
  return {
    top: { height: edge, top: -edge / 2, cursor: "ns-resize" },
    bottom: { height: edge, bottom: -edge / 2, cursor: "ns-resize" },
    left: { width: edge, left: -edge / 2, cursor: "ew-resize" },
    right: { width: edge, right: -edge / 2, cursor: "ew-resize" },
    topLeft: corners("nwse-resize", "left", "top"),
    bottomRight: corners("nwse-resize", "right", "bottom"),
    topRight: corners("nesw-resize", "right", "top"),
    bottomLeft: corners("nesw-resize", "left", "bottom"),
  }
}
